import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { Line, Shape, Group, Circle, Rect, Text as KonvaText, Path } from 'react-konva';
import Konva from 'konva';
import { Edge, Node } from '../../core/models/scene';
import { useDiagramStore } from '../../stores/diagramStore';
import { useThemeAwareColor, useThemeMode } from '../../react-app/theme/ThemeProvider';
import { usePerspectiveStore } from '../../stores/perspectiveStore';
import * as awarenessProtocol from 'y-protocols/awareness';
import { computeMarriageBaselineY } from '../../core/connectors/utils/marriageGeometry';
import {
  getBottomCenter,
  getMarriageTerminalBottomCenter,
  getTopCenter,
} from '../../core/connectors/utils/anchorPoints';
import { UpdateEdgeCommand } from '../../core/commands/edges/UpdateEdgeCommand';
import { getCommandManager } from '../../hooks/useCommandManager';
import { useHighlightStore } from '../../stores/highlightStore';
import { ConnectorOverlay } from './ConnectorOverlay';
import { isFreeEndpointConnector } from '../../core/connectors/isFreeEndpointConnector';
import { useOrgChartViewStore } from '../../stores/orgchartViewStore';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { useDiagramSettings } from '../../hooks/useDiagramSettings';
import { isOrgEdge } from '../../domains/orgchart/types/layout.types';
import { computeOrgReportCounts, isReportsToEdge } from '../../domains/orgchart/services/orgChartReportCounts';
import { ORG_NODE_TYPES, ORG_PERSON_NODE_TYPES } from '../../domains/orgchart/constants/orgNodeTypes';
import {
  isOrgPrimaryReportingEdge,
  isOrgSecondaryReportingEdge,
  resolveOrgRoutingPolicyIdForEdge,
} from '../../domains/orgchart/connectors/routingPolicies';
import {
  planOrgPrimaryReportingRoute,
  type OrgRouteEdge,
  type OrgRouteNode,
  isOrgRoutingOccupancyShape,
} from '../../domains/orgchart/services/orgReportingRoutePlanner';
import { evaluatePrecomputedLayoutHonor } from '../../domains/orgchart/services/orgPrecomputedLayoutPoints';
import {
  CORE_ROUTING_POLICY_IDS,
  resolveConnectorRoutingPolicy,
  type ConnectorRoutingPolicy,
} from '../../core/connectors/routing';
import { blendColor } from '../../lib/color/blend';
import { resolveGenogramConnectorDisplayLabel } from '../../domains/genogram/utils/connectorDisplayLabel';
import type { PersonLabelGeometryContext } from '../../lib/diagram/personLabelBounds';
import {
  computeSmartOrthogonalRoute,
  repairOrthogonalEndpointIntersections,
  type SpatialRect,
} from '../../core/layout/spatialIntent';
import {
  expandOrgPersonPhotoTopCenterRoutingRect,
  getOrgPersonPhotoTopCenterTopOverflow,
  resolveOrgPersonPhotoVisibleAnchorPoint,
} from '../../domains/orgchart/models/orgPersonPhotoAnchors';
import {
  DEFAULT_ORG_CHART_VIEW_MODE,
} from '../../domains/orgchart/models/defaults';
import {
  resolveOrgPersonRenderedCardHeight,
  type OrgPersonCardViewMode,
} from '../../domains/orgchart/models/orgChartShapeVariants';
import {
  resolveSuccessionConnectorPorts,
  resolveSuccessionRightSideRoute,
} from '../../domains/orgchart/services/successionLensEdges';
import { getGenogramPersonSymbolRect } from './lib/genogramPersonSymbolBounds';
import {
  hasFiniteConnectorRouteGeometry,
  isFiniteNodeRect,
  isNodeBelowNodeBounds,
  resolveFiniteNodeRect,
} from '../../core/connectors/utils/nodeRect';
import { resolveEffectiveShapeDimension } from '../../core/shapes/effectiveDimensions';
import { Logger } from '../../utils/logger';
import { Tracker } from '../../domains/analytics/services/Tracker';

const connectorGeometryLogger = Logger.getLoggerWithPrefix('EdgeConnector');
const reportedInvalidConnectorGeometry = new Set<string>();

function reportInvalidConnectorGeometry(
  edge: Edge,
  diagramType: string,
  reason: string,
  invalidFields: string[],
  fallbackAction: 'skip' | 'render',
): void {
  const key = `${edge.id}:${reason}:${invalidFields.join(',')}`;
  if (reportedInvalidConnectorGeometry.has(key)) return;
  reportedInvalidConnectorGeometry.add(key);
  connectorGeometryLogger.warn('Connector has invalid geometry', {
    edgeId: edge.id,
    diagramType,
    reason,
    invalidFields,
    fallbackAction,
  });
  Tracker.track('connector.invalid_geometry', {
    edgeId: edge.id,
    diagramType,
    reason,
    invalidFields,
    fallbackAction,
  });
}

const ORG_LABEL_EDGE_TYPES = new Set([
  'reporting',
  'dotted',
  'matrix',
  'team',
  'occupies',
  'scenario_reporting',
  'reports_to',
  'assistant_to',
  'dotted_line_reports',
  'matrix_reports',
  'team_member',
  'belongs_to',
  'heads',
  'located_at',
  'funded_by',
  'successor_for',
  'mentors',
  'assigned_to',
  'manages_project',
  'is_instance_of',
]);

const DEFAULT_REPORTING_END_ARROW = { type: 'triangle', size: 8 } as const;

const GENOGRAM_STRUCTURAL_CONNECTOR_TYPES = new Set([
  'marriage',
  'child',
  'sibling',
  'half-siblings',
  'twin-mono',
  'twin-di',
]);

const resolveDefaultRoutingPolicyId = (
  diagramType: EdgeConnectorProps['diagramType'],
  edge: Edge,
): string | undefined => {
  if (diagramType === 'org') return resolveOrgRoutingPolicyIdForEdge(edge);
  if (diagramType === 'flowchart') return CORE_ROUTING_POLICY_IDS.processFlow;
  if (diagramType === 'network') return CORE_ROUTING_POLICY_IDS.networkTopology;
  if (diagramType === 'genogram') {
    return GENOGRAM_STRUCTURAL_CONNECTOR_TYPES.has(String(edge.type ?? ''))
      ? CORE_ROUTING_POLICY_IDS.genogramStructural
      : CORE_ROUTING_POLICY_IDS.genogramRelationshipOverlay;
  }
  return undefined;
};

const policyAllowsLayoutSource = (
  policy: ConnectorRoutingPolicy,
  layoutSource: string | undefined,
): boolean => {
  if (!layoutSource) return false;
  if (policy.layoutPointTrust === 'never') return false;
  return policy.allowedLayoutSources.includes(layoutSource);
};

const resolveNodeRect = (node: Node): { x: number; y: number; w: number; h: number } => {
  const baseRect = resolveFiniteNodeRect(node).rect;
  const symbolRect = getGenogramPersonSymbolRect(node, baseRect) ?? baseRect;
  return isFiniteNodeRect(symbolRect) ? symbolRect : baseRect;
};

const getNodeCenterX = (node: Node): number => {
  const rect = resolveNodeRect(node);
  return rect.x + rect.w / 2;
};

const resolveEdgeFromId = (edge: Edge): string | undefined => {
  const legacyEdge = edge as Edge & { sourceShapeId?: unknown };
  const value = edge.from ?? legacyEdge.sourceShapeId;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const resolveEdgeToId = (edge: Edge): string | undefined => {
  const legacyEdge = edge as Edge & { targetShapeId?: unknown };
  const value = edge.to ?? legacyEdge.targetShapeId;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const clampNumber = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

type InferredNetworkEdgeStyle = {
  color?: string;
  width?: number;
  opacity?: number;
  dashed?: boolean;
  dash?: number[];
  endArrow?: { type?: string; size?: number };
};

const inferNetworkImportEdgeStyle = (
  diagramType: EdgeConnectorProps['diagramType'],
  edge: Edge,
): InferredNetworkEdgeStyle | null => {
  if (diagramType !== 'network') return null;
  const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
  if (!attrs.importedFrom && !attrs.infraEdgeKind && !attrs.networkImportEdge) return null;
  const kind = typeof attrs.infraEdgeKind === 'string' ? attrs.infraEdgeKind : edge.type;
  switch (kind) {
    case 'routing':
      return { color: '#15803d', width: 2, opacity: 0.86, endArrow: { type: 'chevron', size: 9 } };
    case 'data_flow':
    case 'connects':
    case 'aggregated':
      return { color: '#2563eb', width: 2.25, opacity: 0.9, endArrow: { type: 'chevron', size: 9 } };
    case 'sg_access':
      return { color: '#d97706', width: 1.25, opacity: 0.28, dashed: true, dash: [8, 6], endArrow: { type: 'chevron', size: 7 } };
    default:
      return null;
  }
};

const snapNearAlignedAxisPoints = (
  from: { x: number; y: number },
  to: { x: number; y: number },
  fromNode: Node | undefined,
  toNode: Node | undefined,
  fromAnchor: 'top' | 'right' | 'bottom' | 'left',
  toAnchor: 'top' | 'right' | 'bottom' | 'left',
  tolerance = 5,
): { from: { x: number; y: number }; to: { x: number; y: number } } => {
  if (!fromNode || !toNode) return { from, to };

  const horizontalPair =
    (fromAnchor === 'right' && toAnchor === 'left') ||
    (fromAnchor === 'left' && toAnchor === 'right');
  if (horizontalPair && Math.abs(from.y - to.y) <= tolerance) {
    const fromRect = resolveNodeRect(fromNode);
    const toRect = resolveNodeRect(toNode);
    const overlapMinY = Math.max(fromRect.y, toRect.y);
    const overlapMaxY = Math.min(fromRect.y + fromRect.h, toRect.y + toRect.h);
    if (overlapMinY <= overlapMaxY) {
      const sharedY = clampNumber((from.y + to.y) / 2, overlapMinY, overlapMaxY);
      return {
        from: { x: from.x, y: sharedY },
        to: { x: to.x, y: sharedY },
      };
    }
  }

  const verticalPair =
    (fromAnchor === 'bottom' && toAnchor === 'top') ||
    (fromAnchor === 'top' && toAnchor === 'bottom');
  if (verticalPair && Math.abs(from.x - to.x) <= tolerance) {
    const fromRect = resolveNodeRect(fromNode);
    const toRect = resolveNodeRect(toNode);
    const overlapMinX = Math.max(fromRect.x, toRect.x);
    const overlapMaxX = Math.min(fromRect.x + fromRect.w, toRect.x + toRect.w);
    if (overlapMinX <= overlapMaxX) {
      const sharedX = clampNumber((from.x + to.x) / 2, overlapMinX, overlapMaxX);
      return {
        from: { x: sharedX, y: from.y },
        to: { x: sharedX, y: to.y },
      };
    }
  }

  return { from, to };
};

interface EdgeConnectorProps {
  edges: Edge[];
  nodes: Node[];
  edge: Edge;
  fromNode: Node | undefined;
  toNode: Node | undefined;
  selected: boolean;
  onClick: (e: any) => void;
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T;
  awareness?: awarenessProtocol.Awareness | null;
  registerRef?: (edgeId: string, node: any | null) => void;
  version?: number; // incremental tick to force recompute when Konva positions move
  onStartTextEdit?: (edgeId: string, textNode: any) => void;
  editingText?: boolean;
  editRequestEdgeId?: string | null;
  diagramType?: 'genogram' | 'flowchart' | 'mindmap' | 'network' | 'org' | 'database' | 'uml' | 'planning' | 'software-design' | 'generic';
  routingPolicyId?: string;
  viewRuleState?: {
    opacity: number;
    visible: boolean;
  };
  secondaryLensColor?: string;
  successorLensColor?: string;
  canWrite?: boolean;
  labelGeometry?: PersonLabelGeometryContext;
}

type EdgeConnectorBodyProps = EdgeConnectorProps & {
  autoLayoutEnabled: boolean;
  orgVerticalSpacing: number;
};

const EdgeConnectorBody: React.FC<EdgeConnectorBodyProps> = ({
  edges,
  nodes,
  edge,
  fromNode,
  toNode,
  selected,
  onClick,
  getShapeState,
  awareness,
  registerRef,
  version,
  onStartTextEdit,
  editingText,
  editRequestEdgeId,
  diagramType = 'genogram',
  routingPolicyId,
  viewRuleState,
  secondaryLensColor,
  canWrite = true,
  autoLayoutEnabled,
  orgVerticalSpacing,
  successorLensColor,
  labelGeometry,
}) => {
  const isStructural = edge.type === 'marriage' || edge.type === 'child';
  const freeEnd = (edge.attrs as any)?.toPoint as { x: number; y: number } | undefined;
  const freeStart = (edge.attrs as any)?.fromPoint as { x: number; y: number } | undefined;

  const [awarenessVersion, setAwarenessVersion] = useState(0);
  // Cache childIsParent result so routing type doesn't flip when snap is off
  const childIsParentCacheRef = useRef<boolean | null>(null);
  const labelTextRef = useRef<Konva.Text | null>(null);
  const setLabelTextRef = useCallback((node: Konva.Text | null) => {
    labelTextRef.current = node;
    if (!node) return;
    if (editingText) return;
    if (editRequestEdgeId !== edge.id) return;
    onStartTextEdit?.(edge.id, node);
  }, [edge.id, editRequestEdgeId, editingText, onStartTextEdit]);
  const getThemeColor = useThemeAwareColor();
  const { doc } = useDiagramDocument();
  const { read: readDiagramSettings } = useDiagramSettings(doc);
  const orgPersonCardViewMode = (readDiagramSettings().orgchart?.viewMode ?? DEFAULT_ORG_CHART_VIEW_MODE) as OrgPersonCardViewMode;
  const connectorLabelFillBase = getThemeColor('#374151', { type: 'foreground' });
  const edgeRelation = (edge.attrs as any)?.relation;
  const isOrgLabelEdge = ORG_LABEL_EDGE_TYPES.has(edge.type as string)
    || (typeof edgeRelation === 'string' && ORG_LABEL_EDGE_TYPES.has(edgeRelation));
  const orgReportCounts = useMemo(
    () => (diagramType === 'org' ? computeOrgReportCounts(nodes, edges) : new Map()),
    [diagramType, nodes, edges],
  );
  const resolveConnectorNodeRect = useCallback((node: Node): { x: number; y: number; w: number; h: number } => {
    const rect = resolveNodeRect(node);
    if (diagramType !== 'org') return rect;

    const nodeType = String(node.type ?? '');
    if (!ORG_PERSON_NODE_TYPES.has(nodeType)) return rect;

    const width = resolveEffectiveShapeDimension(node, 'width') ?? rect.w;
    const persistedHeight = resolveEffectiveShapeDimension(node, 'height') ?? rect.h;
    const counts = orgReportCounts.get(node.id);
    const showFooter = Boolean(counts && (counts.directReports > 0 || counts.totalReports > 0));
    const height = resolveOrgPersonRenderedCardHeight(nodeType, persistedHeight, showFooter);
    const x = typeof node.x === 'number' && Number.isFinite(node.x) ? node.x : rect.x;
    const y = typeof node.y === 'number' && Number.isFinite(node.y) ? node.y : rect.y;

    return { x, y, w: width, h: height };
  }, [diagramType, orgReportCounts]);
  // Local preview state for endpoint dragging (do not persist until drop)
  const [dragPreview, setDragPreview] = useState<{ toPoint?: { x: number; y: number }; fromPoint?: { x: number; y: number } } | null>(null);
  // Hover state for snapping to a marriage bar
  const [hoverMarriageBar, setHoverMarriageBar] = useState<
    | { x1: number; x2: number; y: number; marriageEdgeId: string; parent1Id: string; parent2Id: string }
    | null
  >(null);
  // Hover state for insert step button
  const [isHovered, setIsHovered] = useState(false);
  const [isOverlayHovered, setIsOverlayHovered] = useState(false);
  // Track initial line position for dragging entire line
  const lineDragStartRef = useRef<{ fromPoint: { x: number; y: number }; toPoint: { x: number; y: number } } | null>(null);
  const lineDragCumulativeDeltaRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const lineDragInitialMouseRef = useRef<{ x: number; y: number } | null>(null);
  const lineClickStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const endpointDragInProgressRef = useRef<boolean>(false);
  // Shift constraint state for endpoint handle dragging
  const endpointConstraintDirectionRef = useRef<'horizontal' | 'vertical' | null>(null);
  // - dragStart: used to detect whether the user is moving mostly horizontally/vertically
  // - fixedPoint: the opposite endpoint (kept fixed) that we lock to for true H/V lines
  const endpointConstraintDragStartRef = useRef<{ x: number; y: number } | null>(null);
  const endpointConstraintFixedPointRef = useRef<{ x: number; y: number } | null>(null);
  const CONSTRAINT_THRESHOLD = 5; // pixels to avoid jitter

  const executeEdgeCommand = (updates: Partial<Edge>): void => {
    try {
      // In org tree mode, prevent anchor editing for any org relationship connectors.
      // This avoids hidden anchor changes that only appear after switching Tree OFF.
      if (diagramType === 'org' && autoLayoutEnabled && isOrgLabelEdge) {
        const next: Partial<Edge> = { ...updates };
        // Drop any attempt to change anchors/offsets while in tree mode.
        if ('fromAnchor' in next) delete (next as any).fromAnchor;
        if ('toAnchor' in next) delete (next as any).toAnchor;
        if ('fromOffset' in next) delete (next as any).fromOffset;
        if ('toOffset' in next) delete (next as any).toOffset;
        // Also drop anchor lock toggles (tree mode is always normalized).
        if ('fromAnchorLocked' in next) delete (next as any).fromAnchorLocked;
        if ('toAnchorLocked' in next) delete (next as any).toAnchorLocked;
        updates = next;
      }

      // In org tree mode, secondary edges must always attach to default anchors.
      // This prevents users from drawing dotted/matrix edges to arbitrary sides while Tree is ON.
      if (diagramType === 'org' && autoLayoutEnabled && isSecondaryReportsToEdge) {
        const next: Partial<Edge> = { ...updates };
        if ('from' in next || 'fromAnchor' in next || 'fromOffset' in next) {
          next.fromAnchor = 'top' as any;
          next.fromOffset = 0.5 as any;
        }
        if ('to' in next || 'toAnchor' in next || 'toOffset' in next) {
          next.toAnchor = 'bottom' as any;
          next.toOffset = 0.5 as any;
        }
        updates = next;
      }
      const cmd = new UpdateEdgeCommand(edge.id, updates);
      void getCommandManager().execute(cmd);
    } catch {}
  };

  const translateFlatPointArray = (
    value: unknown,
    deltaX: number,
    deltaY: number,
  ): number[] | undefined => {
    if (!Array.isArray(value) || value.length < 2 || value.length % 2 !== 0) return undefined;
    const shifted: number[] = [];
    for (let i = 0; i < value.length; i += 2) {
      const x = Number(value[i]);
      const y = Number(value[i + 1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
      shifted.push(x + deltaX, y + deltaY);
    }
    return shifted;
  };

  const translateWaypointArray = (
    value: unknown,
    deltaX: number,
    deltaY: number,
  ): Array<Record<string, unknown>> | undefined => {
    if (!Array.isArray(value)) return undefined;
    const shifted: Array<Record<string, unknown>> = [];
    for (const waypoint of value) {
      if (!waypoint || typeof waypoint !== 'object') return undefined;
      const record = waypoint as Record<string, unknown>;
      const x = Number(record.x);
      const y = Number(record.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
      shifted.push({
        ...record,
        x: x + deltaX,
        y: y + deltaY,
      });
    }
    return shifted;
  };

  const buildTranslatedFreeConnectorUpdate = (
    deltaX: number,
    deltaY: number,
    fromPoint: { x: number; y: number },
    toPoint: { x: number; y: number },
  ): Partial<Edge> => {
    const baseAttrs = ((edge.attrs ?? {}) as Record<string, unknown>);
    const attrs: Record<string, unknown> = {
      ...baseAttrs,
      fromPoint,
      toPoint,
    };
    const shiftedLayoutPoints = translateFlatPointArray(baseAttrs.layoutPoints, deltaX, deltaY);
    if (shiftedLayoutPoints) attrs.layoutPoints = shiftedLayoutPoints;

    const shiftedAttrWaypoints = translateWaypointArray(baseAttrs.waypoints, deltaX, deltaY);
    if (shiftedAttrWaypoints) attrs.waypoints = shiftedAttrWaypoints;

    const updates: Partial<Edge> & Record<string, unknown> = { attrs };
    const edgeRecord = edge as Edge & Record<string, unknown>;
    const shiftedPoints = translateFlatPointArray(edgeRecord.points, deltaX, deltaY);
    if (shiftedPoints) updates.points = shiftedPoints;

    const shiftedWaypoints = translateWaypointArray(edgeRecord.waypoints, deltaX, deltaY);
    if (shiftedWaypoints) updates.waypoints = shiftedWaypoints;

    return updates;
  };

  const isSecondaryReportsToEdge = useMemo(() => {
    if (diagramType !== 'org') return false;
    return isOrgSecondaryReportingEdge(edge);
  }, [diagramType, edge.type, edge.attrs]);
  const isSuccessorForEdge = diagramType === 'org'
    && (String(edge.type) === 'successor_for' || edgeRelation === 'successor_for');
  // In tree mode, org edges must remain normalized to default anchors. We still
  // allow the endpoint handle to be dragged and a live preview line to be shown
  // (so users get the same visual feedback as Tree OFF), but on drop we either
  // re-attach to the snapped shape with default anchors (handled inside
  // executeEdgeCommand) or snap the handle back to its original endpoint.
  const orgTreePreviewOnlyDrag = useMemo(() => {
    if (diagramType !== 'org') return false;
    if (!autoLayoutEnabled) return false;
    return isOrgLabelEdge || isOrgEdge(edge) || isSecondaryReportsToEdge;
  }, [diagramType, autoLayoutEnabled, isOrgLabelEdge, edge, isSecondaryReportsToEdge]);

  const isLineShape = useMemo(() => {
    if (edge.type !== 'line') return false;
    const fromIsFree = typeof edge.from === 'string' && edge.from.startsWith('__freeF:');
    const toIsFree = typeof edge.to === 'string' && edge.to.startsWith('__freeT:');
    const a = (edge.attrs as any) || {};
    return fromIsFree || toIsFree || !!a.fromPoint || !!a.toPoint;
  }, [edge]);
  const isFreeEndpointBodyDraggable = !fromNode && !toNode && isFreeEndpointConnector(edge);

  const handleConnectorBodyActivate = (evt: any) => {
    if (!isFreeEndpointBodyDraggable) {
      onClick(evt);
    }
  };

  const handleFreeEndpointBodyMouseDown = (e: any) => {
    const target = e.target as any;
    const targetType = target?.getType?.() || '';

    if (!isFreeEndpointBodyDraggable || !freeStart || !freeEnd) {
      return;
    }

    if (targetType === 'Circle') {
      endpointDragInProgressRef.current = false;
      return;
    }

    const parent = target?.getParent?.();
    if (parent && (parent as any)?.getType?.() === 'Circle') {
      endpointDragInProgressRef.current = false;
      return;
    }

    if (endpointDragInProgressRef.current) {
      return;
    }

    e.cancelBubble = true;
    if (e.evt) {
      e.evt.stopPropagation();
      e.evt.preventDefault();
    }

    const group = e.target as any;
    const stage = group.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (!pointerPos || !stage) {
      return;
    }

    const scale = stage.scaleX();
    const origin = stage.position();
    const startCanvasX = (pointerPos.x - origin.x) / scale;
    const startCanvasY = (pointerPos.y - origin.y) / scale;

    try {
      const store = useDiagramStore.getState();
      const currentSelected: Set<string> = new Set(store.selectedIds);
      if (currentSelected.has(edge.id) && currentSelected.size > 1) {
        let lastDelta = { x: 0, y: 0 };
        try { window.dispatchEvent(new CustomEvent('selection:drag:start', { detail: { sourceId: edge.id } })); } catch {}

        const handleMouseMoveSelection = () => {
          const movePointerPos = stage.getPointerPosition();
          if (!movePointerPos) return;
          const moveScale = stage.scaleX();
          const moveOrigin = stage.position();
          const currentCanvasX = (movePointerPos.x - moveOrigin.x) / moveScale;
          const currentCanvasY = (movePointerPos.y - moveOrigin.y) / moveScale;
          const deltaX = currentCanvasX - startCanvasX;
          const deltaY = currentCanvasY - startCanvasY;
          lastDelta = { x: deltaX, y: deltaY };
          try { window.dispatchEvent(new CustomEvent('selection:drag:update', { detail: { deltaX, deltaY, sourceId: edge.id } })); } catch {}
        };

        const handleMouseUpSelection = () => {
          document.removeEventListener('mousemove', handleMouseMoveSelection);
          document.removeEventListener('mouseup', handleMouseUpSelection);
          try { window.dispatchEvent(new CustomEvent('selection:drag:end', { detail: { deltaX: lastDelta.x, deltaY: lastDelta.y, sourceId: edge.id } })); } catch {}
        };

        document.addEventListener('mousemove', handleMouseMoveSelection);
        document.addEventListener('mouseup', handleMouseUpSelection);
        return;
      }
    } catch {}

    lineClickStartRef.current = {
      x: pointerPos.x,
      y: pointerPos.y,
      time: Date.now()
    };

    lineDragStartRef.current = {
      fromPoint: { ...freeStart },
      toPoint: { ...freeEnd }
    };
    lineDragCumulativeDeltaRef.current = { x: 0, y: 0 };
    lineDragInitialMouseRef.current = {
      x: startCanvasX,
      y: startCanvasY
    };

    const handleMouseMove = () => {
      if (!lineDragStartRef.current || !stage) return;

      const movePointerPos = stage.getPointerPosition();
      if (!movePointerPos) return;

      const moveScale = stage.scaleX();
      const moveOrigin = stage.position();
      const currentCanvasX = (movePointerPos.x - moveOrigin.x) / moveScale;
      const currentCanvasY = (movePointerPos.y - moveOrigin.y) / moveScale;

      const deltaX = currentCanvasX - lineDragInitialMouseRef.current!.x;
      const deltaY = currentCanvasY - lineDragInitialMouseRef.current!.y;

      lineDragCumulativeDeltaRef.current.x = deltaX;
      lineDragCumulativeDeltaRef.current.y = deltaY;

      const newFromPoint = {
        x: lineDragStartRef.current.fromPoint.x + deltaX,
        y: lineDragStartRef.current.fromPoint.y + deltaY
      };
      const newToPoint = {
        x: lineDragStartRef.current.toPoint.x + deltaX,
        y: lineDragStartRef.current.toPoint.y + deltaY
      };

      setDragPreview({
        fromPoint: newFromPoint,
        toPoint: newToPoint
      });
    };

    const handleMouseUp = (upEvt: MouseEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (endpointDragInProgressRef.current) {
        lineDragStartRef.current = null;
        lineClickStartRef.current = null;
        lineDragInitialMouseRef.current = null;
        return;
      }

      if (dragPreview && ((dragPreview.fromPoint && !dragPreview.toPoint) || (dragPreview.toPoint && !dragPreview.fromPoint))) {
        lineDragStartRef.current = null;
        lineClickStartRef.current = null;
        lineDragInitialMouseRef.current = null;
        return;
      }

      if (!lineClickStartRef.current) {
        return;
      }

      const latestPointerPos = stage.getPointerPosition();
      if (latestPointerPos) {
        const dx = Math.abs(latestPointerPos.x - lineClickStartRef.current.x);
        const dy = Math.abs(latestPointerPos.y - lineClickStartRef.current.y);
        const dt = Date.now() - lineClickStartRef.current.time;

        if (dx < 5 && dy < 5 && dt < 300) {
          const syntheticEvt = {
            target: group,
            evt: upEvt
          } as any;
          onClick(syntheticEvt);
        } else if (lineDragStartRef.current) {
          const deltaX = lineDragCumulativeDeltaRef.current.x;
          const deltaY = lineDragCumulativeDeltaRef.current.y;

          const newFromPoint = {
            x: lineDragStartRef.current.fromPoint.x + deltaX,
            y: lineDragStartRef.current.fromPoint.y + deltaY
          };
          const newToPoint = {
            x: lineDragStartRef.current.toPoint.x + deltaX,
            y: lineDragStartRef.current.toPoint.y + deltaY
          };

          const freeFromId = `__freeF:${edge.id}`;
          const freeToId = `__freeT:${edge.id}`;
          executeEdgeCommand({
            from: freeFromId,
            to: freeToId,
            ...buildTranslatedFreeConnectorUpdate(deltaX, deltaY, newFromPoint, newToPoint),
          } as any);
        }
      }

      lineDragStartRef.current = null;
      lineClickStartRef.current = null;
      lineDragInitialMouseRef.current = null;
      setDragPreview(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // If an external edit request targets this edge, and the label Text
  // has been rendered, start the text edit using our own ref. This
  // avoids the need for the canvas to discover the Text node.
  useEffect(() => {
    if (!editRequestEdgeId) return;
    if (editRequestEdgeId !== edge.id) return;
    if (!labelTextRef.current) return;
    onStartTextEdit?.(edge.id, labelTextRef.current);
    // do not clear here; the canvas clears the request when editing state opens
  }, [editRequestEdgeId, edge.id, onStartTextEdit]);

  // Recompute connector endpoints when awareness previews change for connected nodes
  useEffect(() => {
    if (!awareness || !fromNode) return;

    const onChange = () => {
      // Only update if the awareness contains meaningful position previews for our connected nodes
      const states = awareness.getStates();
      const hasRelevantPreview = Array.from(states.values()).some((state: any) => {
        const previews = state.previews;
        if (!previews || typeof previews !== 'object') return false;

        // Check if any preview affects our fromNode or toNode and has meaningful position data
        const fromNodePreview = previews[fromNode.id];
        const toNodePreview = toNode ? previews[toNode.id] : undefined;

        const hasFromNodePositionPreview = fromNodePreview &&
          fromNodePreview.props &&
          (typeof fromNodePreview.props.x === 'number' || typeof fromNodePreview.props.y === 'number');

        const hasToNodePositionPreview = toNodePreview &&
          toNodePreview.props &&
          (typeof toNodePreview.props.x === 'number' || typeof toNodePreview.props.y === 'number');

        return hasFromNodePositionPreview || hasToNodePositionPreview;
      });

      if (hasRelevantPreview) {
        setAwarenessVersion(v => v + 1);
      }
    };

    awareness.on('change', onChange);
    return () => {
      awareness.off('change', onChange);
    };
  }, [awareness, fromNode?.id, toNode?.id]);

  const renderData = useMemo(() => {
    if (isStructural && (!fromNode || !toNode)) return null;
    const fromMerged = fromNode ? (getShapeState ? getShapeState(fromNode.id, fromNode) as Node : fromNode) : undefined;
    const toMerged = toNode ? (getShapeState ? getShapeState(toNode.id, toNode) as Node : toNode) : undefined;

    const getNodeBottomPoint = (node: Node) => getBottomCenter(node, getShapeState);
    const getNodeTopPoint = (node: Node) => getTopCenter(node, getShapeState);

    if (edge.type === 'marriage') {
      if (!fromMerged || !toMerged || !fromNode || !toNode) return null;
      // Marriage connection - horizontal line between partners at bottom
      const from = getMarriageTerminalBottomCenter(fromMerged as Node, getShapeState, labelGeometry);
      const to = getMarriageTerminalBottomCenter(toMerged as Node, getShapeState, labelGeometry);

      // Shared baseline computation to align with child connectors and other renderers
      const midY = computeMarriageBaselineY(fromMerged as any, toMerged as any, getShapeState, labelGeometry);

      // Find all children of this marriage
      return {
        type: 'marriage',
        mainLine: {
          points: [
            from.x, from.y,      // Start from first parent bottom
            from.x, midY,        // Drop down
            to.x, midY,          // Horizontal line
            to.x, to.y           // Up to second parent bottom
          ]
        },
        horizontalSegment: {
          x1: Math.min(from.x, to.x),
          x2: Math.max(from.x, to.x),
          y: midY
        },
        style: {
          dashed: !!edge.attrs?.dashed,
          separated: !!edge.attrs?.separated,
          engaged: !!edge.attrs?.engaged
        }
      };
    }

    if (edge.type === 'child') {
      if (!fromNode) return null;
      // Find parent marriage edge
      const parentMarriageEdge = edges.find(e =>
        e.type === 'marriage' &&
        (e.from === (fromNode as Node).id || e.to === (fromNode as Node).id)
      );

      if (parentMarriageEdge) {
        // Get both parents
        const parent1Base = nodes.find(n => n.id === parentMarriageEdge.from);
        const parent2Base = nodes.find(n => n.id === parentMarriageEdge.to);
        const parent1 = parent1Base ? (getShapeState ? getShapeState(parent1Base.id, parent1Base) as Node : parent1Base) : undefined;
        const parent2 = parent2Base ? (getShapeState ? getShapeState(parent2Base.id, parent2Base) as Node : parent2Base) : undefined;

        if (parent1 && parent2 && toMerged) {
          const p1Bottom = getNodeBottomPoint(parent1);
          const p2Bottom = getNodeBottomPoint(parent2);
          const childTop = getNodeTopPoint(toMerged as Node);

          // Calculate marriage line position via shared util to avoid drift
          // with the marriage adapter (accounts for multi-line name height)
          const marriageY = computeMarriageBaselineY(parent1, parent2, getShapeState, labelGeometry);

          // Check if child is outside parent span
          const parentMinX = Math.min(p1Bottom.x, p2Bottom.x);
          const parentMaxX = Math.max(p1Bottom.x, p2Bottom.x);
          const childX = Math.round(childTop.x);

          // If child is within parent span, simple vertical line
          if (childX >= parentMinX && childX <= parentMaxX) {
            return {
              type: 'child',
              points: [
                childX, marriageY,
                childX, Math.round(childTop.y)
              ]
            };
          }

          // Child is outside parent span - need dashed extension
          const extensionStartX = childX < parentMinX ? parentMinX : parentMaxX;

          return {
            type: 'child',
            points: [
              childX, marriageY,
              childX, Math.round(childTop.y)
            ],
            extension: {
              points: [
                extensionStartX, marriageY,
                childX, marriageY
              ]
            }
          };
        }
      }

      // Fallback for single parent
      if (!fromMerged || !toMerged) return null;
      const from = getNodeBottomPoint(fromMerged as Node);
      const to = getNodeTopPoint(toMerged as Node);

      return {
        type: 'child',
        points: [
          from.x, from.y,
          from.x, from.y + 30,
          to.x, from.y + 30,
          to.x, to.y
        ]
      };
    }

    // Default connector → respect routingType: bezier | straight | orthogonal
    const pickPoint = (node: Node, anchor?: 'top'|'right'|'bottom'|'left', offset?: number) => {
      const nodeType = (node as any).type as string | undefined;
      let { x, y, w, h } = resolveConnectorNodeRect(node);
      if (nodeType === 'freehand') {
        const pts = Array.isArray((node as any)?.attrs?.points) ? ((node as any).attrs.points as number[]) : [];
        if (pts.length >= 4) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (let i = 0; i < pts.length; i += 2) { const px = pts[i] as number; const py = pts[i+1] as number; if (!Number.isFinite(px) || !Number.isFinite(py)) continue; if (px < minX) minX = px; if (py < minY) minY = py; if (px > maxX) maxX = px; if (py > maxY) maxY = py; }
          if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY)) { x = (node as any).x + minX; y = (node as any).y + minY; w = Math.max(1, maxX - minX); h = Math.max(1, maxY - minY); }
        }
      }
      const variant = String((node as any).attrs?.variant ?? (node as any).attrs?.shapeType ?? '').toLowerCase();
      if (variant === 'bpmn-data-store') {
        w = 0.75 * w;
        h = (66.6667 / 80) * h;
      }
      const cardX = x;
      const cardY = y;
      const cardW = w;
      const cardH = h;
      const rect = { x, y, w, h };
      const t = Math.max(0, Math.min(1, offset ?? 0.5));
      if (nodeType === 'org_person_photo' || nodeType === 'org_person_photo_left' || nodeType === 'org_person_photo_right') {
        return resolveOrgPersonPhotoVisibleAnchorPoint(nodeType, anchor ?? 'bottom', cardX, cardY, cardW, cardH, t, orgPersonCardViewMode);
      }
      switch (anchor) {
        case 'top': return { x: rect.x + w * t, y: rect.y };
        case 'right': return { x: rect.x + w, y: rect.y + h * t };
        case 'bottom': return { x: rect.x + w * t, y: rect.y + h };
        case 'left': return { x: rect.x, y: rect.y + h * t };
        default: return { x: rect.x + w / 2, y: rect.y + h / 2 };
      }
    };
    const inferSideBetweenPoints = (
      start: { x: number; y: number },
      end: { x: number; y: number },
    ): 'top' | 'right' | 'bottom' | 'left' => {
      const dxLocal = end.x - start.x;
      const dyLocal = end.y - start.y;
      if (Math.abs(dxLocal) >= Math.abs(dyLocal)) {
        return dxLocal >= 0 ? 'right' : 'left';
      }
      return dyLocal >= 0 ? 'bottom' : 'top';
    };
    const explicitFromAnchor = (edge as any).fromAnchor as 'top' | 'right' | 'bottom' | 'left' | undefined;
    const explicitToAnchor = (edge as any).toAnchor as 'top' | 'right' | 'bottom' | 'left' | undefined;
    const explicitFromOffset = (edge as any).fromOffset as number | undefined;
    const explicitToOffset = (edge as any).toOffset as number | undefined;
    // Older/default connector creation can leave top/bottom anchors on an
    // overlay edge without those ports having been authored by the user.
    // Only locked succession endpoints override the domain side-port resolver.
    const authoredFromAnchor = isSuccessorForEdge && !edge.fromAnchorLocked
      ? undefined
      : explicitFromAnchor;
    const authoredToAnchor = isSuccessorForEdge && !edge.toAnchorLocked
      ? undefined
      : explicitToAnchor;
    const authoredFromOffset = isSuccessorForEdge && !edge.fromAnchorLocked
      ? undefined
      : explicitFromOffset;
    const authoredToOffset = isSuccessorForEdge && !edge.toAnchorLocked
      ? undefined
      : explicitToOffset;
    const resolveSecondaryPort = (
      sourceNode: Node,
      targetNode: Node,
    ): { anchor: 'top' | 'right' | 'bottom' | 'left'; offset: number } => {
      const sRect = resolveConnectorNodeRect(sourceNode);
      const tRect = resolveConnectorNodeRect(targetNode);
      const sCenter = { x: sRect.x + sRect.w / 2, y: sRect.y + sRect.h / 2 };
      const tCenter = { x: tRect.x + tRect.w / 2, y: tRect.y + tRect.h / 2 };
      const dx = tCenter.x - sCenter.x;
      const dy = tCenter.y - sCenter.y;
      const horizontalDominant = Math.abs(dx) > Math.abs(dy) * 0.9;

      if (horizontalDominant) {
        const anchor = dx >= 0 ? 'right' : 'left';
        const offset = clampNumber((tCenter.y - sRect.y) / Math.max(1, sRect.h), 0.18, 0.82);
        return { anchor, offset };
      }

      const anchor = dy >= 0 ? 'bottom' : 'top';
      const offset = clampNumber((tCenter.x - sRect.x) / Math.max(1, sRect.w), 0.18, 0.82);
      return { anchor, offset };
    };
    const provisionalFrom = (dragPreview?.fromPoint)
      ? (dragPreview!.fromPoint)
      : (fromMerged
        ? pickPoint(fromMerged, authoredFromAnchor, authoredFromOffset)
        : (freeStart || { x: (freeEnd?.x ?? 0) - 80, y: (freeEnd?.y ?? 0) })
      );
    const provisionalTo = (dragPreview?.toPoint)
      ? (dragPreview!.toPoint)
      : (toMerged
        ? pickPoint(toMerged, authoredToAnchor, authoredToOffset)
        : (freeEnd || { x: provisionalFrom.x + 80, y: provisionalFrom.y })
      );
    const shouldInferLineAnchors = edge.type === 'line';
    const secondaryPorts = (
      isSecondaryReportsToEdge &&
      fromMerged &&
      toMerged &&
      !dragPreview?.fromPoint &&
      !dragPreview?.toPoint
    )
      ? {
          from: resolveSecondaryPort(fromMerged, toMerged),
          to: resolveSecondaryPort(toMerged, fromMerged),
        }
      : null;
    const successionPorts = (
      isSuccessorForEdge &&
      fromMerged &&
      toMerged &&
      !dragPreview?.fromPoint &&
      !dragPreview?.toPoint
    )
      ? resolveSuccessionConnectorPorts(fromMerged, toMerged)
      : null;
    const fromAnchor = secondaryPorts?.from.anchor || authoredFromAnchor || (shouldInferLineAnchors
      ? inferSideBetweenPoints(provisionalFrom, provisionalTo)
      : successionPorts?.fromAnchor ?? 'bottom');
    const toAnchor = secondaryPorts?.to.anchor || authoredToAnchor || (shouldInferLineAnchors
      ? inferSideBetweenPoints(provisionalTo, provisionalFrom)
      : successionPorts?.toAnchor ?? 'top');
    const rawFrom = (dragPreview?.fromPoint)
      ? (dragPreview!.fromPoint)
      : (fromMerged
        ? pickPoint(
            fromMerged,
            fromAnchor,
            secondaryPorts?.from.offset ?? authoredFromOffset ?? successionPorts?.fromOffset,
          )
        : (freeStart || { x: (freeEnd?.x ?? 0) - 80, y: (freeEnd?.y ?? 0) })
      );
    const rawTo = (dragPreview?.toPoint)
      ? (dragPreview!.toPoint)
      : (toMerged
        ? pickPoint(
            toMerged,
            toAnchor,
            secondaryPorts?.to.offset ?? authoredToOffset ?? successionPorts?.toOffset,
          )
        : (freeEnd || { x: rawFrom.x + 80, y: rawFrom.y })
      );
    const shouldSnapSequenceFlowAxis =
      diagramType === 'flowchart' &&
      (edge as any).type === 'sequence_flow' &&
      !!fromMerged &&
      !!toMerged &&
      !dragPreview?.fromPoint &&
      !dragPreview?.toPoint;
    const { from, to } = shouldSnapSequenceFlowAxis
      ? snapNearAlignedAxisPoints(rawFrom, rawTo, fromMerged, toMerged, fromAnchor, toAnchor)
      : { from: rawFrom, to: rawTo };

    const rotateVec = (vx: number, vy: number, deg: number) => {
      const r = (deg || 0) * Math.PI / 180;
      const cos = Math.cos(r), sin = Math.sin(r);
      return { x: vx * cos - vy * sin, y: vx * sin + vy * cos };
    };
    // outward for start; inward for end
    const sideVector = (side: 'top'|'right'|'bottom'|'left', inward: boolean) => {
      switch (side) {
        case 'top': return inward ? { x: 0, y: 1 } : { x: 0, y: -1 };
        case 'right': return inward ? { x: -1, y: 0 } : { x: 1, y: 0 };
        case 'bottom': return inward ? { x: 0, y: -1 } : { x: 0, y: 1 };
        case 'left': return inward ? { x: 1, y: 0 } : { x: -1, y: 0 };
      }
    };
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.hypot(dx, dy);
    const baseLen = Math.min(160, Math.max(40, dist * 0.35));
    const fromRot = fromMerged ? ((((fromMerged as any).attrs as any)?.rotation as number) || 0) : 0;
    const toRot = toMerged ? ((((toMerged as any).attrs as any)?.rotation as number) || 0) : 0;
    const vFrom0 = sideVector(fromAnchor, false);
    const vTo0 = sideVector(toAnchor, true);
    const vFrom = rotateVec(vFrom0.x, vFrom0.y, fromRot);
    const vTo = rotateVec(vTo0.x, vTo0.y, toRot);
    const c1 = { x: from.x + vFrom.x * baseLen, y: from.y + vFrom.y * baseLen };
    const c2 = { x: to.x - vTo.x * baseLen, y: to.y - vTo.y * baseLen };

    const edgeAttrs = (edge.attrs ?? {}) as Record<string, unknown>;
    const layoutPoints = Array.isArray(edgeAttrs.layoutPoints)
      ? (edgeAttrs.layoutPoints as number[])
      : [];
    const layoutSource = typeof edgeAttrs.layoutSource === 'string' ? edgeAttrs.layoutSource : undefined;
    const layoutDirection = typeof edgeAttrs.layoutDirection === 'string' ? edgeAttrs.layoutDirection : undefined;
    const connectorRoutingPolicy = resolveConnectorRoutingPolicy(
      routingPolicyId ?? resolveDefaultRoutingPolicyId(diagramType, edge),
    );
    const isPrimaryReportsToEdge = isReportsToEdge(edge) || isOrgPrimaryReportingEdge(edge);
    const primaryReportsBelowParent = Boolean(
      isPrimaryReportsToEdge &&
      fromMerged &&
      toMerged &&
      isNodeBelowNodeBounds(fromMerged, toMerged)
    );
    const normalizedLayoutDirection = layoutDirection?.toUpperCase();
    const isHorizontalOrgLayout =
      layoutSource === 'elk' &&
      (normalizedLayoutDirection === 'LEFT' || normalizedLayoutDirection === 'RIGHT');
    const shouldPreferOrgBusOverPrecomputedRoute =
      primaryReportsBelowParent &&
      layoutSource === 'elk' &&
      normalizedLayoutDirection !== 'LEFT' &&
      normalizedLayoutDirection !== 'RIGHT';
    const toLayoutPointRect = (node: Node | null | undefined) => {
      if (!node) return null;
      const rect = resolveConnectorNodeRect(node);
      return { x: rect.x, y: rect.y, width: rect.w, height: rect.h };
    };
    const rectContainsPoint = (
      rect: { x: number; y: number; w: number; h: number },
      point: { x: number; y: number },
    ): boolean => (
      point.x >= rect.x &&
      point.x <= rect.x + rect.w &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.h
    );
    const precomputedLayoutObstacleRects = (() => {
      if (layoutPoints.length < 4) return [] as SpatialRect[];
      const fromId = resolveEdgeFromId(edge);
      const toId = resolveEdgeToId(edge);
      const endpointCenters = [fromMerged, toMerged]
        .filter((node): node is Node => Boolean(node))
        .map((node) => {
          const rect = resolveConnectorNodeRect(node);
          return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
        });

      return nodes.flatMap((node): SpatialRect[] => {
        if (!node || node.id === fromId || node.id === toId) return [];
        if (!isOrgRoutingOccupancyShape({ type: String(node.type ?? ''), id: node.id })) return [];
        const resolved = getShapeState ? getShapeState(node.id, node) as Node : node;
        const attrs = (resolved.attrs ?? {}) as Record<string, unknown>;
        if ((resolved as Node & { visible?: boolean }).visible === false || attrs.visible === false || attrs.hidden === true) {
          return [];
        }

        const rect = resolveConnectorNodeRect(resolved);
        if (!Number.isFinite(rect.x) || !Number.isFinite(rect.y) || rect.w <= 0 || rect.h <= 0) return [];
        const isContainingFrame = String((resolved as Node).type ?? '') === 'frame' &&
          endpointCenters.some((point) => rectContainsPoint(rect, point));
        if (isContainingFrame) return [];

        return [{ x: rect.x, y: rect.y, width: rect.w, height: rect.h }];
      });
    })();
    const layoutSourceAllowedByPolicy = policyAllowsLayoutSource(connectorRoutingPolicy, layoutSource);
    const distanceBetweenPoints = (
      a: { x: number; y: number },
      b: { x: number; y: number },
    ): number => Math.hypot(a.x - b.x, a.y - b.y);
    const layoutEndpointsMatchCurrentAnchors = (() => {
      if (layoutPoints.length < 4) return false;
      const first = { x: layoutPoints[0] as number, y: layoutPoints[1] as number };
      const last = {
        x: layoutPoints[layoutPoints.length - 2] as number,
        y: layoutPoints[layoutPoints.length - 1] as number,
      };
      const tolerance = 8;
      return (
        distanceBetweenPoints(first, from) <= tolerance && distanceBetweenPoints(last, to) <= tolerance
      ) || (
        distanceBetweenPoints(first, to) <= tolerance && distanceBetweenPoints(last, from) <= tolerance
      );
    })();
    const requiresExactLayoutEndpoints = String(fromMerged?.type ?? '') === 'org_person_photo' ||
      String(toMerged?.type ?? '') === 'org_person_photo';
    const precomputedLayoutHonor = evaluatePrecomputedLayoutHonor({
      layoutPoints,
      fromPoint: from,
      toPoint: to,
      fromRect: toLayoutPointRect(fromMerged),
      toRect: toLayoutPointRect(toMerged),
      layoutSource,
      primaryReportsBelowParent,
      isHorizontalOrgLayout,
      shouldPreferOrgBusOverPrecomputedRoute,
      obstacleRects: precomputedLayoutObstacleRects,
      layoutSourceAllowedByPolicy: layoutSourceAllowedByPolicy &&
        (!requiresExactLayoutEndpoints || layoutEndpointsMatchCurrentAnchors),
      validateVisibleGeometry:
        connectorRoutingPolicy.layoutPointTrust === 'validate-visible-geometry',
    });
    const effectiveLayoutPoints = precomputedLayoutHonor.effectivePoints;
    const canHonorPrecomputedLayout = precomputedLayoutHonor.canHonorIgnoringBusPreference;
    const honorPrecomputedLayout = precomputedLayoutHonor.canHonor;

    const attrRoutingType = typeof edgeAttrs.routingType === 'string' ? edgeAttrs.routingType : undefined;
    const attrRouting =
      attrRoutingType === 'straight' ||
      attrRoutingType === 'orthogonal' ||
      attrRoutingType === 'orthogonalRounded' ||
      attrRoutingType === 'bezier' ||
      attrRoutingType === 'rounded'
        ? attrRoutingType
        : undefined;
    const routing: 'straight'|'orthogonal'|'orthogonalRounded'|'bezier'|'rounded' = isSecondaryReportsToEdge
      ? 'bezier'
      : (attrRouting || connectorRoutingPolicy.defaultRoutingType);
    const isScenarioReportingEdge = String(edge.type) === 'scenario_reporting';
    const isOrgOrScenarioReportingEdge = isScenarioReportingEdge || isPrimaryReportsToEdge;
    // While the user is dragging an endpoint handle, bypass any cached
    // layout-specific routes (ELK / obstacle router) so the rendered line
    // follows the live preview point instead of staying anchored to the
    // pre-drag layout. This applies to both Tree ON and Tree OFF.
    const isPreviewingEndpoint = !!(dragPreview?.fromPoint || dragPreview?.toPoint);
    const fromOffsetVal = edge.fromOffset;
    const toOffsetVal = edge.toOffset;
    const fromOffsetIsDefault = fromOffsetVal == null || Math.abs(fromOffsetVal - 0.5) < 0.01;
    const toOffsetIsDefault = toOffsetVal == null || Math.abs(toOffsetVal - 0.5) < 0.01;
    const toAnchorIsDefault = explicitToAnchor === 'bottom' || explicitToAnchor == null;
    const fromAnchorIsOrgBusDefault =
      explicitFromAnchor === 'top' ||
      explicitFromAnchor === 'left' ||
      explicitFromAnchor === 'right' ||
      explicitFromAnchor == null;
    const isDefaultOrgBusLineCandidate = (() => {
      if (
        !isOrgOrScenarioReportingEdge ||
        !fromMerged ||
        !toMerged ||
        isSecondaryReportsToEdge ||
        isPreviewingEndpoint ||
        edge.fromAnchorLocked ||
        edge.toAnchorLocked ||
        !fromAnchorIsOrgBusDefault ||
        !toAnchorIsDefault ||
        !fromOffsetIsDefault ||
        !toOffsetIsDefault
      ) {
        return false;
      }

      return !isHorizontalOrgLayout && isNodeBelowNodeBounds(fromMerged, toMerged);
    })();
    const shouldInferDefaultOrgFallbackAnchors =
      !isDefaultOrgBusLineCandidate &&
      isOrgOrScenarioReportingEdge &&
      fromAnchorIsOrgBusDefault &&
      toAnchorIsDefault &&
      fromOffsetIsDefault &&
      toOffsetIsDefault &&
      !edge.fromAnchorLocked &&
      !edge.toAnchorLocked;

    if (
      isOrgEdge(edge) &&
      (!primaryReportsBelowParent || isHorizontalOrgLayout) &&
      !shouldPreferOrgBusOverPrecomputedRoute &&
      layoutSource === 'elk' &&
      canHonorPrecomputedLayout &&
      !isPreviewingEndpoint
    ) {
      if (routing === 'bezier') {
        // Fall through to bezier rendering below
      } else if (routing === 'rounded') {
        return { type: 'roundedOrthogonal', points: effectiveLayoutPoints } as const;
      } else if (routing === 'orthogonal') {
        return { type: 'orthogonalPolyline', points: effectiveLayoutPoints } as const;
      }
    }

    if (
      !autoLayoutEnabled &&
      isOrgOrScenarioReportingEdge &&
      fromMerged &&
      toMerged &&
      !isSecondaryReportsToEdge &&
      !isPreviewingEndpoint &&
      !isDefaultOrgBusLineCandidate &&
      routing !== 'bezier' &&
      !edge.fromAnchorLocked &&
      !edge.toAnchorLocked
    ) {
      const toManualOrgRouteRect = (node: Node) => {
        const rect = resolveConnectorNodeRect(node);
        if (String(node.type ?? '') === 'org_person_photo') {
          return expandOrgPersonPhotoTopCenterRoutingRect(rect.x, rect.y, rect.w, rect.h, orgPersonCardViewMode);
        }
        return { x: rect.x, y: rect.y, width: rect.w, height: rect.h };
      };
      const obstacleRects = nodes
        .filter((node) => node.id !== fromMerged.id && node.id !== toMerged.id)
        .map((node) => {
          const resolved = getShapeState ? getShapeState(node.id, node) as Node : node;
          return toManualOrgRouteRect(resolved);
        });
      const routeSourceAnchor = shouldInferDefaultOrgFallbackAnchors ? undefined : explicitFromAnchor;
      const routeTargetAnchor = shouldInferDefaultOrgFallbackAnchors ? undefined : explicitToAnchor;
      const route = computeSmartOrthogonalRoute({
        sourceRect: toManualOrgRouteRect(fromMerged),
        targetRect: toManualOrgRouteRect(toMerged),
        sourceAnchor: routeSourceAnchor,
        targetAnchor: routeTargetAnchor,
        preferredTargetAnchors: routeTargetAnchor || shouldInferDefaultOrgFallbackAnchors ? [] : ['bottom'],
        obstacles: obstacleRects,
        gap: 12,
      });
      return {
        type: routing === 'orthogonal' ? 'orthogonalPolyline' : 'roundedOrthogonal',
        points: route.points,
      } as const;
    }

    // Org chart bus-line connectors: org edge children below parent route
    // through a vertical bus at the parent's bottom center X.
    // Leaf children use LEFT/RIGHT anchor → horizontal → bus ↑ parent BOTTOM
    // Branch children (have their own children) use TOP anchor → vertical → bus → parent BOTTOM
    //
    // The bus-line route applies in BOTH Tree ON and Tree OFF for primary org
    // edges, so toggling the tree never visibly shifts an edge's bends. In
    // Tree ON it always applies (anchors are normalized by
    // LayoutOrgChartCommand). In Tree OFF it only applies while the edge's
    // stored anchors / offsets still match the bus-line defaults; if the user
    // has manually moved an endpoint to a non-default side or offset, we fall
    // through to the generic routing below so the user's customization is
    // respected.
    if (
      isOrgOrScenarioReportingEdge &&
      fromMerged &&
      toMerged &&
      !isSecondaryReportsToEdge &&
      routing !== 'bezier'
    ) {
      const toOrgRouteNode = (node: Node): OrgRouteNode => {
        const resolved = getShapeState ? getShapeState(node.id, node) as Node : node;
        const rect = resolveConnectorNodeRect(resolved);
        return {
          id: resolved.id,
          type: String(resolved.type ?? ''),
          x: rect.x,
          y: rect.y,
          width: rect.w,
          height: rect.h,
          visible: (resolved as Node & { visible?: boolean }).visible,
          attrs: (resolved.attrs ?? {}) as Record<string, unknown>,
        };
      };
      const toOrgRouteEdge = (candidate: Edge): OrgRouteEdge => ({
        id: candidate.id,
        type: String(candidate.type ?? ''),
        from: resolveEdgeFromId(candidate),
        to: resolveEdgeToId(candidate),
        fromAnchor: candidate.fromAnchor,
        toAnchor: candidate.toAnchor,
        fromOffset: candidate.fromOffset,
        toOffset: candidate.toOffset,
        fromAnchorLocked: candidate.fromAnchorLocked,
        toAnchorLocked: candidate.toAnchorLocked,
        attrs: (candidate.attrs ?? {}) as Record<string, unknown>,
      });
      const plannedOrgRoute = planOrgPrimaryReportingRoute({
        edge: toOrgRouteEdge(edge),
        nodes: nodes.map(toOrgRouteNode),
        edges: edges.map(toOrgRouteEdge),
        fromNode: toOrgRouteNode(fromMerged),
        toNode: toOrgRouteNode(toMerged),
        autoLayoutEnabled,
        layoutSource,
        fromAnchor: explicitFromAnchor,
        toAnchor: explicitToAnchor,
        fromOffset: explicitFromOffset,
        toOffset: explicitToOffset,
        isPreviewingEndpoint,
        verticalSpacing: orgVerticalSpacing,
        orgPersonCardViewMode,
        childIsParentOverride: !autoLayoutEnabled && childIsParentCacheRef.current !== null
          ? childIsParentCacheRef.current
          : undefined,
      });
      if (plannedOrgRoute) {
        return { type: plannedOrgRoute.type, points: plannedOrgRoute.points } as const;
      }
    }

    if (routing === 'straight') {
      return { type: 'straightLine', points: [from.x, from.y, to.x, to.y] } as const;
    }
    if (
      isSuccessorForEdge &&
      fromAnchor === 'right' &&
      toAnchor === 'right' &&
      !isPreviewingEndpoint
    ) {
      return {
        type: 'roundedOrthogonal',
        points: resolveSuccessionRightSideRoute(from, to),
      } as const;
    }
    // Pre-computed orthogonal polylines from a scene builder.
    // Connector routing policies decide which diagram-specific layout engines
    // are trusted here: org ELK, process swimlane/ELK, and network topology
    // routers all use this same gate before the renderer honors stored points.
    if (honorPrecomputedLayout) {
      // Respect user's routing choice even for ELK-layouted edges
      // Only use ELK layout points for orthogonal/rounded routing types
      if (routing === 'bezier') {
        // Fall through to bezier rendering below
      } else if (routing === 'rounded' || routing === 'orthogonalRounded') {
        return { type: 'roundedOrthogonal', points: effectiveLayoutPoints } as const;
      } else if (routing === 'orthogonal') {
        return { type: 'orthogonalPolyline', points: effectiveLayoutPoints } as const;
      }
      // For 'straight', we already returned at line 390-391
      // For any other case (like bezier), continue to default bezier rendering
    }
    if (routing === 'orthogonal' || routing === 'orthogonalRounded') {
      // Refined orthogonal with a small clearance from nodes and axis chosen from anchors
      const gap = 12;
      const vFrom = sideVector(fromAnchor, false)!; // outward from fromAnchor
      const vTo = sideVector(toAnchor, true)!;      // inward to toAnchor
      const pA = { x: from.x + vFrom.x * gap, y: from.y + vFrom.y * gap };
      const pB = { x: to.x - vTo.x * gap, y: to.y - vTo.y * gap };
      const firstHorizontal = fromAnchor === 'left' || fromAnchor === 'right';
      const pts = [from.x, from.y, pA.x, pA.y];
      const axisForAnchor = (anchor: 'top'|'right'|'bottom'|'left'): 'horizontal'|'vertical' => (
        anchor === 'left' || anchor === 'right' ? 'horizontal' : 'vertical'
      );
      const simplifyOrthogonalPoints = (points: number[], eps = 0.001): number[] => {
        if (!Array.isArray(points) || points.length < 4) return points;

        // Remove consecutive duplicate points
        const deduped: number[] = [];
        for (let i = 0; i < points.length; i += 2) {
          const x = points[i] as number;
          const y = points[i + 1] as number;
          const n = deduped.length;
          if (n >= 2) {
            const lx = deduped[n - 2] as number;
            const ly = deduped[n - 1] as number;
            if (Math.abs(lx - x) <= eps && Math.abs(ly - y) <= eps) continue;
          }
          deduped.push(x, y);
        }

        // Remove collinear interior points
        let simplified = deduped;
        let changed = true;
        while (changed && simplified.length >= 6) {
          changed = false;
          for (let i = 2; i < simplified.length - 2; i += 2) {
            const px = simplified[i - 2] as number;
            const py = simplified[i - 1] as number;
            const cx = simplified[i] as number;
            const cy = simplified[i + 1] as number;
            const nx = simplified[i + 2] as number;
            const ny = simplified[i + 3] as number;
            const collinearX = Math.abs(px - cx) <= eps && Math.abs(cx - nx) <= eps;
            const collinearY = Math.abs(py - cy) <= eps && Math.abs(cy - ny) <= eps;
            if (collinearX || collinearY) {
              simplified = simplified.slice(0, i).concat(simplified.slice(i + 2));
              changed = true;
              break;
            }
          }
        }

        return simplified;
      };
      // Check if this is an org connector (reporting, dotted, matrix, team, occupies)
      const isOrgConnector = (
        (edge.type as any) === 'reporting' ||
        (edge.type as any) === 'dotted' ||
        (edge.type as any) === 'matrix' ||
        (edge.type as any) === 'team' ||
        (edge.type as any) === 'occupies' ||
        (edge.type as any) === 'scenario_reporting'
      );

      // Special handling for org chart connectors (top-to-bottom hierarchy)
      // Only compute staggered bus lines when auto-layout is on — the routing
      // depends on sibling/manager counts which change on add/delete, so skip
      // when snap is off to keep edges visually stable.
      if (
        autoLayoutEnabled &&
        isOrgConnector &&
        fromMerged &&
        toMerged &&
        fromAnchor === 'top' &&
        toAnchor === 'bottom' &&
        from.y > to.y &&
        !isPreviewingEndpoint
      ) {
        const managerRect = resolveConnectorNodeRect(toMerged);
        const managerBottom = managerRect.y + managerRect.h;
        
        // Find all managers at the same level as this manager
        const managerLevel = Math.round(toMerged.y / 120); // Use levelSpacing constant (120)
        const managersAtLevel = nodes
          .filter(n => ORG_NODE_TYPES.has(n.type as string))
          .map(n => {
            const resolved = getShapeState ? getShapeState(n.id, n) as Node : n;
            return { node: resolved, rect: resolveConnectorNodeRect(resolved) };
          })
          .filter(({ rect }) => Math.round(rect.y / 120) === managerLevel)
          .sort((a, b) => a.rect.x - b.rect.x);
        
        const managerIndex = managersAtLevel.findIndex(({ node }) => node.id === edge.to);
        
        // Find all org edges connecting to the same manager
        const orgEdgesToManager = edges.filter((e) => {
          const eType = (e.type as any);
          return (
            (eType === 'reporting' ||
             eType === 'dotted' ||
             eType === 'matrix' ||
             eType === 'team' ||
             eType === 'occupies' ||
             eType === 'scenario_reporting') &&
            e.to === edge.to
          );
        });

        const childTops = orgEdgesToManager
          .map((e) => {
            const child = nodes.find((n) => n.id === e.from);
            if (!child) return null;
            const resolved = getShapeState ? getShapeState(child.id, child) as Node : child;
            return resolveConnectorNodeRect(resolved).y;
          })
          .filter((y): y is number => Number.isFinite(y));
        const currentTop = resolveConnectorNodeRect(fromMerged).y;
        const minChildTop = childTops.length > 0 ? Math.min(...childTops, currentTop) : currentTop;
        const maxBus = minChildTop - 20;

        const baseBusY = managerBottom + 12;
        const busLineSpacing = 20;
        const staggeredBusY = baseBusY + (managerIndex * busLineSpacing);
        const finalBusY = Math.max(staggeredBusY, Math.min(staggeredBusY, maxBus));

        if (finalBusY > to.y + 4 && finalBusY < from.y - 4) {
          const pts = [from.x, from.y, from.x, finalBusY, to.x, finalBusY, to.x, to.y];
          return {
            type: 'orthogonalPolyline',
            points: pts,
          } as const;
        }
      }
      // For org connectors with top/bottom anchors, use cleaner vertical routing
      if (isOrgConnector && fromAnchor === 'top' && toAnchor === 'bottom') {
        // Vertical-first routing: go straight down from source, then horizontal, then up to target
        // This avoids cutting through nodes when source is above target
        if (from.y < to.y) {
          // Source above target: go down, then horizontal, then down to target
          const midY = Math.max(from.y + gap * 2, to.y - gap * 2);
          pts.push(from.x, midY, to.x, midY);
        } else {
          // Source below target: go up, then horizontal, then up to target
          const midY = Math.min(from.y - gap * 2, to.y + gap * 2);
          pts.push(from.x, midY, to.x, midY);
        }
      } else {
        // Prefer a single-bend "L" path when the endpoints are on perpendicular sides.
        // Keep the existing "Z" path when both endpoints are on the same axis (both horizontal or both vertical),
        // so each end stays perpendicular without forcing a long diagonal jump.
        const fromAxis = axisForAnchor(fromAnchor);
        const toAxis = axisForAnchor(toAnchor);
        if (fromAxis !== toAxis) {
          if (fromAxis === 'horizontal') {
            pts.push(pB.x, pA.y);
          } else {
            pts.push(pA.x, pB.y);
          }
          pts.push(pB.x, pB.y);
        } else if (firstHorizontal) {
          // Split the horizontal travel into two equal segments so the "┐/└" looks balanced.
          const midX = (pA.x + pB.x) / 2;
          pts.push(midX, pA.y);
          pts.push(midX, pB.y);
          pts.push(pB.x, pB.y);
        } else {
          // Split the vertical travel into two equal segments so the "┌/┘" looks balanced.
          const midY = (pA.y + pB.y) / 2;
          pts.push(pA.x, midY);
          pts.push(pB.x, midY);
          pts.push(pB.x, pB.y);
        }
      }
      pts.push(to.x, to.y);
      let points = simplifyOrthogonalPoints(pts);
      if (connectorRoutingPolicy.family === 'process-flow' && fromMerged && toMerged && !isPreviewingEndpoint) {
        points = repairOrthogonalEndpointIntersections(points, {
          sourceRect: toLayoutPointRect(fromMerged)!,
          targetRect: toLayoutPointRect(toMerged)!,
          sourceAnchor: fromAnchor,
          targetAnchor: toAnchor,
          sourcePoint: from,
          targetPoint: to,
        });
      }
      return { type: routing === 'orthogonalRounded' ? 'orthogonalRoundedPolyline' : 'orthogonalPolyline', points } as const;
    }
    if (routing === 'rounded') {
      // Rounded orthogonal: right-angled with curved corners (for org chart connectors)
      const gap = 12;
      const vFrom = sideVector(fromAnchor, false)!;
      const vTo = sideVector(toAnchor, true)!;
      const pA = { x: from.x + vFrom.x * gap, y: from.y + vFrom.y * gap };
      const pB = { x: to.x - vTo.x * gap, y: to.y - vTo.y * gap };
      const firstHorizontal = fromAnchor === 'left' || fromAnchor === 'right';
      const pts = [from.x, from.y, pA.x, pA.y];

      // For top/bottom anchors (typical org chart), use vertical-first routing
      if (fromAnchor === 'top' && toAnchor === 'bottom') {
        if (from.y < to.y) {
          const midY = Math.max(from.y + gap * 2, to.y - gap * 2);
          pts.push(from.x, midY, to.x, midY);
        } else {
          const midY = Math.min(from.y - gap * 2, to.y + gap * 2);
          pts.push(from.x, midY, to.x, midY);
        }
      } else if (firstHorizontal) {
        pts.push(pB.x, pA.y);
      } else {
        pts.push(pA.x, pB.y);
      }
      pts.push(pB.x, pB.y, to.x, to.y);
      return { type: 'roundedOrthogonal', points: pts } as const;
    }
    return { type: 'defaultBezier', p0: from, c1, c2, p3: to } as const;

  }, [
    fromNode?.id, fromNode?.x, fromNode?.y, fromNode?.w, fromNode?.h, fromNode?.width, fromNode?.height,
    toNode?.id, toNode?.x, toNode?.y, toNode?.w, toNode?.h, toNode?.width, toNode?.height,
    (edge as any).fromAnchor, (edge as any).toAnchor, (edge as any).fromOffset, (edge as any).toOffset,
    edge.type, edge.attrs, edges, nodes, getShapeState, awarenessVersion, version, autoLayoutEnabled,
    orgVerticalSpacing,
    dragPreview?.fromPoint?.x, dragPreview?.fromPoint?.y, dragPreview?.toPoint?.x, dragPreview?.toPoint?.y,
    isSecondaryReportsToEdge, isSuccessorForEdge, diagramType, routingPolicyId, labelGeometry, resolveConnectorNodeRect,
    orgPersonCardViewMode,
  ]);

  // Resolve live node state (merge optimistic preview if provided)
  const fromResolved = fromNode ? (getShapeState ? getShapeState(fromNode.id, fromNode) as Node : fromNode) : ({} as any);
  const toResolved = toNode ? (getShapeState ? getShapeState(toNode.id, toNode) as Node : toNode) : ({} as any);

  // Calculate midpoint for insert step button (for flowchart sequence_flow connectors)
  const calculateMidpoint = (): { x: number; y: number } | null => {
    if (!renderData) return null;
    if ((edge as any).type !== 'sequence_flow' || diagramType !== 'flowchart') return null;
    
    const renderType = (renderData as any).type;
    if (renderType === 'defaultBezier') {
      const { p0, c1, c2, p3 } = renderData as any;
      const t = 0.5;
      const mt = 1 - t;
      return {
        x: mt * mt * mt * p0.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * p3.x,
        y: mt * mt * mt * p0.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * p3.y
      };
    } else if (renderType === 'orthogonalPolyline' || renderType === 'orthogonalRoundedPolyline') {
      const pts = (renderData as any).points as number[];
      if (pts.length < 4) return null;
      let total = 0;
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const x1 = pts[i] as number;
        const y1 = pts[i + 1] as number;
        const x2 = pts[i + 2] as number;
        const y2 = pts[i + 3] as number;
        total += Math.hypot(x2 - x1, y2 - y1);
      }
      if (total <= 0) return { x: (pts[0] + pts[pts.length - 2]) / 2, y: (pts[1] + pts[pts.length - 1]) / 2 };

      const half = total / 2;
      let acc = 0;
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const x1 = pts[i] as number;
        const y1 = pts[i + 1] as number;
        const x2 = pts[i + 2] as number;
        const y2 = pts[i + 3] as number;
        const seg = Math.hypot(x2 - x1, y2 - y1);
        if (acc + seg >= half) {
          const t = seg > 0 ? (half - acc) / seg : 0;
          return { x: x1 + (x2 - x1) * t, y: y1 + (y2 - y1) * t };
        }
        acc += seg;
      }
      return { x: (pts[0] + pts[pts.length - 2]) / 2, y: (pts[1] + pts[pts.length - 1]) / 2 };
    } else if (renderType === 'straightLine') {
      const pts = (renderData as any).points as number[];
      if (pts.length < 4) return null;
      return { x: (pts[0] + pts[2]) / 2, y: (pts[1] + pts[3]) / 2 };
    }
    return null;
  };

  const midpoint = calculateMidpoint();
  const showInsertButton = midpoint && canWrite && (selected || isHovered || isOverlayHovered) && (edge as any).type === 'sequence_flow' && diagramType === 'flowchart';
  const shouldRenderEdge = Boolean(renderData && (!viewRuleState || viewRuleState.visible));

  // Helper to compute nearest side and offset for a point relative to a node's rect
  const getEffectiveRect = (node: Node) => {
    const rect = resolveConnectorNodeRect(node);
    const { x, w } = rect;
    let { y, h } = rect;
    if ((node as any).type === 'org_person_photo') {
      const topOverflow = getOrgPersonPhotoTopCenterTopOverflow(h, orgPersonCardViewMode);
      y -= topOverflow;
      h += topOverflow;
    }
    return { x, y, w, h };
  };

  // (anchor visualization handled by Overlays; no local anchors here)
  const computeAnchorForPoint = (node: Node, pt: { x: number; y: number }): { side: 'top'|'right'|'bottom'|'left'; offset: number; snapped: { x: number; y: number } } => {
    const rect = getEffectiveRect(node);
    const w = rect.w; const h = rect.h;
    const relX = Math.max(rect.x, Math.min(rect.x + w, pt.x));
    const relY = Math.max(rect.y, Math.min(rect.y + h, pt.y));
    const dTop = Math.abs(relY - rect.y);
    const dBottom = Math.abs(relY - (rect.y + h));
    const dLeft = Math.abs(relX - rect.x);
    const dRight = Math.abs(relX - (rect.x + w));
    let side: 'top'|'right'|'bottom'|'left' = 'top';
    let offset = 0.5;
    let snapped = { x: relX, y: rect.y };
    const minD = Math.min(dTop, dBottom, dLeft, dRight);
    if (minD === dTop) {
      side = 'top';
      offset = (relX - rect.x) / w;
      snapped = { x: rect.x + offset * w, y: rect.y };
    } else if (minD === dBottom) {
      side = 'bottom';
      offset = (relX - rect.x) / w;
      snapped = { x: rect.x + offset * w, y: rect.y + h };
    } else if (minD === dLeft) {
      side = 'left';
      offset = (relY - rect.y) / h;
      snapped = { x: rect.x, y: rect.y + offset * h };
    } else {
      side = 'right';
      offset = (relY - rect.y) / h;
      snapped = { x: rect.x + w, y: rect.y + offset * h };
    }
    offset = Math.max(0, Math.min(1, offset));
    return { side, offset, snapped };
  };

  const fromDockTableId = (((fromNode as any)?.attrs as any)?.tableDock?.tableId as string | undefined) || null;
  const toDockTableId = (((toNode as any)?.attrs as any)?.tableDock?.tableId as string | undefined) || null;

  // Find nearest shape to snap the free endpoint. Returns best side/offset and snapped point.
  const findSnapTarget = (
    pt: { x: number; y: number },
    excludeNodeId?: string | null,
    excludeTableId?: string | null,
  ): { id: string; side: 'top'|'right'|'bottom'|'left'; offset: number; snapped: { x: number; y: number }; dist: number } | null => {
    let best: { id: string; side: 'top'|'right'|'bottom'|'left'; offset: number; snapped: { x: number; y: number }; dist: number } | null = null;
    const margin = 16; // snap margin in px
    for (const n of nodes) {
      if (!n || (excludeNodeId && n.id === excludeNodeId)) continue;
      // Tables are valid snap targets, except when the connector originates from a shape
      // docked inside that same table (avoid snapping to the table border).
      if (excludeTableId && (n as any)?.type === 'table' && n.id === excludeTableId) continue;
      const rect = getEffectiveRect(n);
      const nearX = pt.x >= rect.x - margin && pt.x <= rect.x + rect.w + margin;
      const nearY = pt.y >= rect.y - margin && pt.y <= rect.y + rect.h + margin;
      if (!nearX || !nearY) continue;
      const { side, offset, snapped } = computeAnchorForPoint(n, pt);
      const dx = snapped.x - pt.x; const dy = snapped.y - pt.y;
      const d2 = dx*dx + dy*dy;
      if (best == null || d2 < best.dist) {
        best = { id: n.id, side, offset, snapped, dist: d2 };
      }
    }
    return best;
  };

  // Precompute marriage bars for snapping and hover indicator
  const marriageBars = useMemo(() => {
    if (!shouldRenderEdge) return [];
    const bars: Array<{ x1: number; x2: number; y: number; marriageEdgeId: string; parent1Id: string; parent2Id: string }> = [];
    for (const e of edges) {
      if (e.type !== 'marriage') continue;
      const p1 = nodes.find((n) => n.id === e.from);
      const p2 = nodes.find((n) => n.id === e.to);
      if (!p1 || !p2) continue;
      const m1 = getShapeState ? (getShapeState(p1.id, p1) as Node) : p1;
      const m2 = getShapeState ? (getShapeState(p2.id, p2) as Node) : p2;
      let x1 = Math.min(getNodeCenterX(m1), getNodeCenterX(m2));
      let x2 = Math.max(getNodeCenterX(m1), getNodeCenterX(m2));
      const y = computeMarriageBaselineY(m1, m2, getShapeState, labelGeometry);

      // Expand the detectable bar extent to cover dashed extensions
      // when children lie outside the parent span.
      const childEdges = edges.filter((ce) => ce.type === 'child' && (ce.from === e.from || ce.from === e.to));
      if (childEdges.length > 0) {
        for (const ce of childEdges) {
          const child = nodes.find((n) => n.id === ce.to);
          if (!child) continue;
          const cm = getShapeState ? (getShapeState(child.id, child) as Node) : child;
          const cxc = getNodeCenterX(cm);
          if (cxc < x1) x1 = cxc;
          if (cxc > x2) x2 = cxc;
        }
      }
      if (Number.isFinite(x1) && Number.isFinite(x2) && Number.isFinite(y)) {
        bars.push({ x1, x2, y, marriageEdgeId: e.id, parent1Id: e.from, parent2Id: e.to });
      }
    }
    return bars;
  }, [edges, nodes, getShapeState, shouldRenderEdge]);

  const findMarriageBarSnap = (
    pt: { x: number; y: number }
  ): | { snapped: { x: number; y: number }; bar: { x1: number; x2: number; y: number; marriageEdgeId: string; parent1Id: string; parent2Id: string } }
    | null => {
    const margin = 24; // generous tolerance so users feel the snap
    let best: { snapped: { x: number; y: number }; bar: { x1: number; x2: number; y: number; marriageEdgeId: string; parent1Id: string; parent2Id: string }; d2: number } | null = null;
    for (const bar of marriageBars) {
      if (pt.x < bar.x1 - margin || pt.x > bar.x2 + margin) continue;
      if (Math.abs(pt.y - bar.y) > margin) continue;
      const clampedX = Math.max(bar.x1, Math.min(bar.x2, pt.x));
      const dx = clampedX - pt.x;
      const dy = bar.y - pt.y;
      const d2 = dx * dx + dy * dy;
      if (!best || d2 < best.d2) best = { snapped: { x: clampedX, y: bar.y }, bar, d2 };
    }
    // Also allow snapping when near an existing child connector's vertical drop
    for (const ce of edges) {
      if (ce.type !== 'child') continue;
      const parentMarriage = edges.find((m) => m.type === 'marriage' && (m.from === ce.from || m.to === ce.from));
      if (!parentMarriage) continue;
      const p1 = nodes.find((n) => n.id === parentMarriage.from);
      const p2 = nodes.find((n) => n.id === parentMarriage.to);
      const child = nodes.find((n) => n.id === ce.to);
      if (!p1 || !p2 || !child) continue;
      const y = computeMarriageBaselineY(p1 as any, p2 as any, getShapeState, labelGeometry);
      const cx = getNodeCenterX(child);
      const nearVertical = Math.abs(pt.x - cx) <= margin && Math.abs(pt.y - y) <= margin;
      if (nearVertical) {
        const bar = marriageBars.find((b) => b.marriageEdgeId === parentMarriage.id);
        if (bar) {
          const dx = cx - pt.x; const dy = y - pt.y; const d2 = dx*dx + dy*dy;
          if (!best || d2 < best.d2) best = { snapped: { x: cx, y }, bar, d2 };
        }
      }
    }
    return best ? { snapped: best.snapped, bar: best.bar } : null;
  };


  // Check if this edge is highlighted
  const highlightedEdgeIds = useHighlightStore((s) => s.highlightedEdgeIds);
  const highlightColor = useHighlightStore((s) => s.highlightColor);
  const isEdgeHighlighted = highlightedEdgeIds.has(edge.id);

  const edgeOpacity = viewRuleState?.opacity ?? 1;
  const { mode } = useThemeMode();
  const edgeBackgroundColor = mode === 'dark' ? '#0E162A' : '#F7F8FA';
  const edgeBlendRatio = edgeOpacity < 1 ? Math.max(0, Math.min(1, 1 - edgeOpacity)) : 0;
  const orgLabelVerticalNudge = isOrgLabelEdge ? -1 : 0;
  const applyEdgeDimColor = useCallback((color: string) => (
    edgeBlendRatio > 0
      ? blendColor(color, edgeBackgroundColor, edgeBlendRatio, edgeBackgroundColor)
      : color
  ), [edgeBackgroundColor, edgeBlendRatio]);
  const inferredNetworkStyle = inferNetworkImportEdgeStyle(diagramType, edge);
  const effectiveEdgeOpacity = Math.min(edgeOpacity, inferredNetworkStyle?.opacity ?? 1);
  const effectiveEdgeBlendRatio = effectiveEdgeOpacity < 1 ? Math.max(0, Math.min(1, 1 - effectiveEdgeOpacity)) : 0;
  const applyEffectiveEdgeDimColor = useCallback((color: string) => (
    effectiveEdgeBlendRatio > 0
      ? blendColor(color, edgeBackgroundColor, effectiveEdgeBlendRatio, edgeBackgroundColor)
      : color
  ), [edgeBackgroundColor, effectiveEdgeBlendRatio]);
  const connectorLabelFill = applyEdgeDimColor(connectorLabelFillBase);

  if (!renderData || (viewRuleState && !viewRuleState.visible)) {
    return null;
  }

  const endpointResolutions = [fromNode, toNode]
    .filter((node): node is Node => Boolean(node))
    .map((node) => {
      const resolved = getShapeState ? getShapeState(node.id, node) as Node : node;
      const resolution = resolveFiniteNodeRect(resolved);
      return { nodeId: node.id, resolution };
    });
  const diagnosticFields = endpointResolutions.flatMap(({ nodeId, resolution }) =>
    resolution.invalidFields.map((field) => `${nodeId}.${field}`));
  const unrenderableFields = endpointResolutions
    .filter(({ resolution }) => !resolution.renderable)
    .flatMap(({ nodeId, resolution }) => resolution.invalidFields.length > 0
      ? resolution.invalidFields.map((field) => `${nodeId}.${field}`)
      : [`${nodeId}.position`]);
  if (diagnosticFields.length > 0) {
    reportInvalidConnectorGeometry(edge, diagramType, 'endpoint-diagnostic', diagnosticFields, 'render');
  }
  if (unrenderableFields.length > 0) {
    reportInvalidConnectorGeometry(edge, diagramType, 'endpoint', unrenderableFields, 'skip');
    return null;
  }
  if (!hasFiniteConnectorRouteGeometry(renderData)) {
    reportInvalidConnectorGeometry(edge, diagramType, 'route', ['routePoints'], 'skip');
    return null;
  }

  const getStrokeColor = () => {
    // Highlight takes precedence for connectors
    if (isEdgeHighlighted) return applyEffectiveEdgeDimColor(highlightColor);
    const c = (edge.attrs as any)?.color as string | undefined;
    const inferredColor = inferredNetworkStyle?.color;
    const secondaryColor = getThemeColor('#0EA5A4', { type: 'accent' });
    if (selected && isSuccessorForEdge) {
      return applyEffectiveEdgeDimColor(c || inferredColor || getThemeColor('#3478DD', { type: 'accent' }));
    }
    if (isSuccessorForEdge && successorLensColor) {
      return applyEffectiveEdgeDimColor(successorLensColor);
    }
    if (isSecondaryReportsToEdge && secondaryLensColor) {
      return applyEffectiveEdgeDimColor(secondaryLensColor);
    }
    const resolvedColor = selected
      ? (c || inferredColor || getThemeColor('#3478DD', { type: 'accent' }))
      : (isSecondaryReportsToEdge
        ? (c || inferredColor || secondaryColor)
        : getThemeColor(c || inferredColor || '#333', { type: 'foreground' }));
    return applyEffectiveEdgeDimColor(resolvedColor);
  };

  const getLineDash = () => {
    if (isSecondaryReportsToEdge) return [8, 6];
    const dashed = (edge.attrs as any)?.dashed as boolean | undefined;
    const dashDot = (edge.attrs as any)?.dashDot as boolean | undefined;
    const dashDoubleDot = (edge.attrs as any)?.dashDoubleDot as boolean | undefined;
    const dashRaw = (edge.attrs as any)?.dash;
    const dash = Array.isArray(dashRaw) ? dashRaw : undefined;
    if (dashDoubleDot) return dash && dash.length ? dash : [12, 4, 2, 4, 2, 4];
    if (dashDot) return dash && dash.length ? dash : [12, 2, 1, 2];
    if (dashed || inferredNetworkStyle?.dashed) return dash && dash.length ? dash : (inferredNetworkStyle?.dash ?? [6, 6]);
    return [];
  };

  const getLineWidth = () => (
    typeof (edge.attrs as any)?.width === 'number'
      ? (edge.attrs as any).width
      : (inferredNetworkStyle?.width ?? (selected ? 3 : 2))
  );

  const getEndArrow = () => {
    const explicitEndArrow = (edge.attrs as any)?.endArrow as { type?: string; size?: number } | undefined;
    if (explicitEndArrow) return explicitEndArrow;
    if (diagramType === 'org' && !isSecondaryReportsToEdge && isReportsToEdge(edge)) {
      return DEFAULT_REPORTING_END_ARROW;
    }
    return inferredNetworkStyle?.endArrow;
  };

  // Special rendering for marriage with extensions and/or divorce
  if (edge.type === 'marriage' && renderData.type === 'marriage' && renderData.horizontalSegment && renderData.mainLine) {
    const horizontalSegment = renderData.horizontalSegment;
    const mainLine = renderData.mainLine;
    const marriageStyle = renderData.style;
    const divorced = edge.attrs?.divorced;
    const midX = (horizontalSegment.x1 + horizontalSegment.x2) / 2;
    const midY = horizontalSegment.y;
    const width = getLineWidth();

    return (
      <Group ref={(n) => registerRef?.(edge.id, n)} opacity={1} onMouseDown={handleFreeEndpointBodyMouseDown}>
        {hoverMarriageBar && (
          <Line
            points={[hoverMarriageBar.x1, hoverMarriageBar.y, hoverMarriageBar.x2, hoverMarriageBar.y]}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={14}
            opacity={0.25}
            listening={false}
          />
        )}
        {hoverMarriageBar && (
          <Line
            points={[hoverMarriageBar.x1, hoverMarriageBar.y, hoverMarriageBar.x2, hoverMarriageBar.y]}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={14}
            opacity={0.25}
            listening={false}
          />
        )}
        {hoverMarriageBar && (
          <Line
            points={[hoverMarriageBar.x1, hoverMarriageBar.y, hoverMarriageBar.x2, hoverMarriageBar.y]}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={14}
            opacity={0.25}
            listening={false}
          />
        )}
        {selected && (
          <Shape
            sceneFunc={(context, shape) => {
              context.beginPath();
              context.moveTo(mainLine.points[0], mainLine.points[1]);
              for (let i = 2; i < mainLine.points.length; i += 2) {
                context.lineTo(mainLine.points[i], mainLine.points[i + 1]);
              }
              context.fillStrokeShape(shape);
            }}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={12}
            opacity={0.4}
            shadowColor={getThemeColor('#3478DD', { type: 'accent' })}
            shadowBlur={10}
            shadowOpacity={0.7}
            lineCap="round"
            listening={false}
            perfectDrawEnabled={false}
          />
        )}
        {selected && (
          <Line
            points={[horizontalSegment.x1, horizontalSegment.y, horizontalSegment.x2, horizontalSegment.y]}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={14}
            opacity={0.35}
            lineCap="round"
            listening={false}
            perfectDrawEnabled={false}
          />
        )}
        {/* Main marriage line */}
        <Shape
          sceneFunc={(context, shape) => {
            context.beginPath();
            const lineDash = getLineDash();
            if (lineDash && lineDash.length) {
              context.setLineDash(lineDash);
            }
            context.moveTo(mainLine.points[0], mainLine.points[1]);
            for (let i = 2; i < mainLine.points.length; i += 2) {
              context.lineTo(mainLine.points[i], mainLine.points[i + 1]);
            }
            context.fillStrokeShape(shape);
          }}
          stroke={getStrokeColor()}
          strokeWidth={width}
          dash={getLineDash()}
          onClick={onClick}
          onTap={onClick}
          hitStrokeWidth={10}
          perfectDrawEnabled={false}
        />

        {/* NOTE:
            Marriage extension segments are intentionally hidden in live canvas.
            Child edges already render their own horizontal extension from the
            marriage span to out-of-span children, and drawing both produces
            a protruding artifact in imported genograms. */}

        {/* Divorce slashes */}
        {divorced && (
          <>
            <Line
              points={[midX - 10, midY - 10, midX + 10, midY + 10]}
              stroke={getStrokeColor()}
              strokeWidth={2}
              perfectDrawEnabled={false}
            />
            <Line
              points={[midX - 5, midY - 10, midX + 15, midY + 10]}
              stroke={getStrokeColor()}
              strokeWidth={2}
              perfectDrawEnabled={false}
            />
          </>
        )}
        {marriageStyle?.separated && !divorced && (
          <Line
            points={[midX - 8, midY - 8, midX + 8, midY + 8]}
            stroke={getStrokeColor()}
            strokeWidth={2}
            perfectDrawEnabled={false}
          />
        )}
        {marriageStyle?.engaged && (
          <Circle x={midX} y={midY} radius={4} fill={getStrokeColor()} />
        )}

        {/* House marker for cohabitation */}
        {(edge.attrs as any)?.house && (
          <Shape
            sceneFunc={(context, shape) => {
              const w = 10, h = 8;
              const roof = 6;
              context.beginPath();
              context.moveTo(midX - w/2, midY);
              context.lineTo(midX - w/2, midY - h);
              context.lineTo(midX, midY - h - roof);
              context.lineTo(midX + w/2, midY - h);
              context.lineTo(midX + w/2, midY);
              context.closePath();
              context.fillStrokeShape(shape);
            }}
            stroke={getStrokeColor()}
            fill={undefined}
            strokeWidth={width}
            perfectDrawEnabled={false}
          />
        )}
      </Group>
    );
  }

  // Render child (polyline with optional dashed extension)
  if ((renderData as any).type === 'child' && (renderData as any).points && (renderData as any).points.length >= 2) {
    const width = getLineWidth();
    return (
      <Group ref={(n) => registerRef?.(edge.id, n)} opacity={1} onMouseDown={handleFreeEndpointBodyMouseDown}>
        {/* Dashed horizontal extension if child is outside parent span */}
        {(renderData as any).extension && (
          <>
            {selected && (
              <Line
                points={(renderData as any).extension.points}
                stroke={getThemeColor('#3478DD', { type: 'accent' })}
                strokeWidth={6}
                opacity={0.2}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}
            <Line
              points={(renderData as any).extension.points}
              stroke={getStrokeColor()}
              strokeWidth={width}
              dash={getLineDash()}
              opacity={0.7}
              onClick={handleConnectorBodyActivate}
              onTap={handleConnectorBodyActivate}
              hitStrokeWidth={10}
              perfectDrawEnabled={false}
            />
          </>
        )}

        {/* Main vertical line from marriage bar to child */}
        {selected && (
          <Shape
            sceneFunc={(context, shape) => {
              context.beginPath();
              context.moveTo((renderData as any).points[0], (renderData as any).points[1]);
              for (let i = 2; i < (renderData as any).points.length; i += 2) {
                context.lineTo((renderData as any).points[i], (renderData as any).points[i + 1]);
              }
              context.fillStrokeShape(shape);
            }}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={6}
            opacity={0.25}
            listening={false}
          />
        )}
        <Shape
          sceneFunc={(context, shape) => {
            context.beginPath();
            context.moveTo((renderData as any).points[0], (renderData as any).points[1]);

            for (let i = 2; i < (renderData as any).points.length; i += 2) {
              context.lineTo((renderData as any).points[i], (renderData as any).points[i + 1]);
            }

            context.fillStrokeShape(shape);
          }}
          stroke={getStrokeColor()}
          strokeWidth={width}
          dash={getLineDash()}
          onClick={onClick}
          onTap={onClick}
          hitStrokeWidth={10}
        />
      </Group>
    );
  }

  // Default Bezier with arrowhead and label
  if ((renderData as any).type === 'defaultBezier') {
    const { p0, c1, c2, p3 } = renderData as any as {
      p0: { x: number; y: number };
      c1: { x: number; y: number };
      c2: { x: number; y: number };
      p3: { x: number; y: number };
    };
    const color = getStrokeColor();
    const width = getLineWidth();
    const endArrow = getEndArrow();
    const startArrow = (edge.attrs as any)?.startArrow as { type?: string; size?: number } | undefined;
    const attrs = (edge.attrs as any) || {};
    const doubleLine = attrs.doubleLine === true;
    const tripleLine = attrs.tripleLine === true;
    const midTicks = attrs.midTicks === true;
    const midCircle = attrs.midCircle === true;
    const waveLine = attrs.wave === true;
    const cutoffRepaired = attrs.cutoffRepaired === true;
    const neverMet = attrs.neverMet === true;
    const bestFriends = attrs.bestFriends === true;
    const emotionalConnection = attrs.emotionalConnection === true;
    const inLoveHarmony = attrs.inLoveHarmony === true;
    const distrust = attrs.distrust === true;
    const fusedHostile = attrs.fusedHostile === true;
    const distanceHostile = attrs.distanceHostile === true;
    const closeHostile = attrs.closeHostile === true;
    const distantViolence = attrs.distantViolence === true;
    const closeViolence = attrs.closeViolence === true;
    const focusedNegatively = attrs.focusedNegatively === true;
    const abusePattern = attrs.abusePattern === true;
    const physicalAbuse = attrs.physicalAbuse === true;
    const emotionalAbuse = attrs.emotionalAbuse === true;
    const sexualAbuse = attrs.sexualAbuse === true;
    const neglectAbuse = attrs.neglectAbuse === true;
    const manipulative = attrs.manipulative === true;
    const controlling = attrs.controlling === true;
    const jealous = attrs.jealous === true;
    const fanAdmirer = attrs.fanAdmirer === true;
    const limerence = attrs.limerence === true;
    const lineOffsets = tripleLine ? [-4, 0, 4] : doubleLine ? [-3.5, 3.5] : [0];
    const sampleBezier = (t: number) => {
      const mt = 1 - t;
      const x = mt * mt * mt * p0.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * p3.x;
      const y = mt * mt * mt * p0.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * p3.y;
      return { x, y };
    };
    const sampleTangent = (t: number) => {
      const mt = 1 - t;
      const dx = 3 * mt * mt * (c1.x - p0.x) + 6 * mt * t * (c2.x - c1.x) + 3 * t * t * (p3.x - c2.x);
      const dy = 3 * mt * mt * (c1.y - p0.y) + 6 * mt * t * (c2.y - c1.y) + 3 * t * t * (p3.y - c2.y);
      return { dx, dy };
    };
    const normalFromTangent = (dx: number, dy: number) => {
      const len = Math.hypot(dx, dy) || 1;
      return { nx: -dy / len, ny: dx / len };
    };
    const midPoint = sampleBezier(0.5);
    const midTangent = sampleTangent(0.5);
    const { nx: midNx, ny: midNy } = normalFromTangent(midTangent.dx, midTangent.dy);
    const offsetBezier = (offset: number) => {
      const apply = (pt: { x: number; y: number }) => ({ x: pt.x + midNx * offset, y: pt.y + midNy * offset });
      return { p0: apply(p0), c1: apply(c1), c2: apply(c2), p3: apply(p3) };
    };
    const renderBezier = (ctx: any, offset: number) => {
      const bez = offset === 0 ? { p0, c1, c2, p3 } : offsetBezier(offset);
      ctx.moveTo(bez.p0.x, bez.p0.y);
      ctx.bezierCurveTo(bez.c1.x, bez.c1.y, bez.c2.x, bez.c2.y, bez.p3.x, bez.p3.y);
    };
    const renderWave = (ctx: any) => {
      // Jagged zig-zag along the curve (closer to SVG preview)
      // Calculate line length for proper wave frequency
      const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
      const steps = Math.max(20, Math.floor(lineLength / 5)); // ~5px per step
      const amplitude = 3;
      // Use fixed pixel distance for fade (15px), converted to t-space
      const fadePixels = 15;
      const fadeZone = Math.min(0.15, fadePixels / lineLength);
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const base = sampleBezier(t);
        const tan = sampleTangent(t);
        const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
        // Fade amplitude at both ends so zigzag touches the shapes
        let amp = amplitude;
        if (t < fadeZone) {
          amp = amplitude * (t / fadeZone);
        } else if (t > 1 - fadeZone) {
          amp = amplitude * ((1 - t) / fadeZone);
        }
        const offset = (i % 2 === 0 ? 1 : -1) * amp;
        const x = base.x + nx * offset;
        const y = base.y + ny * offset;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
    };
    const showCaptions = usePerspectiveStore.getState().showRelationshipCaptions;
    const label = resolveGenogramConnectorDisplayLabel({
      diagramType,
      edgeType: edge.type as string,
      attrs: ((edge.attrs as any) || {}) as Record<string, unknown>,
      showCaptions: isSecondaryReportsToEdge ? false : showCaptions,
    });
    const labelOffset = typeof (edge.attrs as any)?.labelOffset === 'number' ? (edge.attrs as any).labelOffset : 0.5;

    // Arrowhead geometry based on tangent at t=1 => direction = p3 - c2
    const dirX = p3.x - c2.x;
    const dirY = p3.y - c2.y;
    const len = Math.hypot(dirX, dirY) || 1;
    const ux = dirX / len;
    const uy = dirY / len;
    const pointerLength = 12;
    const pointerWidth = 8;
    const baseX = p3.x - ux * pointerLength;
    const baseY = p3.y - uy * pointerLength;
    const px = -uy * (pointerWidth / 2);
    const py = ux * (pointerWidth / 2);
    const leftX = baseX + px;
    const leftY = baseY + py;
    const rightX = baseX - px;
    const rightY = baseY - py;

    return (
      <Group ref={(n) => registerRef?.(edge.id, n)} opacity={1} onMouseDown={handleFreeEndpointBodyMouseDown}>
        {selected && (
          <Shape
            sceneFunc={(context, shape) => {
              context.beginPath();
              context.moveTo(p0.x, p0.y);
              context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p3.x, p3.y);
              context.strokeShape(shape);
            }}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={width + 4}
            opacity={0.25}
            listening={false}
          />
        )}
        {/* Base connector line.
            For hostile variants we render a custom style (2 solid + zigzag / dashed + zigzag),
            so we must NOT also draw the default line underneath. */}
        {(distanceHostile || closeHostile || distantViolence || closeViolence || emotionalConnection || physicalAbuse || emotionalAbuse || sexualAbuse || abusePattern || neglectAbuse) && (
          <Shape
            sceneFunc={(context, shape) => {
              context.beginPath();
              context.moveTo(p0.x, p0.y);
              context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p3.x, p3.y);
              context.strokeShape(shape);
            }}
            // Keep this effectively invisible but still clickable (full path + arrow area)
            stroke="rgba(0,0,0,0.01)"
            strokeWidth={width}
            onClick={handleConnectorBodyActivate}
            onTap={handleConnectorBodyActivate}
            hitStrokeWidth={18}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        )}
        {!distanceHostile && !closeHostile && !distantViolence && !closeViolence && !emotionalConnection && !physicalAbuse && !emotionalAbuse && !sexualAbuse && !abusePattern && !neglectAbuse && (
          <Shape
            sceneFunc={(context, shape) => {
              context.save();
              context.strokeStyle = color;
              context.lineWidth = width;
              context.beginPath();
              const dash = getLineDash();
              if (dash && dash.length) context.setLineDash(dash);
              if (waveLine) {
                renderWave(context);
              } else {
                lineOffsets.forEach((off) => renderBezier(context, off));
              }
              context.strokeShape(shape);
              context.restore();
            }}
            stroke={color}
            strokeWidth={width}
            dash={getLineDash()}
            onClick={handleConnectorBodyActivate}
            onTap={handleConnectorBodyActivate}
            hitStrokeWidth={18}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        )}

        {/* Arrowheads */}
        {(() => {
          // Skip default arrow for custom-rendered relationships
          if (distanceHostile || closeHostile || distantViolence || closeViolence || emotionalConnection) return null;
          // Skip default arrow for abuse patterns (have custom arrows)
          if (abusePattern || physicalAbuse || emotionalAbuse || sexualAbuse || neglectAbuse) return null;
          const type = (endArrow?.type || 'triangle') as string;
          if (type === 'none') return null;
          const size = (endArrow?.size as number) || 8;
          if (type === 'circle') {
            // Shift back by radius so circle sits fully outside the node
            const cx = p3.x - ux * (size / 2);
            const cy = p3.y - uy * (size / 2);
            return <Circle x={cx} y={cy} radius={size/2} fill={color} />;
          }
          if (type === 'circleOutline') {
            const cx = p3.x - ux * (size / 2);
            const cy = p3.y - uy * (size / 2);
            return <Circle x={cx} y={cy} radius={size/2} fill="white" stroke={color} strokeWidth={width} />;
          }
          if (type === 'diamond') {
            const centerX = p3.x - ux * (size / 2);
            const centerY = p3.y - uy * (size / 2);
            const px2 = -uy * (size/2);
            const py2 = ux * (size/2);
            return (
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  // front point towards the endpoint
                  context.moveTo(centerX + ux * (size/2), centerY + uy * (size/2));
                  // side 1
                  context.lineTo(centerX + px2, centerY + py2);
                  // back
                  context.lineTo(centerX - ux * (size/2), centerY - uy * (size/2));
                  // side 2
                  context.lineTo(centerX - px2, centerY - py2);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          if (type === 'bar') {
            const cx = p3.x - ux * (size / 2);
            const cy = p3.y - uy * (size / 2);
            const nxn = -uy * (size/2);
            const nyn = ux * (size/2);
            return (
              <Line points={[cx + nxn, cy + nyn, cx - nxn, cy - nyn]} stroke={color} strokeWidth={width} listening={false} />
            );
          }
          if (type === 'outlineBlock') {
            return (
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(p3.x, p3.y);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          if (type === 'triangle-outline') {
            // Outline triangle arrow (not filled)
            return (
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(p3.x, p3.y);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          if (type === 'chevron') {
            // Open arrow (V-shape) - only 2 sides, no closing line
            // Use larger dimensions like neglectAbuse for better visibility
            const chevronLength = 12;
            const chevronWidth = 16;
            const chevronOffset = 2;
            const tipX = p3.x - ux * chevronOffset;
            const tipY = p3.y - uy * chevronOffset;
            const chevronBaseX = tipX - ux * chevronLength;
            const chevronBaseY = tipY - uy * chevronLength;
            const cpx = -uy * (chevronWidth / 2);
            const cpy = ux * (chevronWidth / 2);
            const chevronLeftX = chevronBaseX + cpx;
            const chevronLeftY = chevronBaseY + cpy;
            const chevronRightX = chevronBaseX - cpx;
            const chevronRightY = chevronBaseY - cpy;
            return (
              <Line
                points={[chevronLeftX, chevronLeftY, tipX, tipY, chevronRightX, chevronRightY]}
                stroke={color}
                strokeWidth={width}
                lineCap="round"
                lineJoin="round"
                listening={false}
              />
            );
          }
          // triangle default (filled)
          return (
            <Shape
              sceneFunc={(context, shape) => {
                context.beginPath();
                context.moveTo(p3.x, p3.y);
                context.lineTo(leftX, leftY);
                context.lineTo(rightX, rightY);
                context.closePath();
                context.fillStrokeShape(shape);
              }}
              fill={color}
              stroke={color}
              strokeWidth={width}
              listening={false}
            />
          );
        })()}
        {(() => {
          const type = (startArrow?.type || 'none') as string;
          if (type === 'none') return null;
          // tangent at t=0 => c1 - p0
          const dirX0 = c1.x - p0.x;
          const dirY0 = c1.y - p0.y;
          const len0 = Math.hypot(dirX0, dirY0) || 1;
          const ux0 = dirX0 / len0;
          const uy0 = dirY0 / len0;
          const pointerLength0 = 12;
          const pointerWidth0 = 8;
          const baseX0 = p0.x + ux0 * pointerLength0;
          const baseY0 = p0.y + uy0 * pointerLength0;
          const px0 = -uy0 * (pointerWidth0 / 2);
          const py0 = ux0 * (pointerWidth0 / 2);
          const size = (startArrow?.size as number) || 6;
          if (type === 'circle') {
            const cx = p0.x + ux0 * (size / 2);
            const cy = p0.y + uy0 * (size / 2);
            return <Circle x={cx} y={cy} radius={size/2} fill={color} />;
          }
          if (type === 'circleOutline') {
            const cx = p0.x + ux0 * (size / 2);
            const cy = p0.y + uy0 * (size / 2);
            return <Circle x={cx} y={cy} radius={size/2} fill="white" stroke={color} strokeWidth={width} />;
          }
          if (type === 'diamond') {
            const centerX = p0.x + ux0 * (size / 2);
            const centerY = p0.y + uy0 * (size / 2);
            const px2 = -uy0 * (size/2);
            const py2 = ux0 * (size/2);
            return (
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(centerX - ux0 * (size/2), centerY - uy0 * (size/2));
                  context.lineTo(centerX + px2, centerY + py2);
                  context.lineTo(centerX + ux0 * (size/2), centerY + uy0 * (size/2));
                  context.lineTo(centerX - px2, centerY - py2);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          if (type === 'bar') {
            const cx = p0.x + ux0 * (size / 2);
            const cy = p0.y + uy0 * (size / 2);
            const pxn = -uy0 * (size / 2);
            const pyn = ux0 * (size / 2);
            return <Line points={[cx + pxn, cy + pyn, cx - pxn, cy - pyn]} stroke={color} strokeWidth={width} listening={false} />;
          }
          if (type === 'backslash') {
            const cx = p0.x + ux0 * (size / 2);
            const cy = p0.y + uy0 * (size / 2);
            const half = size / 2;
            const dx = half * (ux0 - uy0);
            const dy = half * (ux0 + uy0);
            return <Line points={[cx - dx, cy - dy, cx + dx, cy + dy]} stroke={color} strokeWidth={width} lineCap="round" listening={false} />;
          }
          // triangle
          return (
            <Shape
              sceneFunc={(context, shape) => {
                context.beginPath();
                context.moveTo(p0.x, p0.y);
                context.lineTo(baseX0 + px0, baseY0 + py0);
                context.lineTo(baseX0 - px0, baseY0 - py0);
                context.closePath();
                context.fillStrokeShape(shape);
              }}
              fill={color}
              stroke={color}
              strokeWidth={width}
              listening={false}
            />
          );
        })()}
        {midTicks && (() => {
          const len = 12;
          const half = len / 2;
          const tx = midTangent.dx;
          const ty = midTangent.dy;
          const tLen = Math.hypot(tx, ty) || 1;
          const ux = tx / tLen;
          const uy = ty / tLen;
          const nux = -uy;
          const nuy = ux;
          const tickCenters = [-2, 2].map((d) => ({ x: midPoint.x + ux * d, y: midPoint.y + uy * d }));
          return tickCenters.map((c, idx) => (
            <Line
              key={`tick-${idx}`}
              points={[c.x - nux * half, c.y - nuy * half, c.x + nux * half, c.y + nuy * half]}
              stroke={color}
              strokeWidth={width}
              listening={false}
            />
          ));
        })()}
        {midCircle && (
          <Circle
            x={midPoint.x}
            y={midPoint.y}
            radius={6}
            stroke={color}
            strokeWidth={width}
            fill="white"
            listening={false}
          />
        )}
        {distrust && (() => {
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const numTicks = Math.max(5, Math.min(35, Math.floor(lineLength / 12)));
          const tickLen = 14;
          const half = tickLen / 2;
          const tickColor = '#CA3A31';
          const ticks = [];
          for (let i = 0; i < numTicks; i++) {
            const t = (i + 1) / (numTicks + 1);
            const pt = sampleBezier(t);
            const tan = sampleTangent(t);
            const tLen = Math.hypot(tan.dx, tan.dy) || 1;
            const nux = -tan.dy / tLen;
            const nuy = tan.dx / tLen;
            ticks.push(
              <Line
                key={`distrust-tick-${i}`}
                points={[
                  pt.x - nux * half, pt.y - nuy * half,
                  pt.x + nux * half, pt.y + nuy * half
                ]}
                stroke={tickColor}
                strokeWidth={4}
                listening={false}
              />
            );
          }
          return ticks;
        })()}
        {bestFriends && doubleLine && (() => {
          // Vertical strokes extending beyond the two parallel lines (ladder pattern)
          // doubleLine uses offsets [-3.5, 3.5], strokes extend beyond by 4px on each side
          const topExtend = 7.5; // Goes 4px beyond top line (-3.5 - 4)
          const bottomExtend = 7.5; // Goes 4px beyond bottom line (3.5 + 4)
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const numTicks = Math.max(5, Math.min(35, Math.floor(lineLength / 12)));
          const ticks = [];
          for (let i = 0; i < numTicks; i++) {
            const t = (i + 1) / (numTicks + 1);
            const point = sampleBezier(t);
            const tangent = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tangent.dx, tangent.dy);
            // Position tick perpendicular to curve
            ticks.push(
              <Line
                key={`bf-tick-${i}`}
                points={[
                  point.x - nx * topExtend, point.y - ny * topExtend,
                  point.x + nx * bottomExtend, point.y + ny * bottomExtend
                ]}
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          return <>{ticks}</>;
        })()}
        {emotionalConnection && (() => {
          // Emotional Connection / Spiritual Relationship:
          // chain of touching circles (no overlap), no base line.
          const circleRadius = 5;
          const spacing = circleRadius * 2;
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const numCircles = Math.max(2, Math.floor(lineLength / spacing));
          const circles = [];
          for (let i = 0; i < numCircles; i++) {
            const t = (i + 0.5) / numCircles;
            const point = sampleBezier(t);
            circles.push(
              <Circle
                key={`ec-${i}`}
                x={point.x}
                y={point.y}
                radius={circleRadius}
                stroke={color}
                strokeWidth={width}
                fill="transparent"
                listening={false}
              />
            );
          }
          return <>{circles}</>;
        })()}

        {inLoveHarmony && (() => {
          // In Love (Harmony): two overlapping circles in the middle (green).
          // Base line is drawn; circles mask it with white fill.
          const circleRadius = 6;
          const circleOffset = 4;
          const tLen = Math.hypot(midTangent.dx, midTangent.dy) || 1;
          const tux = midTangent.dx / tLen;
          const tuy = midTangent.dy / tLen;
          const circle1X = midPoint.x - tux * circleOffset;
          const circle1Y = midPoint.y - tuy * circleOffset;
          const circle2X = midPoint.x + tux * circleOffset;
          const circle2Y = midPoint.y + tuy * circleOffset;
          return (
            <>
              <Circle x={circle1X} y={circle1Y} radius={circleRadius} fill="white" listening={false} />
              <Circle x={circle2X} y={circle2Y} radius={circleRadius} fill="white" listening={false} />
              <Circle x={circle1X} y={circle1Y} radius={circleRadius} stroke={color} strokeWidth={width} listening={false} />
              <Circle x={circle2X} y={circle2Y} radius={circleRadius} stroke={color} strokeWidth={width} listening={false} />
            </>
          );
        })()}
        {fusedHostile && (() => {
          // 3 black lines + red hostile-style jagged zigzag (amplitude > 4 so zigzag extends outside lines). Renders fused-hostile and fused-violence.
          const tripleOffsets = [-4, 0, 4];
          const redColor = '#DC0101';
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 5;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const endTan = sampleTangent(1);
          const endLen = Math.hypot(endTan.dx, endTan.dy) || 1;
          const endUx = endTan.dx / endLen;
          const endUy = endTan.dy / endLen;
          const endExtend = 2;
          const elements = [];
          // 3 black lines (same sampling + end extension as close-violence)
          tripleOffsets.forEach((off, idx) => {
            const pts: number[] = [];
            for (let i = 0; i <= 50; i++) {
              const t = i / 50;
              const point = sampleBezier(t);
              const tangent = sampleTangent(t);
              const { nx, ny } = normalFromTangent(tangent.dx, tangent.dy);
              pts.push(point.x + nx * off, point.y + ny * off);
            }
            pts.push(pts[pts.length - 2] + endUx * endExtend, pts[pts.length - 1] + endUy * endExtend);
            elements.push(
              <Line key={`fh-${idx}`} points={pts} stroke="#000000" strokeWidth={width} listening={false} />
            );
          });
          // Red hostile-style jagged zigzag (matches renderWave)
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > 1 - fadeZone) amp = amplitude * ((1 - t) / fadeZone);
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          elements.push(
            <Line key="fh-zigzag" points={zigzagPts} stroke={redColor} strokeWidth={width} listening={false} />
          );
          return <>{elements}</>;
        })()}
        {distanceHostile && (() => {
          // Dashed black line + red hostile-style jagged zigzag (thinner zigzag)
          const redColor = '#DC0101';
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 3;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const zigzagStrokeWidth = Math.max(1, width - 1);
          const elements = [];
          // Dashed black line
          const pts: number[] = [];
          for (let t = 0; t <= 1; t += 0.02) {
            const point = sampleBezier(t);
            pts.push(point.x, point.y);
          }
          elements.push(
            <Line key="dh-line" points={pts} stroke="#000000" strokeWidth={width} dash={[6, 6]} listening={false} />
          );
          // Red hostile-style jagged zigzag (matches renderWave)
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > 1 - fadeZone) amp = amplitude * ((1 - t) / fadeZone);
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          elements.push(
            <Line key="dh-zigzag" points={zigzagPts} stroke={redColor} strokeWidth={zigzagStrokeWidth} listening={false} />
          );
          return <>{elements}</>;
        })()}
        {distantViolence && (() => {
          // Distant - Violence: 1 dashed black line + red hostile-style jagged zigzag
          const redColor = '#DC0101';
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 3;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const zigzagStrokeWidth = Math.max(1, width - 1);
          const elements = [];
          // Dashed black line
          const pts: number[] = [];
          for (let t = 0; t <= 1; t += 0.02) {
            const point = sampleBezier(t);
            pts.push(point.x, point.y);
          }
          elements.push(
            <Line key="dv-line" points={pts} stroke="#000000" strokeWidth={width} dash={[6, 6]} listening={false} />
          );
          // Red hostile-style jagged zigzag (matches renderWave)
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > 1 - fadeZone) amp = amplitude * ((1 - t) / fadeZone);
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          elements.push(
            <Line key="dv-zigzag" points={zigzagPts} stroke={redColor} strokeWidth={zigzagStrokeWidth} listening={false} />
          );
          return <>{elements}</>;
        })()}
        {closeHostile && (() => {
          // 2 black lines + red hostile-style jagged zigzag (amplitude > 3.5 so zigzag extends outside lines)
          const doubleOffsets = [-3.5, 3.5];
          const redColor = '#DC0101';
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 5;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const endTan = sampleTangent(1);
          const endLen = Math.hypot(endTan.dx, endTan.dy) || 1;
          const endUx = endTan.dx / endLen;
          const endUy = endTan.dy / endLen;
          const endExtend = 2;
          const elements = [];
          // 2 black lines (same sampling + end extension as close-violence)
          doubleOffsets.forEach((off, idx) => {
            const pts: number[] = [];
            for (let i = 0; i <= 50; i++) {
              const t = i / 50;
              const point = sampleBezier(t);
              const tangent = sampleTangent(t);
              const { nx, ny } = normalFromTangent(tangent.dx, tangent.dy);
              pts.push(point.x + nx * off, point.y + ny * off);
            }
            pts.push(pts[pts.length - 2] + endUx * endExtend, pts[pts.length - 1] + endUy * endExtend);
            elements.push(
              <Line key={`ch-${idx}`} points={pts} stroke="#000000" strokeWidth={width} listening={false} />
            );
          });
          // Red hostile-style jagged zigzag (matches renderWave)
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > 1 - fadeZone) amp = amplitude * ((1 - t) / fadeZone);
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          elements.push(
            <Line key="ch-zigzag" points={zigzagPts} stroke={redColor} strokeWidth={width} listening={false} />
          );
          return <>{elements}</>;
        })()}
        {closeViolence && (() => {
          // Close - Violence: 2 solid black lines + red hostile-style jagged zigzag (amplitude > 3.5 so zigzag extends outside lines)
          const doubleOffsets = [-3.5, 3.5];
          const redColor = '#DC0101';
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 5;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const endTan = sampleTangent(1);
          const endLen = Math.hypot(endTan.dx, endTan.dy) || 1;
          const endUx = endTan.dx / endLen;
          const endUy = endTan.dy / endLen;
          const endExtend = 2;
          const elements = [];
          // 2 black lines (extend at end so they touch the target shape)
          doubleOffsets.forEach((off, idx) => {
            const pts: number[] = [];
            for (let i = 0; i <= 50; i++) {
              const t = i / 50;
              const point = sampleBezier(t);
              const tangent = sampleTangent(t);
              const { nx, ny } = normalFromTangent(tangent.dx, tangent.dy);
              pts.push(point.x + nx * off, point.y + ny * off);
            }
            pts.push(pts[pts.length - 2] + endUx * endExtend, pts[pts.length - 1] + endUy * endExtend);
            elements.push(
              <Line key={`cv-${idx}`} points={pts} stroke="#000000" strokeWidth={width} listening={false} />
            );
          });
          // Red hostile-style jagged zigzag (matches renderWave)
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > 1 - fadeZone) amp = amplitude * ((1 - t) / fadeZone);
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          elements.push(
            <Line key="cv-zigzag" points={zigzagPts} stroke={redColor} strokeWidth={width} listening={false} />
          );
          return <>{elements}</>;
        })()}
        {manipulative && (() => {
          // Red line with X in middle and chevron arrow at end
          const redColor = '#CA3A31';
          // X in center
          const xSize = 6;
          const tLen = Math.hypot(midTangent.dx, midTangent.dy) || 1;
          const tux = midTangent.dx / tLen;
          const tuy = midTangent.dy / tLen;
          const nux = -tuy;
          const nuy = tux;
          // X corners
          const x1 = midPoint.x + (tux + nux) * xSize;
          const y1 = midPoint.y + (tuy + nuy) * xSize;
          const x2 = midPoint.x - (tux + nux) * xSize;
          const y2 = midPoint.y - (tuy + nuy) * xSize;
          const x3 = midPoint.x + (tux - nux) * xSize;
          const y3 = midPoint.y + (tuy - nuy) * xSize;
          const x4 = midPoint.x - (tux - nux) * xSize;
          const y4 = midPoint.y - (tuy - nuy) * xSize;
          return (
            <>
              {/* Red bezier line */}
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(p0.x, p0.y);
                  context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p3.x, p3.y);
                  context.fillStrokeShape(shape);
                }}
                stroke={redColor}
                strokeWidth={width}
                listening={false}
              />
              {/* X in center */}
              <Line
                key="manipulative-x1"
                points={[x1, y1, x2, y2]}
                stroke={redColor}
                strokeWidth={width}
                listening={false}
              />
              <Line
                key="manipulative-x2"
                points={[x3, y3, x4, y4]}
                stroke={redColor}
                strokeWidth={width}
                listening={false}
              />
            </>
          );
        })()}
        {controlling && (() => {
          // Red line with square+X in middle and double chevron arrow at end
          const redColor = '#CA3A31';
          // Square with X in center
          const squareSize = 7;
          const tLen = Math.hypot(midTangent.dx, midTangent.dy) || 1;
          const tux = midTangent.dx / tLen;
          const tuy = midTangent.dy / tLen;
          const nux = -tuy;
          const nuy = tux;
          // Square corners
          const corner1X = midPoint.x + (tux + nux) * squareSize;
          const corner1Y = midPoint.y + (tuy + nuy) * squareSize;
          const corner2X = midPoint.x + (tux - nux) * squareSize;
          const corner2Y = midPoint.y + (tuy - nuy) * squareSize;
          const corner3X = midPoint.x - (tux + nux) * squareSize;
          const corner3Y = midPoint.y - (tuy + nuy) * squareSize;
          const corner4X = midPoint.x - (tux - nux) * squareSize;
          const corner4Y = midPoint.y - (tuy - nuy) * squareSize;
          return (
            <>
              {/* Red bezier line */}
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(p0.x, p0.y);
                  context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p3.x, p3.y);
                  context.fillStrokeShape(shape);
                }}
                stroke={redColor}
                strokeWidth={width}
                listening={false}
              />
              {/* Square outline */}
              <Line
                key="controlling-square"
                points={[corner1X, corner1Y, corner2X, corner2Y, corner3X, corner3Y, corner4X, corner4Y]}
                stroke={redColor}
                strokeWidth={width}
                fill="white"
                closed={true}
                listening={false}
              />
              {/* X inside square */}
              <Line
                key="controlling-x1"
                points={[corner1X, corner1Y, corner3X, corner3Y]}
                stroke={redColor}
                strokeWidth={width}
                listening={false}
              />
              <Line
                key="controlling-x2"
                points={[corner2X, corner2Y, corner4X, corner4Y]}
                stroke={redColor}
                strokeWidth={width}
                listening={false}
              />
            </>
          );
        })()}
        {jealous && (() => {
          // Red line with diamond in middle and chevron arrow at end
          const redColor = '#CA3A31';
          // Diamond in center
          const diamondSize = 8;
          const tLen = Math.hypot(midTangent.dx, midTangent.dy) || 1;
          const tux = midTangent.dx / tLen;
          const tuy = midTangent.dy / tLen;
          const nux = -tuy;
          const nuy = tux;
          // Diamond corners
          const topX = midPoint.x + nux * diamondSize;
          const topY = midPoint.y + nuy * diamondSize;
          const rightX = midPoint.x + tux * diamondSize;
          const rightY = midPoint.y + tuy * diamondSize;
          const bottomX = midPoint.x - nux * diamondSize;
          const bottomY = midPoint.y - nuy * diamondSize;
          const leftX = midPoint.x - tux * diamondSize;
          const leftY = midPoint.y - tuy * diamondSize;
          return (
            <>
              {/* Red bezier line */}
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(p0.x, p0.y);
                  context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p3.x, p3.y);
                  context.fillStrokeShape(shape);
                }}
                stroke={redColor}
                strokeWidth={width}
                listening={false}
              />
              {/* Diamond outline */}
              <Line
                key="jealous-diamond"
                points={[topX, topY, rightX, rightY, bottomX, bottomY, leftX, leftY]}
                stroke={redColor}
                strokeWidth={width}
                fill="white"
                closed={true}
                listening={false}
              />
            </>
          );
        })()}
        {fanAdmirer && (() => {
          // Gray line with circle in middle and chevron arrow at end
          const grayColor = '#4D5562';
          const circleRadius = 6;
          return (
            <>
              {/* Gray bezier line */}
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(p0.x, p0.y);
                  context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p3.x, p3.y);
                  context.fillStrokeShape(shape);
                }}
                stroke={grayColor}
                strokeWidth={width}
                listening={false}
              />
              {/* Circle in center */}
              <Circle
                x={midPoint.x}
                y={midPoint.y}
                radius={circleRadius}
                stroke={grayColor}
                strokeWidth={width}
                fill="white"
                listening={false}
              />
            </>
          );
        })()}
        {limerence && (() => {
          // Gray line with 2 overlapping circles in middle and chevron arrow at end
          const grayColor = '#4D5562';
          const circleRadius = 6;
          const circleOffset = 4;
          // Calculate tangent unit vector at midpoint
          const tLen = Math.hypot(midTangent.dx, midTangent.dy) || 1;
          const tux = midTangent.dx / tLen;
          const tuy = midTangent.dy / tLen;
          // Position circles along the tangent
          const circle1X = midPoint.x - tux * circleOffset;
          const circle1Y = midPoint.y - tuy * circleOffset;
          const circle2X = midPoint.x + tux * circleOffset;
          const circle2Y = midPoint.y + tuy * circleOffset;
          return (
            <>
              {/* Gray bezier line */}
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(p0.x, p0.y);
                  context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p3.x, p3.y);
                  context.fillStrokeShape(shape);
                }}
                stroke={grayColor}
                strokeWidth={width}
                listening={false}
              />
              {/* Circle 1 fill (white background) */}
              <Circle
                x={circle1X}
                y={circle1Y}
                radius={circleRadius}
                fill="white"
                listening={false}
              />
              {/* Circle 2 fill (white background) */}
              <Circle
                x={circle2X}
                y={circle2Y}
                radius={circleRadius}
                fill="white"
                listening={false}
              />
              {/* Circle 1 stroke (full outline visible) */}
              <Circle
                x={circle1X}
                y={circle1Y}
                radius={circleRadius}
                stroke={grayColor}
                strokeWidth={width}
                listening={false}
              />
              {/* Circle 2 stroke (full outline visible) */}
              <Circle
                x={circle2X}
                y={circle2Y}
                radius={circleRadius}
                stroke={grayColor}
                strokeWidth={width}
                listening={false}
              />
            </>
          );
        })()}
        {focusedNegatively && (() => {
          // Black straight line with black zigzag through it (arrow handled separately)
          const waveAmp = 6;
          // Calculate line length for proper wave frequency
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const waveCount = Math.max(3, Math.floor(lineLength / 20));
          // Arrow takes up some space at the end
          const pointerLength = 12;
          const arrowOffset = 2;
          const arrowSpace = (pointerLength + arrowOffset) / lineLength;
          const fadeEnd = Math.max(0.7, 1 - arrowSpace);
          const fadeStart = fadeEnd - 0.1;
          // Render black zigzag line through the main line, fade out smoothly before arrow
          const zigzagPts: number[] = [];
          for (let t = 0; t <= fadeEnd; t += 0.01) {
            const point = sampleBezier(t);
            const tangent = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tangent.dx, tangent.dy);
            // Fade out amplitude near the arrow
            let amp = waveAmp;
            if (t > fadeStart) {
              amp = waveAmp * (1 - (t - fadeStart) / (fadeEnd - fadeStart));
            }
            const waveOffset = Math.sin(t * waveCount * Math.PI * 2) * amp;
            zigzagPts.push(
              point.x + nx * waveOffset,
              point.y + ny * waveOffset
            );
          }
          return (
            <Line
              key="fn-zigzag"
              points={zigzagPts}
              stroke={color}
              strokeWidth={width}
              listening={false}
            />
          );
        })()}
        {abusePattern && (() => {
          // Dashed black base line (abuse uses invisible hit Shape; default line skipped)
          const dashPts: number[] = [];
          for (let t = 0; t <= 1; t += 0.02) {
            const pt = sampleBezier(t);
            dashPts.push(pt.x, pt.y);
          }
          return (
            <Line key="abuse-dashed" points={dashPts} stroke="#000000" strokeWidth={width} dash={[6, 6]} listening={false} />
          );
        })()}
        {abusePattern && (() => {
          // Blue hostile-style jagged zigzag through the dashed black line, fade out before arrow (thinner stroke)
          const blueColor = '#2563eb';
          const abuseStrokeWidth = Math.max(1, width - 1);
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 3;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const pointerLength = 12;
          const arrowOffset = 2;
          const arrowSpace = (pointerLength + arrowOffset) / lineLength;
          const fadeEnd = Math.max(0.7, 1 - arrowSpace);
          const fadeStart = fadeEnd - 0.1;
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = (i / steps) * fadeEnd;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > fadeStart) amp = amplitude * (1 - (t - fadeStart) / (fadeEnd - fadeStart));
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          return (
            <Line
              key="abuse-zigzag"
              points={zigzagPts}
              stroke={blueColor}
              strokeWidth={abuseStrokeWidth}
              listening={false}
            />
          );
        })()}
        {abusePattern && (() => {
          // Blue triangle outline arrow at end - positioned before the shape (thinner stroke)
          const blueColor = '#2563eb';
          const abuseStrokeWidth = Math.max(1, width - 1);
          const pointerLength = 12;
          const pointerWidth = 16;
          const arrowOffset = 2;
          const tipX = p3.x - ux * arrowOffset;
          const tipY = p3.y - uy * arrowOffset;
          const baseX = tipX - ux * pointerLength;
          const baseY = tipY - uy * pointerLength;
          const px = -uy * (pointerWidth / 2);
          const py = ux * (pointerWidth / 2);
          const leftX = baseX + px;
          const leftY = baseY + py;
          const rightX = baseX - px;
          const rightY = baseY - py;
          return (
            <>
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={blueColor}
                strokeWidth={abuseStrokeWidth}
                listening={false}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="rgba(0,0,0,0.01)"
                stroke="transparent"
                onClick={onClick}
                onTap={onClick}
                listening={true}
              />
            </>
          );
        })()}
        {physicalAbuse && (() => {
          // Blue hostile-style jagged zigzag line, fade out before arrow
          const blueColor = '#2563eb';
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 3;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const pointerLength = 12;
          const arrowOffset = 2;
          const arrowSpace = (pointerLength + arrowOffset) / lineLength;
          const fadeEnd = Math.max(0.7, 1 - arrowSpace);
          const fadeStart = fadeEnd - 0.1;
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = (i / steps) * fadeEnd;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > fadeStart) amp = amplitude * (1 - (t - fadeStart) / (fadeEnd - fadeStart));
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          return (
            <Line
              key="physical-abuse-zigzag"
              points={zigzagPts}
              stroke={blueColor}
              strokeWidth={width}
              listening={false}
            />
          );
        })()}
        {physicalAbuse && (() => {
          // Blue filled triangle arrow at end
          const blueColor = '#2563eb';
          const pointerLength = 12;
          const pointerWidth = 16;
          const arrowOffset = 2;
          const tipX = p3.x - ux * arrowOffset;
          const tipY = p3.y - uy * arrowOffset;
          const baseX = tipX - ux * pointerLength;
          const baseY = tipY - uy * pointerLength;
          const px = -uy * (pointerWidth / 2);
          const py = ux * (pointerWidth / 2);
          const leftX = baseX + px;
          const leftY = baseY + py;
          const rightX = baseX - px;
          const rightY = baseY - py;
          return (
            <>
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill={blueColor}
                stroke={blueColor}
                strokeWidth={width}
                listening={false}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="rgba(0,0,0,0.01)"
                stroke="transparent"
                onClick={onClick}
                onTap={onClick}
                listening={true}
              />
            </>
          );
        })()}
        {emotionalAbuse && (() => {
          // Blue hostile-style jagged zigzag line, fade out before arrow
          const blueColor = '#2563eb';
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 3;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const pointerLength = 12;
          const arrowOffset = 2;
          const arrowSpace = (pointerLength + arrowOffset) / lineLength;
          const fadeEnd = Math.max(0.7, 1 - arrowSpace);
          const fadeStart = fadeEnd - 0.1;
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = (i / steps) * fadeEnd;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > fadeStart) amp = amplitude * (1 - (t - fadeStart) / (fadeEnd - fadeStart));
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          return (
            <Line
              key="emotional-abuse-zigzag"
              points={zigzagPts}
              stroke={blueColor}
              strokeWidth={width}
              listening={false}
            />
          );
        })()}
        {emotionalAbuse && (() => {
          // Blue triangle outline arrow at end
          const blueColor = '#2563eb';
          const pointerLength = 12;
          const pointerWidth = 16;
          const arrowOffset = 2;
          const tipX = p3.x - ux * arrowOffset;
          const tipY = p3.y - uy * arrowOffset;
          const baseX = tipX - ux * pointerLength;
          const baseY = tipY - uy * pointerLength;
          const px = -uy * (pointerWidth / 2);
          const py = ux * (pointerWidth / 2);
          const leftX = baseX + px;
          const leftY = baseY + py;
          const rightX = baseX - px;
          const rightY = baseY - py;
          return (
            <>
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={blueColor}
                strokeWidth={width}
                listening={false}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="rgba(0,0,0,0.01)"
                stroke="transparent"
                onClick={onClick}
                onTap={onClick}
                listening={true}
              />
            </>
          );
        })()}
        {sexualAbuse && (() => {
          // 2 blue lines with blue hostile-style jagged zigzag through them
          const blueColor = '#2563eb';
          const lineOffset = 3.5;
          const lineLength = Math.sqrt(Math.pow(p3.x - p0.x, 2) + Math.pow(p3.y - p0.y, 2));
          const steps = Math.max(20, Math.floor(lineLength / 5));
          const amplitude = 5;
          const fadePixels = 15;
          const fadeZone = Math.min(0.15, fadePixels / lineLength);
          const pointerLength = 12;
          const arrowOffset = 2;
          const arrowSpace = (pointerLength + arrowOffset) / lineLength;
          const lineEnd = Math.max(0.7, 1 - arrowSpace * 0.5);
          const fadeEnd = Math.max(0.7, 1 - arrowSpace);
          const fadeStart = fadeEnd - 0.1;
          const line1Pts: number[] = [];
          const line2Pts: number[] = [];
          for (let t = 0; t <= lineEnd; t += 0.01) {
            const point = sampleBezier(t);
            const tangent = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tangent.dx, tangent.dy);
            line1Pts.push(point.x + nx * lineOffset, point.y + ny * lineOffset);
            line2Pts.push(point.x - nx * lineOffset, point.y - ny * lineOffset);
          }
          const zigzagPts: number[] = [];
          for (let i = 0; i <= steps; i++) {
            const t = (i / steps) * fadeEnd;
            const base = sampleBezier(t);
            const tan = sampleTangent(t);
            const { nx, ny } = normalFromTangent(tan.dx, tan.dy);
            let amp = amplitude;
            if (t < fadeZone) amp = amplitude * (t / fadeZone);
            else if (t > fadeStart) amp = amplitude * (1 - (t - fadeStart) / (fadeEnd - fadeStart));
            const offset = (i % 2 === 0 ? 1 : -1) * amp;
            zigzagPts.push(base.x + nx * offset, base.y + ny * offset);
          }
          return (
            <>
              <Line key="sexual-abuse-line1" points={line1Pts} stroke={blueColor} strokeWidth={width} listening={false} />
              <Line key="sexual-abuse-line2" points={line2Pts} stroke={blueColor} strokeWidth={width} listening={false} />
              <Line key="sexual-abuse-zigzag" points={zigzagPts} stroke={blueColor} strokeWidth={width} listening={false} />
            </>
          );
        })()}
        {sexualAbuse && (() => {
          // Blue filled triangle arrow at end
          const blueColor = '#2563eb';
          const pointerLength = 12;
          const pointerWidth = 16;
          const arrowOffset = 2;
          const tipX = p3.x - ux * arrowOffset;
          const tipY = p3.y - uy * arrowOffset;
          const baseX = tipX - ux * pointerLength;
          const baseY = tipY - uy * pointerLength;
          const px = -uy * (pointerWidth / 2);
          const py = ux * (pointerWidth / 2);
          const leftX = baseX + px;
          const leftY = baseY + py;
          const rightX = baseX - px;
          const rightY = baseY - py;
          return (
            <>
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill={blueColor}
                stroke={blueColor}
                strokeWidth={width}
                listening={false}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath();
                  context.moveTo(tipX, tipY);
                  context.lineTo(leftX, leftY);
                  context.lineTo(rightX, rightY);
                  context.closePath();
                  context.fillStrokeShape(shape);
                }}
                fill="rgba(0,0,0,0.01)"
                stroke="transparent"
                onClick={onClick}
                onTap={onClick}
                listening={true}
              />
            </>
          );
        })()}
        {neglectAbuse && (() => {
          // Dashed base line (neglect uses invisible hit Shape; default line skipped)
          const dashPts: number[] = [];
          for (let t = 0; t <= 1; t += 0.02) {
            const pt = sampleBezier(t);
            dashPts.push(pt.x, pt.y);
          }
          return (
            <Line key="neglect-dashed" points={dashPts} stroke={color} strokeWidth={width} dash={[6, 6]} listening={false} />
          );
        })()}
        {neglectAbuse && (() => {
          // Single open arrow (chevron) at the end
          const blueColor = '#2563eb';
          const pointerLength = 12;
          const pointerWidth = 16;
          const arrowOffset = 2;
          const tipX = p3.x - ux * arrowOffset;
          const tipY = p3.y - uy * arrowOffset;
          const baseX = tipX - ux * pointerLength;
          const baseY = tipY - uy * pointerLength;
          const px = -uy * (pointerWidth / 2);
          const py = ux * (pointerWidth / 2);
          const leftX = baseX + px;
          const leftY = baseY + py;
          const rightX = baseX - px;
          const rightY = baseY - py;
          const chevronPts = [leftX, leftY, tipX, tipY, rightX, rightY];
          return (
            <>
              <Line
                key="neglect-chevron"
                points={chevronPts}
                stroke={blueColor}
                strokeWidth={width}
                lineCap="round"
                lineJoin="round"
                listening={false}
              />
              <Line
                key="neglect-chevron-hit"
                points={chevronPts}
                stroke="rgba(0,0,0,0.01)"
                strokeWidth={1}
                hitStrokeWidth={20}
                lineCap="round"
                lineJoin="round"
                onClick={onClick}
                onTap={onClick}
                listening={true}
              />
            </>
          );
        })()}

        {cutoffRepaired && (() => {
          // Circle and vertical strokes - positioned along tangent direction
          const tickOffset = 6;
          const tickHalf = 6;
          // Calculate tangent and normal unit vectors at midpoint
          const tLen = Math.hypot(midTangent.dx, midTangent.dy) || 1;
          const tux = midTangent.dx / tLen;
          const tuy = midTangent.dy / tLen;
          const nux = -tuy;
          const nuy = tux;
          // Position ticks along tangent
          const tick1X = midPoint.x - tux * tickOffset;
          const tick1Y = midPoint.y - tuy * tickOffset;
          const tick2X = midPoint.x + tux * tickOffset;
          const tick2Y = midPoint.y + tuy * tickOffset;
          return (
            <>
              <Circle
                x={midPoint.x}
                y={midPoint.y}
                radius={4}
                stroke={color}
                strokeWidth={width}
                fill="white"
                listening={false}
              />
              <Line
                points={[tick1X - nux * tickHalf, tick1Y - nuy * tickHalf, tick1X + nux * tickHalf, tick1Y + nuy * tickHalf]}
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
              <Line
                points={[tick2X - nux * tickHalf, tick2Y - nuy * tickHalf, tick2X + nux * tickHalf, tick2Y + nuy * tickHalf]}
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            </>
          );
        })()}
        {neverMet && (() => {
          // Square with X - rotated along tangent direction
          const squareSize = 8;
          // Calculate tangent and normal unit vectors at midpoint
          const tLen = Math.hypot(midTangent.dx, midTangent.dy) || 1;
          const tux = midTangent.dx / tLen;
          const tuy = midTangent.dy / tLen;
          const nux = -tuy;
          const nuy = tux;
          // Square corners using tangent and normal
          const corner1X = midPoint.x + (tux + nux) * squareSize;
          const corner1Y = midPoint.y + (tuy + nuy) * squareSize;
          const corner2X = midPoint.x + (tux - nux) * squareSize;
          const corner2Y = midPoint.y + (tuy - nuy) * squareSize;
          const corner3X = midPoint.x - (tux + nux) * squareSize;
          const corner3Y = midPoint.y - (tuy + nuy) * squareSize;
          const corner4X = midPoint.x - (tux - nux) * squareSize;
          const corner4Y = midPoint.y - (tuy - nuy) * squareSize;
          return (
            <>
              {/* Square outline - rotated along curve */}
              <Line
                key="never-met-square"
                points={[corner1X, corner1Y, corner2X, corner2Y, corner3X, corner3Y, corner4X, corner4Y]}
                stroke={color}
                strokeWidth={width}
                fill="white"
                closed={true}
                listening={false}
              />
              {/* X inside square - rotated along curve */}
              <Line
                key="never-met-x1"
                points={[corner1X, corner1Y, corner3X, corner3Y]}
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
              <Line
                key="never-met-x2"
                points={[corner2X, corner2Y, corner4X, corner4Y]}
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            </>
          );
        })()}
        {(() => {
          if (!label) return null;
          const t = Math.max(0, Math.min(1, labelOffset));
          const x = Math.pow(1 - t, 3) * p0.x + 3 * Math.pow(1 - t, 2) * t * c1.x + 3 * (1 - t) * Math.pow(t, 2) * c2.x + Math.pow(t, 3) * p3.x;
          const y = Math.pow(1 - t, 3) * p0.y + 3 * Math.pow(1 - t, 2) * t * c1.y + 3 * (1 - t) * Math.pow(t, 2) * c2.y + Math.pow(t, 3) * p3.y;
          const measureLabel = (text: string, fontPx: number, family: string, lineH: number) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return { width: 0, height: 0 };
            ctx.font = `${fontPx}px ${family}`;
            const lines = String(text).split('\n');
            let maxW = 0;
            for (const ln of lines) {
              const w = ctx.measureText(ln || ' ').width;
              if (w > maxW) maxW = w;
            }
            const h = Math.max(1, lines.length) * fontPx * lineH;
            return { width: Math.ceil(maxW), height: Math.ceil(h) };
          };
          const fontPx = 14;
          const family = 'system-ui, -apple-system, sans-serif';
          const lineH = 1.2;
          const metrics = measureLabel(label, fontPx, family, lineH);
          const padX = 8;
          const padY = 4;
          const labelYOffset = (controlling || jealous)
            ? 22
            : (
              doubleLine
              || tripleLine
              || emotionalConnection
              || fusedHostile
              || distanceHostile
              || closeHostile
              || distantViolence
              || closeViolence
              || midTicks
              || midCircle
              || cutoffRepaired
              || neverMet
              || inLoveHarmony
              || distrust
              || waveLine
              || focusedNegatively
              || abusePattern
              || physicalAbuse
              || emotionalAbuse
              || sexualAbuse
              || neglectAbuse
              || manipulative
              || fanAdmirer
              || limerence
            )
              ? 18
              : 12;
          return (
            <>
              {/* Mask: punch a transparent hole under the label for readability */}
              <Rect
                x={x}
                y={y - labelYOffset + orgLabelVerticalNudge}
                width={metrics.width + padX}
                height={metrics.height + padY}
                offsetX={(metrics.width + padX) / 2}
                offsetY={(metrics.height + padY) / 2}
                fill={edgeBackgroundColor}
                listening={false}
                perfectDrawEnabled={false}
                cornerRadius={2}
              />
              <KonvaText
                ref={(n) => { setLabelTextRef((n as unknown as Konva.Text) || null); }}
                x={x}
                y={y - labelYOffset + orgLabelVerticalNudge}
                text={label}
                fontSize={fontPx}
                fontFamily={family}
                fill={connectorLabelFill}
                align="center"
                wrap="none"
                listening={true}
                perfectDrawEnabled={false}
                width={metrics.width}
                offsetX={metrics.width / 2}
                offsetY={metrics.height / 2}
                visible={!editingText}
                onDblClick={() => onStartTextEdit?.(edge.id, labelTextRef.current)}
                onDblTap={() => onStartTextEdit?.(edge.id, labelTextRef.current)}
              />
            </>
          );
        })()}

        {/* Label drag handle when selected and label present */}
        {selected && label && (
          <Circle
            x={(() => { const t = Math.max(0, Math.min(1, labelOffset)); return Math.pow(1 - t, 3) * p0.x + 3 * Math.pow(1 - t, 2) * t * c1.x + 3 * (1 - t) * Math.pow(t, 2) * c2.x + Math.pow(t, 3) * p3.x; })()}
            y={(() => { const t = Math.max(0, Math.min(1, labelOffset)); return Math.pow(1 - t, 3) * p0.y + 3 * Math.pow(1 - t, 2) * t * c1.y + 3 * (1 - t) * Math.pow(t, 2) * c2.y + Math.pow(t, 3) * p3.y; })()}
            radius={5}
            fill="#ffffff"
            stroke="#10b981"
            strokeWidth={2}
            draggable
            onDragMove={(e) => {
              const pos = e.target.position();
              // Find closest t on cubic Bezier via sampling (fast approx)
              let bestT = 0.5;
              let bestD = Number.POSITIVE_INFINITY;
              for (let i = 0; i <= 50; i++) {
                const t = i / 50;
                const x = Math.pow(1 - t, 3) * p0.x + 3 * Math.pow(1 - t, 2) * t * c1.x + 3 * (1 - t) * Math.pow(t, 2) * c2.x + Math.pow(t, 3) * p3.x;
                const y = Math.pow(1 - t, 3) * p0.y + 3 * Math.pow(1 - t, 2) * t * c1.y + 3 * (1 - t) * Math.pow(t, 2) * c2.y + Math.pow(t, 3) * p3.y;
                const d = (x - pos.x) * (x - pos.x) + (y - pos.y) * (y - pos.y);
                if (d < bestD) { bestD = d; bestT = t; }
              }
              const x = Math.pow(1 - bestT, 3) * p0.x + 3 * Math.pow(1 - bestT, 2) * bestT * c1.x + 3 * (1 - bestT) * Math.pow(bestT, 2) * c2.x + Math.pow(bestT, 3) * p3.x;
              const y = Math.pow(1 - bestT, 3) * p0.y + 3 * Math.pow(1 - bestT, 2) * bestT * c1.y + 3 * (1 - bestT) * Math.pow(bestT, 2) * c2.y + Math.pow(bestT, 3) * p3.y;
              e.target.position({ x, y });
              const { updateEdge } = useDiagramStore.getState();
              updateEdge(edge.id, { attrs: { ...(edge.attrs || {}), labelOffset: bestT } });
            }}
            onDragEnd={(e) => {
              const pos = e.target.position();
              let bestT = 0.5;
              let bestD = Number.POSITIVE_INFINITY;
              for (let i = 0; i <= 50; i++) {
                const t = i / 50;
                const x = Math.pow(1 - t, 3) * p0.x + 3 * Math.pow(1 - t, 2) * t * c1.x + 3 * (1 - t) * Math.pow(t, 2) * c2.x + Math.pow(t, 3) * p3.x;
                const y = Math.pow(1 - t, 3) * p0.y + 3 * Math.pow(1 - t, 2) * t * c1.y + 3 * (1 - t) * Math.pow(t, 2) * c2.y + Math.pow(t, 3) * p3.y;
                const d = (x - pos.x) * (x - pos.x) + (y - pos.y) * (y - pos.y);
                if (d < bestD) { bestD = d; bestT = t; }
              }
              const x = Math.pow(1 - bestT, 3) * p0.x + 3 * Math.pow(1 - bestT, 2) * bestT * c1.x + 3 * (1 - bestT) * Math.pow(bestT, 2) * c2.x + Math.pow(bestT, 3) * p3.x;
              const y = Math.pow(1 - bestT, 3) * p0.y + 3 * Math.pow(1 - bestT, 2) * bestT * c1.y + 3 * (1 - bestT) * Math.pow(bestT, 2) * c2.y + Math.pow(bestT, 3) * p3.y;
              e.target.position({ x, y });
              const { updateEdge } = useDiagramStore.getState();
              updateEdge(edge.id, { attrs: { ...(edge.attrs || {}), labelOffset: bestT } });
            }}
          />
        )}

        {/* Draggable endpoint handles when selected */}
        {selected && (
          <>
            <Circle
              x={p0.x}
              y={p0.y}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onDragStart={() => { try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: toNode?.id } })); } catch {} }}
	              onDragMove={(e) => {
	                const pos = e.target.position();
	                const snap = findSnapTarget(pos, toNode?.id, toDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  setDragPreview(prev => ({ ...(prev || {}), fromPoint: snap.snapped }));
	                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId: toNode?.id } })); } catch {}
	                } else {
                  e.target.position({ x: pos.x, y: pos.y });
                  setDragPreview(prev => ({ ...(prev || {}), fromPoint: { x: pos.x, y: pos.y } }));
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: pos.x, y: pos.y, sourceId: toNode?.id } })); } catch {}
                }
              }}
	              onDragEnd={(e) => {
	                const pos = e.target.position();
	                const snap = findSnapTarget(pos, toNode?.id, toDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  executeEdgeCommand({
                      from: snap.id,
                      fromAnchor: snap.side as any,
                      fromOffset: snap.offset as any,
                      attrs: isLineShape ? ({ fromPoint: undefined } as any) : ({ ...(edge.attrs || {}), fromPoint: undefined } as any),
                    } as any);
	                } else if (orgTreePreviewOnlyDrag) {
                  // Tree ON: never detach an org edge to free space. Snap back to original endpoint.
                  e.target.position({ x: p0.x, y: p0.y });
	                } else {
                  const freeId = `__freeF:${edge.id}`;
                  e.target.position({ x: pos.x, y: pos.y });
                  executeEdgeCommand({
                    from: freeId,
                    fromAnchor: undefined as any,
                    fromOffset: undefined as any,
                    attrs: isLineShape
                      ? ({ fromPoint: { x: pos.x, y: pos.y } } as any)
                      : ({ ...(edge.attrs || {}), fromPoint: { x: pos.x, y: pos.y } } as any),
                  } as any);
	                }
                setDragPreview(prev => ({ ...(prev || {}), fromPoint: undefined }));
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
            <Circle
              x={p3.x}
              y={p3.y}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onDragStart={() => {
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: fromNode?.id } })); } catch {}
              }}
              onDragMove={(e) => {
                const pos = e.target.position();
                const barSnap = findMarriageBarSnap(pos);
                if (barSnap) {
                  e.target.position(barSnap.snapped);
                  setDragPreview({ toPoint: barSnap.snapped });
                  setHoverMarriageBar(barSnap.bar);
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: barSnap.snapped.x, y: barSnap.snapped.y, sourceId: fromNode?.id } })); } catch {}
                  return;
                }
	                setHoverMarriageBar(null);
	                // Try to snap to any available shape
	                const snap = findSnapTarget(pos, fromNode?.id, fromDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  setDragPreview({ toPoint: snap.snapped });
	                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId: fromNode?.id } })); } catch {}
	                } else {
                  // Free preview
                  e.target.position({ x: pos.x, y: pos.y });
                  setDragPreview({ toPoint: { x: pos.x, y: pos.y } });
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: pos.x, y: pos.y, sourceId: fromNode?.id } })); } catch {}
                }
              }}
              onDragEnd={(e) => {
                const pos = e.target.position();
                const barSnap = findMarriageBarSnap(pos);
                if (!isLineShape && barSnap && fromNode) {
                  // Convert to child connector from nearest parent to the child node (fromNode)
                  e.target.position(barSnap.snapped);
                  const p1 = nodes.find(n => n.id === barSnap.bar.parent1Id);
                  const p2 = nodes.find(n => n.id === barSnap.bar.parent2Id);
                  const m1 = p1 ? (getShapeState ? (getShapeState(p1.id, p1) as Node) : p1) : null;
                  const m2 = p2 ? (getShapeState ? (getShapeState(p2.id, p2) as Node) : p2) : null;
                  let chosenParentId = barSnap.bar.parent1Id;
                  if (m1 && m2) {
                    const childResolved = getShapeState ? (getShapeState(fromNode.id, fromNode) as Node) : fromNode;
                    const childCenterX = getNodeCenterX(childResolved);
                    const d1 = Math.abs(getNodeCenterX(m1) - childCenterX);
                    const d2 = Math.abs(getNodeCenterX(m2) - childCenterX);
                    chosenParentId = d1 <= d2 ? barSnap.bar.parent1Id : barSnap.bar.parent2Id;
                  }
                  executeEdgeCommand({
                    type: 'child' as any,
                    from: chosenParentId,
                    to: fromNode.id,
                    fromAnchor: undefined as any,
                    toAnchor: undefined as any,
                    fromOffset: undefined as any,
                    toOffset: undefined as any,
                    attrs: { ...(edge.attrs || {}), toPoint: undefined, fromPoint: undefined, routingType: undefined } as any,
                  } as any);
	                } else {
	                  const snap = findSnapTarget(pos, fromNode?.id, fromDockTableId);
	                  if (snap) {
	                    // Attach to shape
	                    e.target.position(snap.snapped);
	                    executeEdgeCommand({
                        to: snap.id,
                        toAnchor: snap.side as any,
                        toOffset: snap.offset as any,
                        ...(isLineShape
                          ? { attrs: { toPoint: undefined } as any }
                          : { toAnchorLocked: true as any, attrs: { ...(edge.attrs || {}), toPoint: undefined } as any }),
                      } as any);
                  } else if (orgTreePreviewOnlyDrag) {
                    // Tree ON: never detach an org edge to free space. Snap back to original endpoint.
                    e.target.position({ x: p3.x, y: p3.y });
                  } else {
                    // Detach to free point
                    const freeId = isLineShape ? `__freeT:${edge.id}` : `__free:${edge.id}`;
                    e.target.position({ x: pos.x, y: pos.y });
                    executeEdgeCommand({
                      to: freeId,
                      toAnchor: undefined as any,
                      toOffset: undefined as any,
                      attrs: isLineShape
                        ? ({ toPoint: { x: pos.x, y: pos.y } } as any)
                        : ({ ...(edge.attrs || {}), toPoint: { x: pos.x, y: pos.y } } as any),
                    } as any);
                  }
                }
                setHoverMarriageBar(null);
                setDragPreview(null);
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
          </>
        )}

        <ConnectorOverlay
          edgeId={edge.id}
          midpoint={midpoint}
          canShowInsert={!!showInsertButton}
          onHoverChange={setIsOverlayHovered}
        />
      </Group>
    );
  }

  // Straight line routing
  if ((renderData as any).type === 'straightLine') {
    const pts = (renderData as any).points as number[];
    const color = getStrokeColor();
    const width = getLineWidth();
    const endArrow = getEndArrow();
    const startArrow = (edge.attrs as any)?.startArrow as { type?: string; size?: number } | undefined;
    const showCaptions = usePerspectiveStore.getState().showRelationshipCaptions;
    const label = resolveGenogramConnectorDisplayLabel({
      diagramType,
      edgeType: edge.type as string,
      attrs: (((edge.attrs as any) || {}) as Record<string, unknown>),
      showCaptions: isSecondaryReportsToEdge ? false : showCaptions,
    });
    const labelOffset = typeof (edge.attrs as any)?.labelOffset === 'number' ? (edge.attrs as any).labelOffset : 0.5;
    const x1 = pts[0], y1 = pts[1], x2 = pts[2], y2 = pts[3];
    // Safety check: if coordinates are invalid, don't render
    if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
      return null;
    }
    const ux = (x2 - x1), uy = (y2 - y1); const len = Math.hypot(ux, uy) || 1; const nx = ux/len, ny = uy/len;
    const baseX = x2 - nx * 12; const baseY = y2 - ny * 12; const px = -ny * 4; const py = nx * 4;

    return (
      <Group
        ref={(n) => registerRef?.(edge.id, n)}
        draggable={false}
        onMouseDown={handleFreeEndpointBodyMouseDown}
        onClick={isFreeEndpointBodyDraggable ? undefined : onClick}
        onTap={isFreeEndpointBodyDraggable ? undefined : onClick}
      >
        {selected && (
          <Line points={pts} stroke={getThemeColor('#3478DD', { type: 'accent' })} strokeWidth={width + 4} opacity={0.25} listening={false} />
        )}
        <Line
          points={pts}
          stroke={color}
          strokeWidth={width}
          dash={getLineDash()}
          onClick={(e) => {
            if (!isFreeEndpointBodyDraggable) {
              onClick(e);
            }
            // For draggable lines, onClick is handled by Group's onMouseUp
          }}
          onTap={(tapEvt) => {
            if (!isFreeEndpointBodyDraggable) {
              onClick(tapEvt);
            }
            // For draggable lines, onTap is handled by Group's onMouseUp
          }}
          onMouseDown={() => {
            // For draggable lines, let the event bubble to Group
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          hitStrokeWidth={10}
          listening={true}
        />
        {(() => {
          const type = (endArrow?.type || 'triangle');
          if (type === 'none') return null;
          const size = (endArrow?.size as number) || 8;
          if (type === 'circle') { const cx = x2 - nx*(size/2); const cy = y2 - ny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill={color} />; }
          if (type === 'circleOutline') { const cx = x2 - nx*(size/2); const cy = y2 - ny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill="white" stroke={color} strokeWidth={width} />; }
          if (type === 'diamond') {
            const cx = x2 - nx * (size / 2); const cy = y2 - ny * (size / 2);
            return (
              <Shape sceneFunc={(ctx, shape) => { ctx.beginPath(); ctx.moveTo(cx + nx*(size/2), cy + ny*(size/2)); ctx.lineTo(cx + px, cy + py); ctx.lineTo(cx - nx*(size/2), cy - ny*(size/2)); ctx.lineTo(cx - px, cy - py); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill="white" stroke={color} strokeWidth={width} listening={false} />
            );
          }
          if (type === 'bar') { const cx = x2 - nx*(size/2); const cy = y2 - ny*(size/2); const pxn = -ny*(size/2); const pyn = nx*(size/2); return <Line points={[cx+pxn, cy+pyn, cx-pxn, cy-pyn]} stroke={color} strokeWidth={width} listening={false} />; }
          if (type === 'outlineBlock') {
            return (
              <Shape sceneFunc={(ctx, shape) => { ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(baseX+px, baseY+py); ctx.lineTo(baseX-px, baseY-py); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill="white" stroke={color} strokeWidth={width} listening={false} />
            );
          }
          if (type === 'triangle-outline') {
            return (
              <Shape sceneFunc={(ctx, shape) => { ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(baseX+px, baseY+py); ctx.lineTo(baseX-px, baseY-py); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill="white" stroke={color} strokeWidth={width} listening={false} />
            );
          }
          if (type === 'chevron') {
            return (
              <Line points={[baseX+px, baseY+py, x2, y2, baseX-px, baseY-py]} stroke={color} strokeWidth={width} lineCap="round" lineJoin="round" listening={false} />
            );
          }
          return (
            <Shape sceneFunc={(ctx, shape) => { ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(baseX+px, baseY+py); ctx.lineTo(baseX-px, baseY-py); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill={color} stroke={color} strokeWidth={width} listening={false} />
          );
        })()}
        {(() => {
          const type = (startArrow?.type || 'none');
          if (type === 'none') return null;
          const sbaseX = x1 + nx * 12; const sbaseY = y1 + ny * 12; const spx = -ny * 4; const spy = nx * 4;
          const size = (startArrow?.size as number) || 6;
          if (type === 'circle') { const cx = x1 + nx*(size/2); const cy = y1 + ny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill={color} />; }
          if (type === 'circleOutline') { const cx = x1 + nx*(size/2); const cy = y1 + ny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill="white" stroke={color} strokeWidth={width} />; }
          if (type === 'diamond') {
            const cx = x1 + nx * (size / 2); const cy = y1 + ny * (size / 2); return (
              <Shape sceneFunc={(ctx, shape) => { ctx.beginPath(); ctx.moveTo(cx - nx*(size/2), cy - ny*(size/2)); ctx.lineTo(cx + spx, cy + spy); ctx.lineTo(cx + nx*(size/2), cy + ny*(size/2)); ctx.lineTo(cx - spx, cy - spy); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill="white" stroke={color} strokeWidth={width} listening={false} />
            ); }
          if (type === 'bar') { const cx = x1 + nx*(size/2); const cy = y1 + ny*(size/2); const pxn = -ny*(size/2); const pyn = nx*(size/2); return <Line points={[cx+pxn, cy+pyn, cx-pxn, cy-pyn]} stroke={color} strokeWidth={width} listening={false} />; }
          if (type === 'backslash') {
            const cx = x1 + nx * (size / 2); const cy = y1 + ny * (size / 2);
            const half = size / 2; const dx = half * (nx - ny); const dy = half * (ny + nx);
            return <Line points={[cx - dx, cy - dy, cx + dx, cy + dy]} stroke={color} strokeWidth={width} lineCap="round" listening={false} />;
          }
          return (
            <Shape sceneFunc={(ctx, shape) => { ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(sbaseX+spx, sbaseY+spy); ctx.lineTo(sbaseX-spx, sbaseY-spy); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill={color} stroke={color} strokeWidth={width} listening={false} />
          );
        })()}
        {(() => {
          if (!label) return null; const t = Math.max(0, Math.min(1, labelOffset));
          const x = x1 + (x2 - x1) * t; const y = y1 + (y2 - y1) * t;
          const fontPx = 14;
          const family = 'system-ui, -apple-system, sans-serif';
          const lineH = 1.2;
          const measure = (text: string) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return { width: 0, height: 0 };
            ctx.font = `${fontPx}px ${family}`;
            const lines = String(text).split('\n');
            let maxW = 0;
            for (const ln of lines) {
              const w = ctx.measureText(ln || ' ').width;
              if (w > maxW) maxW = w;
            }
            const h = Math.max(1, lines.length) * fontPx * lineH;
            return { width: Math.ceil(maxW), height: Math.ceil(h) };
          };
          const m = measure(label);
          const padX = 8, padY = 4;
          return (
            <>
              {/* Mask: punch a transparent hole under the label for readability */}
              <Rect
                x={x}
                y={y + orgLabelVerticalNudge}
                width={m.width + padX}
                height={m.height + padY}
                offsetX={(m.width + padX) / 2}
                offsetY={(m.height + padY) / 2}
                fill={edgeBackgroundColor}
                listening={false}
                perfectDrawEnabled={false}
                cornerRadius={2}
              />
              <KonvaText
                ref={(n) => { setLabelTextRef((n as unknown as Konva.Text) || null); }}
                x={x}
                y={y + orgLabelVerticalNudge}
                text={label}
                fontSize={fontPx}
                fontFamily={family}
                fill={connectorLabelFill}
                align="center"
                wrap="none"
                listening={!!canWrite}
                visible={!editingText}
                perfectDrawEnabled={false}
                width={m.width}
                offsetX={m.width / 2}
                offsetY={m.height / 2}
                onClick={onClick}
                onTap={onClick}
                onDblClick={() => {
                  if (!canWrite) return;
                  const textNode = labelTextRef.current;
                  if (!textNode) return;
                  onStartTextEdit?.(edge.id, textNode);
                }}
                onDblTap={() => {
                  if (!canWrite) return;
                  const textNode = labelTextRef.current;
                  if (!textNode) return;
                  onStartTextEdit?.(edge.id, textNode);
                }}
              />
            </>
          );
        })()}

        {/* Endpoint drag handles */}
        {selected && (
          <>
            <Circle
              x={x1}
              y={y1}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onMouseDown={(e) => {
                // Prevent Group's onMouseDown from firing
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                  e.evt.preventDefault();
                }
                // Clear any line drag state
                lineDragStartRef.current = null;
                lineClickStartRef.current = null;
              }}
              onDragStart={(e) => {
                // Stop propagation to prevent Group drag
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                }
                // Mark endpoint drag as in progress
                endpointDragInProgressRef.current = true;
                // Clear any line drag state
                lineDragStartRef.current = null;
                lineClickStartRef.current = null;
                // Store initial position for shift constraint
                const initialPos = e.target.position();
                endpointConstraintDragStartRef.current = { x: initialPos.x, y: initialPos.y };
                // When dragging the start handle, the fixed point is the end of the line
                endpointConstraintFixedPointRef.current = { x: x2, y: y2 };
                endpointConstraintDirectionRef.current = null;
                // Emit drag start event for connection snap overlay
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: toNode?.id || null } })); } catch {}
              }}
              onDragMove={(e) => {
                // Stop propagation to prevent Group drag
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                }
                let handlePos = e.target.position();
                const isShiftPressed = e.evt?.shiftKey || false;

                // Apply shift constraint for free endpoints
                if (!fromNode && isShiftPressed && endpointConstraintDragStartRef.current && endpointConstraintFixedPointRef.current) {
                  // Determine constraint direction if not already set
                  if (!endpointConstraintDirectionRef.current) {
                    const deltaX = Math.abs(handlePos.x - endpointConstraintDragStartRef.current.x);
                    const deltaY = Math.abs(handlePos.y - endpointConstraintDragStartRef.current.y);
                    const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                    if (totalMovement >= CONSTRAINT_THRESHOLD) {
                      endpointConstraintDirectionRef.current = deltaX > deltaY ? 'horizontal' : 'vertical';
                    }
                  }

                  // Apply constraint based on direction
                  if (endpointConstraintDirectionRef.current === 'horizontal') {
                    // Horizontal line => lock Y to the opposite (fixed) endpoint
                    handlePos = { x: handlePos.x, y: endpointConstraintFixedPointRef.current.y };
                  } else if (endpointConstraintDirectionRef.current === 'vertical') {
                    // Vertical line => lock X to the opposite (fixed) endpoint
                    handlePos = { x: endpointConstraintFixedPointRef.current.x, y: handlePos.y };
                  }
                } else if (!isShiftPressed) {
                  // Clear constraint direction when shift is released
                  endpointConstraintDirectionRef.current = null;
                }

                if (fromNode) {
                  const { snapped } = computeAnchorForPoint(fromResolved, handlePos);
                  e.target.position(snapped);
                  // Endpoint dragging is a preview only. Persisting the anchor here
                  // would overwrite UpdateEdgeCommand's before-state and break undo.
                  setDragPreview({ fromPoint: snapped });
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snapped.x, y: snapped.y, sourceId: fromNode?.id } })); } catch {}
	                } else {
	                  // Free endpoint - check for snap targets
	                  const snap = findSnapTarget(handlePos, toNode?.id, toDockTableId);
	                  const sourceId = toNode?.id || null;
	                  if (snap) {
	                    e.target.position(snap.snapped);
	                    setDragPreview({ fromPoint: snap.snapped });
                    try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId } })); } catch {}
                  } else {
                    e.target.position({ x: handlePos.x, y: handlePos.y });
                    setDragPreview({ fromPoint: { x: handlePos.x, y: handlePos.y } });
                    try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: handlePos.x, y: handlePos.y, sourceId } })); } catch {}
                  }
                  // Clear Group drag state to prevent interference
                  lineDragStartRef.current = null;
                  lineClickStartRef.current = null;
                }
              }}
              onDragEnd={(e) => {
                // Stop propagation to prevent Group drag
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                }
                let handlePos = e.target.position();
                const isShiftPressed = e.evt?.shiftKey || false;

                // Apply shift constraint for free endpoints on drag end
                if (!fromNode && isShiftPressed && endpointConstraintFixedPointRef.current && endpointConstraintDirectionRef.current) {
                  if (endpointConstraintDirectionRef.current === 'horizontal') {
                    handlePos = { x: handlePos.x, y: endpointConstraintFixedPointRef.current.y };
                  } else if (endpointConstraintDirectionRef.current === 'vertical') {
                    handlePos = { x: endpointConstraintFixedPointRef.current.x, y: handlePos.y };
                  }
                }

                // Clear constraint state
                endpointConstraintDirectionRef.current = null;
                endpointConstraintDragStartRef.current = null;
                endpointConstraintFixedPointRef.current = null;
                if (fromNode) {
                  const { side, offset, snapped } = computeAnchorForPoint(fromResolved, handlePos);
                  e.target.position(snapped);
                  executeEdgeCommand({ fromAnchor: side as any, fromOffset: offset, fromAnchorLocked: true } as any);
	                } else {
	                  // Free endpoint - check for snap targets
	                  const snap = findSnapTarget(handlePos, toNode?.id, toDockTableId);
	                  const currentToPoint = (edge.attrs as any)?.toPoint;
	                  if (snap) {
	                    // Snap to the shape
	                    e.target.position(snap.snapped);
                    executeEdgeCommand({
                        from: snap.id,
                        fromAnchor: snap.side as any,
                        fromOffset: snap.offset as any,
                        ...(isLineShape
                          ? { attrs: { fromPoint: undefined, toPoint: currentToPoint } as any }
                          : {
                              fromAnchorLocked: true as any,
                              attrs: { ...(edge.attrs || {}), fromPoint: undefined, toPoint: currentToPoint } as any,
                            }),
                      } as any);
                  } else {
                    // Stay as free endpoint
                    const freeId = `__freeF:${edge.id}`;
                    e.target.position({ x: handlePos.x, y: handlePos.y });
                    executeEdgeCommand({
                        from: freeId,
                        fromAnchor: undefined as any,
                        fromOffset: undefined as any,
                        ...(isLineShape
                          ? {
                              attrs: { fromPoint: { x: handlePos.x, y: handlePos.y }, toPoint: currentToPoint } as any,
                            }
                          : {
                              attrs: { ...(edge.attrs || {}), fromPoint: { x: handlePos.x, y: handlePos.y }, toPoint: currentToPoint } as any,
                            }),
                      } as any);
                  }
                }
                // Clear dragPreview completely
                setDragPreview(null);
                // Ensure line drag state is cleared
                lineDragStartRef.current = null;
                lineClickStartRef.current = null;
                // Mark endpoint drag as finished
                endpointDragInProgressRef.current = false;
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
            <Circle
              x={x2}
              y={y2}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onMouseDown={(e) => {
                // Prevent Group's onMouseDown from firing
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                  e.evt.preventDefault();
                }
                // Clear any line drag state
                lineDragStartRef.current = null;
                lineClickStartRef.current = null;
              }}
              onDragStart={(e) => {
                // Stop propagation to prevent Group drag
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                }
                // Mark endpoint drag as in progress
                endpointDragInProgressRef.current = true;
                // Clear any line drag state
                lineDragStartRef.current = null;
                lineClickStartRef.current = null;
                // Store initial position for shift constraint
                const initialPos = e.target.position();
                endpointConstraintDragStartRef.current = { x: initialPos.x, y: initialPos.y };
                // When dragging the end handle, the fixed point is the start of the line
                endpointConstraintFixedPointRef.current = { x: x1, y: y1 };
                endpointConstraintDirectionRef.current = null;
                // Emit drag start event for connection snap overlay
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: fromNode?.id || null } })); } catch {}
              }}
              onDragMove={(e) => {
                // Stop propagation to prevent Group drag
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                }
                let endHandlePos = e.target.position();
                const isShiftPressed = e.evt?.shiftKey || false;

                // Apply shift constraint for free endpoints
                if (!toNode && isShiftPressed && endpointConstraintDragStartRef.current && endpointConstraintFixedPointRef.current) {
                  // Determine constraint direction if not already set
                  if (!endpointConstraintDirectionRef.current) {
                    const deltaX = Math.abs(endHandlePos.x - endpointConstraintDragStartRef.current.x);
                    const deltaY = Math.abs(endHandlePos.y - endpointConstraintDragStartRef.current.y);
                    const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                    if (totalMovement >= CONSTRAINT_THRESHOLD) {
                      endpointConstraintDirectionRef.current = deltaX > deltaY ? 'horizontal' : 'vertical';
                    }
                  }

                  // Apply constraint based on direction
                  if (endpointConstraintDirectionRef.current === 'horizontal') {
                    endHandlePos = { x: endHandlePos.x, y: endpointConstraintFixedPointRef.current.y };
                  } else if (endpointConstraintDirectionRef.current === 'vertical') {
                    endHandlePos = { x: endpointConstraintFixedPointRef.current.x, y: endHandlePos.y };
                  }
                } else if (!isShiftPressed) {
                  // Clear constraint direction when shift is released
                  endpointConstraintDirectionRef.current = null;
                }

                const barSnap = findMarriageBarSnap(endHandlePos);
                if (barSnap) {
                  e.target.position(barSnap.snapped);
                  setDragPreview({ toPoint: barSnap.snapped });
                  setHoverMarriageBar(barSnap.bar);
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: barSnap.snapped.x, y: barSnap.snapped.y, sourceId: fromNode?.id } })); } catch {}
                  return;
                }
                setHoverMarriageBar(null);
                if (toNode) {
                  const { snapped } = computeAnchorForPoint(toResolved, endHandlePos);
                  e.target.position(snapped);
                  // Keep the live endpoint in component preview state so the
                  // command created on drop can still capture the original edge.
                  setDragPreview({ toPoint: snapped });
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snapped.x, y: snapped.y, sourceId: toNode.id } })); } catch {}
	                } else {
	                  // Free endpoint - check for snap targets
	                  const snap = findSnapTarget(endHandlePos, fromNode?.id, fromDockTableId);
	                  const sourceId = fromNode?.id || null;
	                  if (snap) {
	                    e.target.position(snap.snapped);
	                    setDragPreview({ toPoint: snap.snapped });
                    try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId } })); } catch {}
                  } else {
                    e.target.position({ x: endHandlePos.x, y: endHandlePos.y });
                    setDragPreview({ toPoint: { x: endHandlePos.x, y: endHandlePos.y } });
                    try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: endHandlePos.x, y: endHandlePos.y, sourceId } })); } catch {}
                  }
                  // Clear Group drag state to prevent interference
                  lineDragStartRef.current = null;
                  lineClickStartRef.current = null;
                }
              }}
              onDragEnd={(e) => {
                // Stop propagation to prevent Group drag
                e.cancelBubble = true;
                if (e.evt) {
                  e.evt.stopPropagation();
                }
                let endHandlePos = e.target.position();
                const isShiftPressed = e.evt?.shiftKey || false;

                // Apply shift constraint for free endpoints on drag end
                if (!toNode && isShiftPressed && endpointConstraintFixedPointRef.current && endpointConstraintDirectionRef.current) {
                  if (endpointConstraintDirectionRef.current === 'horizontal') {
                    endHandlePos = { x: endHandlePos.x, y: endpointConstraintFixedPointRef.current.y };
                  } else if (endpointConstraintDirectionRef.current === 'vertical') {
                    endHandlePos = { x: endpointConstraintFixedPointRef.current.x, y: endHandlePos.y };
                  }
                }

                // Clear constraint state
                endpointConstraintDirectionRef.current = null;
                endpointConstraintDragStartRef.current = null;
                endpointConstraintFixedPointRef.current = null;

                const barSnap = findMarriageBarSnap(endHandlePos);
                if (barSnap && fromNode) {
                  e.target.position(barSnap.snapped);
                  const p1 = nodes.find(n => n.id === barSnap.bar.parent1Id);
                  const p2 = nodes.find(n => n.id === barSnap.bar.parent2Id);
                  const m1 = p1 ? (getShapeState ? (getShapeState(p1.id, p1) as Node) : p1) : null;
                  const m2 = p2 ? (getShapeState ? (getShapeState(p2.id, p2) as Node) : p2) : null;
                  let chosenParentId = barSnap.bar.parent1Id;
                  if (m1 && m2) {
                    const childResolved = getShapeState ? (getShapeState(fromNode.id, fromNode) as Node) : fromNode;
                    const childCenterX = getNodeCenterX(childResolved);
                    const d1 = Math.abs(getNodeCenterX(m1) - childCenterX);
                    const d2 = Math.abs(getNodeCenterX(m2) - childCenterX);
                    chosenParentId = d1 <= d2 ? barSnap.bar.parent1Id : barSnap.bar.parent2Id;
                  }
                  executeEdgeCommand({
                    type: 'child' as any,
                    from: chosenParentId,
                    to: fromNode.id,
                    fromAnchor: undefined as any,
                    toAnchor: undefined as any,
                    fromOffset: undefined as any,
                    toOffset: undefined as any,
                    attrs: { ...(edge.attrs || {}), toPoint: undefined, fromPoint: undefined, routingType: undefined } as any,
                  } as any);
                } else if (toNode) {
                  const { side, offset, snapped } = computeAnchorForPoint(toResolved, endHandlePos);
                  e.target.position(snapped);
                  executeEdgeCommand({ toAnchor: side as any, toOffset: offset, toAnchorLocked: true } as any);
	                } else {
	                  // Free endpoint - update toPoint in attrs (keep fromPoint unchanged)
	                  const snap = findSnapTarget(endHandlePos, fromNode?.id, fromDockTableId);
	                  if (snap) {
	                    e.target.position(snap.snapped);
	                    const currentFromPoint = (edge.attrs as any)?.fromPoint;
                      executeEdgeCommand({
                        to: snap.id,
                        toAnchor: snap.side as any,
                        toOffset: snap.offset as any,
                        attrs: isLineShape
                          ? ({ toPoint: undefined, fromPoint: currentFromPoint } as any)
                          : ({ ...(edge.attrs || {}), toPoint: undefined, fromPoint: currentFromPoint } as any),
                      } as any);
                  } else {
                    const freeId = `__freeT:${edge.id}`;
                    e.target.position({ x: endHandlePos.x, y: endHandlePos.y });
                    const currentFromPoint = (edge.attrs as any)?.fromPoint;
                    executeEdgeCommand({
                      to: freeId,
                      toAnchor: undefined as any,
                      toOffset: undefined as any,
                      attrs: isLineShape
                        ? ({ toPoint: { x: endHandlePos.x, y: endHandlePos.y }, fromPoint: currentFromPoint } as any)
                        : ({ ...(edge.attrs || {}), toPoint: { x: endHandlePos.x, y: endHandlePos.y }, fromPoint: currentFromPoint } as any),
                    } as any);
                  }
                }
                setHoverMarriageBar(null);
                // Clear dragPreview completely
                setDragPreview(null);
                // Ensure line drag state is cleared
                lineDragStartRef.current = null;
                lineClickStartRef.current = null;
                // Mark endpoint drag as finished
                endpointDragInProgressRef.current = false;
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
          </>
        )}

        <ConnectorOverlay
          edgeId={edge.id}
          midpoint={midpoint}
          canShowInsert={!!showInsertButton}
          onHoverChange={setIsOverlayHovered}
        />
      </Group>
    );
  }

  // Orthogonal polyline routing
  if ((renderData as any).type === 'orthogonalPolyline' || (renderData as any).type === 'orthogonalRoundedPolyline') {
    const rounded = (renderData as any).type === 'orthogonalRoundedPolyline';
    const pts = (renderData as any).points as number[];
    const color = getStrokeColor();
    const width = getLineWidth();
    const endArrow = getEndArrow();
    const startArrow = (edge.attrs as any)?.startArrow as { type?: string; size?: number } | undefined;
    const rawLabel = (edge.attrs as any)?.label as string | undefined;
    const showCaptions = usePerspectiveStore.getState().showRelationshipCaptions;
    const suppressed = (edge.attrs as any)?.__captionHidden === true;
    const label = suppressed ? undefined : (rawLabel ?? (showCaptions ? undefined : undefined));
    const labelOffset = typeof (edge.attrs as any)?.labelOffset === 'number' ? (edge.attrs as any).labelOffset : 0.5;
    const pointAt = (points: number[], t: number) => {
      const segments: Array<{ x1: number; y1: number; x2: number; y2: number; len: number }> = [];
      let total = 0;
      for (let i = 0; i < points.length - 2; i += 2) {
        const x1 = points[i], y1 = points[i + 1];
        const x2 = points[i + 2], y2 = points[i + 3];
        const len = Math.hypot(x2 - x1, y2 - y1);
        segments.push({ x1, y1, x2, y2, len });
        total += len;
      }
      if (!total) return { x: points[0], y: points[1], nx: 0, ny: -1 };
      let dist = Math.max(0, Math.min(1, t)) * total;
      for (const seg of segments) {
        if (seg.len === 0) continue;
        if (dist <= seg.len) {
          const ratio = dist / seg.len;
          const x = seg.x1 + (seg.x2 - seg.x1) * ratio;
          const y = seg.y1 + (seg.y2 - seg.y1) * ratio;
          const dx = seg.x2 - seg.x1;
          const dy = seg.y2 - seg.y1;
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          return { x, y, nx, ny };
        }
        dist -= seg.len;
      }
      const last = segments[segments.length - 1];
      const dx = last.x2 - last.x1;
      const dy = last.y2 - last.y1;
      const len = Math.hypot(dx, dy) || 1;
      return { x: last.x2, y: last.y2, nx: -dy / len, ny: dx / len };
    };
    // last segment for arrow
    const n = pts.length; const x1 = pts[n-4], y1 = pts[n-3], x2 = pts[n-2], y2 = pts[n-1];
    const ux = (x2 - x1), uy = (y2 - y1); const len = Math.hypot(ux, uy) || 1; const nx = ux/len, ny = uy/len;
    const baseX = x2 - nx * 12; const baseY = y2 - ny * 12; const px = -ny * 4; const py = nx * 4;
    const roundedCornerRadius = 10;

    const buildRoundedOrthogonalPath = (points: number[], rBase: number): string => {
      if (!points || points.length < 4) return '';
      let d = `M ${points[0]} ${points[1]}`;
      for (let i = 2; i < points.length; i += 2) {
        const x = points[i] as number;
        const y = points[i + 1] as number;
        if (!rounded || i >= points.length - 2 || i < 2) {
          d += ` L ${x} ${y}`;
          continue;
        }

        const px = points[i - 2] as number;
        const py = points[i - 1] as number;
        const nx = points[i + 2] as number;
        const ny = points[i + 3] as number;

        const v1x = x - px;
        const v1y = y - py;
        const v2x = nx - x;
        const v2y = ny - y;
        const len1 = Math.hypot(v1x, v1y);
        const len2 = Math.hypot(v2x, v2y);
        const dot = v1x * v2x + v1y * v2y;
        const isCorner = len1 > 0 && len2 > 0 && Math.abs(dot) < 1e-6;
        if (!isCorner) {
          d += ` L ${x} ${y}`;
          continue;
        }

        const r = Math.min(rBase, len1 / 2, len2 / 2);
        const u1x = v1x / len1;
        const u1y = v1y / len1;
        const u2x = v2x / len2;
        const u2y = v2y / len2;

        const x1 = x - u1x * r;
        const y1 = y - u1y * r;
        const x2 = x + u2x * r;
        const y2 = y + u2y * r;

        d += ` L ${x1} ${y1} Q ${x} ${y} ${x2} ${y2}`;
      }
      return d;
    };

    const roundedPath = rounded ? buildRoundedOrthogonalPath(pts, roundedCornerRadius) : '';

    return (
      <Group ref={(n) => registerRef?.(edge.id, n)} opacity={1} onMouseDown={handleFreeEndpointBodyMouseDown}>
        {hoverMarriageBar && (
          <Line
            points={[hoverMarriageBar.x1, hoverMarriageBar.y, hoverMarriageBar.x2, hoverMarriageBar.y]}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={14}
            opacity={0.25}
            listening={false}
          />
        )}
        {rounded ? (
          <>
            {selected ? (
              <Path
                data={roundedPath}
                stroke={getThemeColor('#3478DD', { type: 'accent' })}
                strokeWidth={width + 4}
                opacity={0.25}
                listening={false}
              />
            ) : null}
            <Path
              data={roundedPath}
              stroke={color}
              strokeWidth={width}
              dash={getLineDash()}
              onClick={handleConnectorBodyActivate}
              onTap={handleConnectorBodyActivate}
              hitStrokeWidth={10}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
          </>
        ) : (
          <>
            {selected ? (
              <Line points={pts} stroke={getThemeColor('#3478DD', { type: 'accent' })} strokeWidth={width + 4} opacity={0.25} listening={false} />
            ) : null}
            <Line
              points={pts}
              stroke={color}
              strokeWidth={width}
              dash={getLineDash()}
              onClick={handleConnectorBodyActivate}
              onTap={handleConnectorBodyActivate}
              hitStrokeWidth={10}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
          </>
        )}
        {(() => { const type = (endArrow?.type || 'triangle'); if (type === 'none') return null; const size = (endArrow?.size as number) || 8; if (type==='circle') { const cx = x2 - nx*(size/2); const cy = y2 - ny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill={color}/>; } if (type==='circleOutline') { const cx = x2 - nx*(size/2); const cy = y2 - ny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill="white" stroke={color} strokeWidth={width}/>; } if (type==='diamond') { const cx = x2 - nx*(size/2); const cy = y2 - ny*(size/2); return (<Shape sceneFunc={(ctx,shape)=>{ctx.beginPath(); ctx.moveTo(cx + nx*(size/2), cy + ny*(size/2)); ctx.lineTo(cx + px, cy + py); ctx.lineTo(cx - nx*(size/2), cy - ny*(size/2)); ctx.lineTo(cx - px, cy - py); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill="white" stroke={color} strokeWidth={width} listening={false}/>);} if (type==='bar') { const cx = x2 - nx*(size/2); const cy = y2 - ny*(size/2); const pxn = -ny*(size/2); const pyn = nx*(size/2); return <Line points={[cx+pxn,cy+pyn,cx-pxn,cy-pyn]} stroke={color} strokeWidth={width} listening={false}/>; } if (type==='outlineBlock') { return (<Shape sceneFunc={(ctx,shape)=>{ctx.beginPath();ctx.moveTo(x2,y2);ctx.lineTo(baseX+px,baseY+py);ctx.lineTo(baseX-px,baseY-py);ctx.closePath();ctx.fillStrokeShape(shape);}} fill="white" stroke={color} strokeWidth={width} listening={false}/>); } if (type==='chevron') { return (<Line points={[baseX+px,baseY+py,x2,y2,baseX-px,baseY-py]} stroke={color} strokeWidth={width} lineCap="round" lineJoin="round" listening={false}/>); } return (<Shape sceneFunc={(ctx,shape)=>{ctx.beginPath();ctx.moveTo(x2,y2);ctx.lineTo(baseX+px,baseY+py);ctx.lineTo(baseX-px,baseY-py);ctx.closePath();ctx.fillStrokeShape(shape);}} fill={color} stroke={color} strokeWidth={width} listening={false}/>); })()}
        {(() => { const type = (startArrow?.type || 'none'); if (type === 'none') return null; const sx1 = pts[0], sy1 = pts[1], sx2 = pts[2], sy2 = pts[3]; const vux = (sx2 - sx1), vuy = (sy2 - sy1); const vlen = Math.hypot(vux, vuy) || 1; const vnx = vux / vlen, vny = vuy / vlen; const sbaseX = sx1 + vnx * 12; const sbaseY = sy1 + vny * 12; const spx = -vny * 4; const spy = vnx * 4; const size = (startArrow?.size as number) || 6; if (type==='circle') { const cx = sx1 + vnx*(size/2); const cy = sy1 + vny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill={color}/>; } if (type==='circleOutline') { const cx = sx1 + vnx*(size/2); const cy = sy1 + vny*(size/2); return <Circle x={cx} y={cy} radius={size/2} fill="white" stroke={color} strokeWidth={width}/>; } if (type==='diamond') { const cx = sx1 + vnx*(size/2); const cy = sy1 + vny*(size/2); return (<Shape sceneFunc={(ctx,shape)=>{ctx.beginPath(); ctx.moveTo(cx - vnx*(size/2), cy - vny*(size/2)); ctx.lineTo(cx + spx, cy + spy); ctx.lineTo(cx + vnx*(size/2), cy + vny*(size/2)); ctx.lineTo(cx - spx, cy - spy); ctx.closePath(); ctx.fillStrokeShape(shape); }} fill="white" stroke={color} strokeWidth={width} listening={false}/>);} if (type==='bar') { const cx = sx1 + vnx*(size/2); const cy = sy1 + vny*(size/2); const pxn = -vny*(size/2); const pyn = vnx*(size/2); return <Line points={[cx+pxn,cy+pyn,cx-pxn,cy-pyn]} stroke={color} strokeWidth={width} listening={false}/>; } if (type==='backslash') { const cx = sx1 + vnx*(size/2); const cy = sy1 + vny*(size/2); const half = size/2; const dx = half*(vnx-vny); const dy = half*(vny+vnx); return <Line points={[cx-dx,cy-dy,cx+dx,cy+dy]} stroke={color} strokeWidth={width} lineCap="round" listening={false}/>; } return (<Shape sceneFunc={(ctx,shape)=>{ctx.beginPath();ctx.moveTo(sx1,sy1);ctx.lineTo(sbaseX+spx,sbaseY+spy);ctx.lineTo(sbaseX-spx,sbaseY-spy);ctx.closePath();ctx.fillStrokeShape(shape);}} fill={color} stroke={color} strokeWidth={width} listening={false}/>); })()}
        {label && (() => {
          const anchor = pointAt(pts, labelOffset);
          const labelYOffset = 12;
          // For flowchart/generic diagrams, labels should sit on the connector and "cut" the stroke beneath.
          // For genograms, keep the historical normal offset behavior.
          const cx = diagramType === 'genogram' ? anchor.x + anchor.nx * labelYOffset : anchor.x;
          const cy = (diagramType === 'genogram' ? anchor.y + anchor.ny * labelYOffset : anchor.y) + orgLabelVerticalNudge;
          const fontPx = 14;
          const family = 'system-ui, -apple-system, sans-serif';
          const lineH = 1.2;
          const measure = (text: string) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return { width: 0, height: 0 };
            ctx.font = `${fontPx}px ${family}`;
            const lines = String(text).split('\n');
            let maxW = 0;
            for (const ln of lines) { const w = ctx.measureText(ln || ' ').width; if (w > maxW) maxW = w; }
            const h = Math.max(1, lines.length) * fontPx * lineH;
            return { width: Math.ceil(maxW), height: Math.ceil(h) };
          };
          const m = measure(label);
          const padX = 8;
          const padY = Math.max(4, Math.ceil(width) + 6);
          return (
            <>
              <Rect
                x={cx}
                y={cy}
                width={m.width + padX}
                height={m.height + padY}
                offsetX={(m.width + padX) / 2}
                offsetY={(m.height + padY) / 2}
                fill={edgeBackgroundColor}
                listening={false}
                perfectDrawEnabled={false}
                cornerRadius={2}
              />
              <KonvaText
                ref={(n) => { setLabelTextRef((n as unknown as Konva.Text) || null); }}
                x={cx}
                y={cy}
                text={label}
                fontSize={fontPx}
                fontFamily={family}
                fill={connectorLabelFill}
                align="center"
                wrap="none"
                listening={!!canWrite}
                visible={!editingText}
                width={m.width}
                offsetX={m.width / 2}
                offsetY={m.height / 2}
                perfectDrawEnabled={false}
                onClick={onClick}
                onTap={onClick}
                onDblClick={() => {
                  if (!canWrite) return;
                  const textNode = labelTextRef.current;
                  if (!textNode) return;
                  onStartTextEdit?.(edge.id, textNode);
                }}
                onDblTap={() => {
                  if (!canWrite) return;
                  const textNode = labelTextRef.current;
                  if (!textNode) return;
                  onStartTextEdit?.(edge.id, textNode);
                }}
              />
            </>
          );
        })()}

        {/* Endpoint drag handles */}
        {selected && (
          <>
            <Circle
              x={pts[0]}
              y={pts[1]}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onDragStart={() => {
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: toNode?.id } })); } catch {}
              }}
	              onDragMove={(e) => {
	                const pos = e.target.position();
	                const snap = findSnapTarget(pos, toNode?.id, toDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  setDragPreview(prev => ({ ...(prev || {}), fromPoint: snap.snapped }));
	                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId: toNode?.id } })); } catch {}
	                } else {
                  e.target.position({ x: pos.x, y: pos.y });
                  setDragPreview(prev => ({ ...(prev || {}), fromPoint: { x: pos.x, y: pos.y } }));
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: pos.x, y: pos.y, sourceId: toNode?.id } })); } catch {}
                }
              }}
	              onDragEnd={(e) => {
	                const pos = e.target.position();
	                const snap = findSnapTarget(pos, toNode?.id, toDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  executeEdgeCommand({
                      from: snap.id,
                      fromAnchor: snap.side as any,
                      fromOffset: snap.offset as any,
                      fromAnchorLocked: true as any,
                      attrs: { ...(edge.attrs || {}), fromPoint: undefined } as any,
                    } as any);
                } else if (orgTreePreviewOnlyDrag) {
                  // Tree ON: never detach an org edge to free space. Snap back to original endpoint.
                  e.target.position({ x: pts[0], y: pts[1] });
                } else {
                  const freeId = `__freeF:${edge.id}`;
                  e.target.position({ x: pos.x, y: pos.y });
                  executeEdgeCommand({
                    from: freeId,
                    fromAnchor: undefined as any,
                    fromOffset: undefined as any,
                    attrs: { ...(edge.attrs || {}), fromPoint: { x: pos.x, y: pos.y } } as any,
                  } as any);
                }
                setDragPreview(prev => ({ ...(prev || {}), fromPoint: undefined }));
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
            <Circle
              x={pts[pts.length-2]}
              y={pts[pts.length-1]}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onDragStart={() => {
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: fromNode?.id } })); } catch {}
              }}
	              onDragMove={(e) => {
                const pos = e.target.position();
                const barSnap = findMarriageBarSnap(pos);
                if (barSnap) {
                  e.target.position(barSnap.snapped);
                  setDragPreview({ toPoint: barSnap.snapped });
                  setHoverMarriageBar(barSnap.bar);
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: barSnap.snapped.x, y: barSnap.snapped.y, sourceId: fromNode?.id } })); } catch {}
                  return;
                }
	                setHoverMarriageBar(null);
	                const snap = findSnapTarget(pos, fromNode?.id, fromDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  setDragPreview({ toPoint: snap.snapped });
	                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId: fromNode?.id } })); } catch {}
	                } else {
                  e.target.position({ x: pos.x, y: pos.y });
                  setDragPreview({ toPoint: { x: pos.x, y: pos.y } });
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: pos.x, y: pos.y, sourceId: fromNode?.id } })); } catch {}
                }
              }}
              onDragEnd={(e) => {
                const pos = e.target.position();
                const barSnap = findMarriageBarSnap(pos);
                if (barSnap && fromNode) {
                  e.target.position(barSnap.snapped);
                  const p1 = nodes.find(n => n.id === barSnap.bar.parent1Id);
                  const p2 = nodes.find(n => n.id === barSnap.bar.parent2Id);
                  const m1 = p1 ? (getShapeState ? (getShapeState(p1.id, p1) as Node) : p1) : null;
                  const m2 = p2 ? (getShapeState ? (getShapeState(p2.id, p2) as Node) : p2) : null;
                  let chosenParentId = barSnap.bar.parent1Id;
                  if (m1 && m2) {
                    const childResolved = getShapeState ? (getShapeState(fromNode.id, fromNode) as Node) : fromNode;
                    const childCenterX = getNodeCenterX(childResolved);
                    const d1 = Math.abs(getNodeCenterX(m1) - childCenterX);
                    const d2 = Math.abs(getNodeCenterX(m2) - childCenterX);
                    chosenParentId = d1 <= d2 ? barSnap.bar.parent1Id : barSnap.bar.parent2Id;
                  }
                  executeEdgeCommand({
                    type: 'child' as any,
                    from: chosenParentId,
                    to: fromNode.id,
                    fromAnchor: undefined as any,
                    toAnchor: undefined as any,
                    fromOffset: undefined as any,
                    toOffset: undefined as any,
                    attrs: { ...(edge.attrs || {}), toPoint: undefined, fromPoint: undefined, routingType: undefined } as any,
                  } as any);
	                } else {
	                  const snap = findSnapTarget(pos, fromNode?.id, fromDockTableId);
	                  if (snap) {
	                    e.target.position(snap.snapped);
	                    executeEdgeCommand({
                        to: snap.id,
                        toAnchor: snap.side as any,
                        toOffset: snap.offset as any,
                        ...(isLineShape
                          ? { attrs: { toPoint: undefined } as any }
                          : { toAnchorLocked: true as any, attrs: { ...(edge.attrs || {}), toPoint: undefined } as any }),
                      } as any);
                  } else if (orgTreePreviewOnlyDrag) {
                    // Tree ON: never detach an org edge to free space. Snap back to original endpoint.
                    e.target.position({ x: pts[pts.length - 2], y: pts[pts.length - 1] });
                  } else {
                    const freeId = isLineShape ? `__freeT:${edge.id}` : `__free:${edge.id}`;
                    e.target.position({ x: pos.x, y: pos.y });
                    executeEdgeCommand({
                      to: freeId,
                      toAnchor: undefined as any,
                      toOffset: undefined as any,
                      attrs: isLineShape
                        ? ({ toPoint: { x: pos.x, y: pos.y } } as any)
                        : ({ ...(edge.attrs || {}), toPoint: { x: pos.x, y: pos.y } } as any),
                    } as any);
                  }
                }
                setHoverMarriageBar(null);
                setDragPreview(null);
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
          </>
        )}

        <ConnectorOverlay
          edgeId={edge.id}
          midpoint={midpoint}
          canShowInsert={!!showInsertButton}
          onHoverChange={setIsOverlayHovered}
        />
      </Group>
    );
  }

  // Rounded orthogonal routing (right-angled with curved corners)
  if ((renderData as any).type === 'roundedOrthogonal') {
    const pts = (renderData as any).points as number[];
    const color = getStrokeColor();
    const width = getLineWidth();
    const endArrow = getEndArrow();
    const startArrow = (edge.attrs as any)?.startArrow as { type?: string; size?: number } | undefined;
    const rawLabel = (edge.attrs as any)?.label as string | undefined;
    const showCaptions = usePerspectiveStore.getState().showRelationshipCaptions;
    const suppressed = (edge.attrs as any)?.__captionHidden === true;
    const label = suppressed ? undefined : (rawLabel ?? (showCaptions ? undefined : undefined));
    const labelOffset = typeof (edge.attrs as any)?.labelOffset === 'number' ? (edge.attrs as any).labelOffset : 0.5;

    // Corner radius for rounded corners
    const cornerRadius = 8;

    // Helper to draw polyline with rounded corners
    const drawRoundedPolyline = (ctx: any) => {
      if (pts.length < 4) return;
      ctx.beginPath();
      ctx.moveTo(pts[0], pts[1]);

      for (let i = 2; i < pts.length - 2; i += 2) {
        const prevX = pts[i - 2], prevY = pts[i - 1];
        const currX = pts[i], currY = pts[i + 1];
        const nextX = pts[i + 2], nextY = pts[i + 3];

        // Calculate vectors
        const v1x = currX - prevX, v1y = currY - prevY;
        const v2x = nextX - currX, v2y = nextY - currY;
        const len1 = Math.hypot(v1x, v1y) || 1;
        const len2 = Math.hypot(v2x, v2y) || 1;

        // Limit radius to half of the shorter segment
        const maxRadius = Math.min(len1 / 2, len2 / 2, cornerRadius);

        // Calculate points before and after corner
        const beforeX = currX - (v1x / len1) * maxRadius;
        const beforeY = currY - (v1y / len1) * maxRadius;
        const afterX = currX + (v2x / len2) * maxRadius;
        const afterY = currY + (v2y / len2) * maxRadius;

        // Draw line to point before corner, then quadratic curve through corner
        ctx.lineTo(beforeX, beforeY);
        ctx.quadraticCurveTo(currX, currY, afterX, afterY);
      }

      // Draw to the last point
      ctx.lineTo(pts[pts.length - 2], pts[pts.length - 1]);
    };

    const pointAt = (points: number[], t: number) => {
      const segments: Array<{ x1: number; y1: number; x2: number; y2: number; len: number }> = [];
      let total = 0;
      for (let i = 0; i < points.length - 2; i += 2) {
        const x1 = points[i], y1 = points[i + 1];
        const x2 = points[i + 2], y2 = points[i + 3];
        const len = Math.hypot(x2 - x1, y2 - y1);
        segments.push({ x1, y1, x2, y2, len });
        total += len;
      }
      if (!total) return { x: points[0], y: points[1], nx: 0, ny: -1 };
      let dist = Math.max(0, Math.min(1, t)) * total;
      for (const seg of segments) {
        if (seg.len === 0) continue;
        if (dist <= seg.len) {
          const ratio = dist / seg.len;
          const x = seg.x1 + (seg.x2 - seg.x1) * ratio;
          const y = seg.y1 + (seg.y2 - seg.y1) * ratio;
          const dx = seg.x2 - seg.x1;
          const dy = seg.y2 - seg.y1;
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          return { x, y, nx, ny };
        }
        dist -= seg.len;
      }
      const last = segments[segments.length - 1];
      const dx = last.x2 - last.x1;
      const dy = last.y2 - last.y1;
      const len = Math.hypot(dx, dy) || 1;
      return { x: last.x2, y: last.y2, nx: -dy / len, ny: dx / len };
    };

    // last segment for arrow
    const n = pts.length;
    const x1 = pts[n - 4], y1 = pts[n - 3], x2 = pts[n - 2], y2 = pts[n - 1];
    const ux = (x2 - x1), uy = (y2 - y1);
    const len = Math.hypot(ux, uy) || 1;
    const nx = ux / len, ny = uy / len;
    const baseX = x2 - nx * 12;
    const baseY = y2 - ny * 12;
    const px = -ny * 4;
    const py = nx * 4;

    return (
      <Group ref={(n) => registerRef?.(edge.id, n)} opacity={1} onMouseDown={handleFreeEndpointBodyMouseDown}>
        {selected && (
          <Shape
            sceneFunc={(ctx, shape) => {
              drawRoundedPolyline(ctx);
              ctx.strokeShape(shape);
            }}
            stroke={getThemeColor('#3478DD', { type: 'accent' })}
            strokeWidth={width + 4}
            opacity={0.25}
            listening={false}
          />
        )}
        <Shape
          sceneFunc={(ctx, shape) => {
            drawRoundedPolyline(ctx);
            ctx.strokeShape(shape);
          }}
          stroke={color}
          strokeWidth={width}
          dash={getLineDash()}
          onClick={handleConnectorBodyActivate}
          onTap={handleConnectorBodyActivate}
          hitStrokeWidth={10}
        />
        {/* End arrow */}
        {(() => {
          const type = (endArrow?.type || 'none');
          if (type === 'none') return null;
          const size = (endArrow?.size as number) || 8;
          if (type === 'circle') {
            const cx = x2 - nx * (size / 2);
            const cy = y2 - ny * (size / 2);
            return <Circle x={cx} y={cy} radius={size / 2} fill={color} />;
          }
          if (type === 'circleOutline') {
            const cx = x2 - nx * (size / 2);
            const cy = y2 - ny * (size / 2);
            return <Circle x={cx} y={cy} radius={size / 2} fill="white" stroke={color} strokeWidth={width} />;
          }
          if (type === 'diamond') {
            const cx = x2 - nx * (size / 2);
            const cy = y2 - ny * (size / 2);
            return (
              <Shape
                sceneFunc={(ctx, shape) => {
                  ctx.beginPath();
                  ctx.moveTo(cx + nx * (size / 2), cy + ny * (size / 2));
                  ctx.lineTo(cx + px, cy + py);
                  ctx.lineTo(cx - nx * (size / 2), cy - ny * (size / 2));
                  ctx.lineTo(cx - px, cy - py);
                  ctx.closePath();
                  ctx.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          if (type === 'bar') {
            const cx = x2 - nx * (size / 2);
            const cy = y2 - ny * (size / 2);
            const pxn = -ny * (size / 2);
            const pyn = nx * (size / 2);
            return <Line points={[cx + pxn, cy + pyn, cx - pxn, cy - pyn]} stroke={color} strokeWidth={width} listening={false} />;
          }
          if (type === 'outlineBlock') {
            return (
              <Shape
                sceneFunc={(ctx, shape) => {
                  ctx.beginPath();
                  ctx.moveTo(x2, y2);
                  ctx.lineTo(baseX + px, baseY + py);
                  ctx.lineTo(baseX - px, baseY - py);
                  ctx.closePath();
                  ctx.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          if (type === 'chevron') {
            return (
              <Line
                points={[baseX + px, baseY + py, x2, y2, baseX - px, baseY - py]}
                stroke={color}
                strokeWidth={width}
                lineCap="round"
                lineJoin="round"
                listening={false}
              />
            );
          }
          // Default triangle arrow
          return (
            <Shape
              sceneFunc={(ctx, shape) => {
                ctx.beginPath();
                ctx.moveTo(x2, y2);
                ctx.lineTo(baseX + px, baseY + py);
                ctx.lineTo(baseX - px, baseY - py);
                ctx.closePath();
                ctx.fillStrokeShape(shape);
              }}
              fill={color}
              stroke={color}
              strokeWidth={width}
              listening={false}
            />
          );
        })()}
        {/* Start arrow */}
        {(() => {
          const type = (startArrow?.type || 'none');
          if (type === 'none') return null;
          const sx1 = pts[0], sy1 = pts[1], sx2 = pts[2], sy2 = pts[3];
          const vux = (sx2 - sx1), vuy = (sy2 - sy1);
          const vlen = Math.hypot(vux, vuy) || 1;
          const vnx = vux / vlen, vny = vuy / vlen;
          const sbaseX = sx1 + vnx * 12;
          const sbaseY = sy1 + vny * 12;
          const spx = -vny * 4;
          const spy = vnx * 4;
          const size = (startArrow?.size as number) || 6;
          if (type === 'circle') {
            const cx = sx1 + vnx * (size / 2);
            const cy = sy1 + vny * (size / 2);
            return <Circle x={cx} y={cy} radius={size / 2} fill={color} />;
          }
          if (type === 'circleOutline') {
            const cx = sx1 + vnx * (size / 2);
            const cy = sy1 + vny * (size / 2);
            return <Circle x={cx} y={cy} radius={size / 2} fill="white" stroke={color} strokeWidth={width} />;
          }
          if (type === 'diamond') {
            const cx = sx1 + vnx * (size / 2);
            const cy = sy1 + vny * (size / 2);
            return (
              <Shape
                sceneFunc={(ctx, shape) => {
                  ctx.beginPath();
                  ctx.moveTo(cx - vnx * (size / 2), cy - vny * (size / 2));
                  ctx.lineTo(cx + spx, cy + spy);
                  ctx.lineTo(cx + vnx * (size / 2), cy + vny * (size / 2));
                  ctx.lineTo(cx - spx, cy - spy);
                  ctx.closePath();
                  ctx.fillStrokeShape(shape);
                }}
                fill="white"
                stroke={color}
                strokeWidth={width}
                listening={false}
              />
            );
          }
          if (type === 'bar') {
            const cx = sx1 + vnx * (size / 2);
            const cy = sy1 + vny * (size / 2);
            const pxn = -vny * (size / 2);
            const pyn = vnx * (size / 2);
            return <Line points={[cx + pxn, cy + pyn, cx - pxn, cy - pyn]} stroke={color} strokeWidth={width} listening={false} />;
          }
          if (type === 'backslash') {
            const cx = sx1 + vnx * (size / 2);
            const cy = sy1 + vny * (size / 2);
            const half = size / 2;
            const dx = half * (vnx - vny);
            const dy = half * (vny + vnx);
            return <Line points={[cx - dx, cy - dy, cx + dx, cy + dy]} stroke={color} strokeWidth={width} lineCap="round" listening={false} />;
          }
          return (
            <Shape
              sceneFunc={(ctx, shape) => {
                ctx.beginPath();
                ctx.moveTo(sx1, sy1);
                ctx.lineTo(sbaseX + spx, sbaseY + spy);
                ctx.lineTo(sbaseX - spx, sbaseY - spy);
                ctx.closePath();
                ctx.fillStrokeShape(shape);
              }}
              fill={color}
              stroke={color}
              strokeWidth={width}
              listening={false}
            />
          );
        })()}
        {/* Label */}
        {label && (() => {
          const anchor = pointAt(pts, labelOffset);
          const labelYOffset = 12;
          // For flowchart/generic diagrams, labels should sit on the connector and "cut" the stroke beneath.
          // For genograms, keep the historical normal offset behavior.
          const cx = diagramType === 'genogram' ? anchor.x + anchor.nx * labelYOffset : anchor.x;
          const cy = (diagramType === 'genogram' ? anchor.y + anchor.ny * labelYOffset : anchor.y) + orgLabelVerticalNudge;
          const fontPx = 14;
          const family = 'system-ui, -apple-system, sans-serif';
          const lineH = 1.2;
          const measure = (text: string) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return { width: 0, height: 0 };
            ctx.font = `${fontPx}px ${family}`;
            const lines = String(text).split('\n');
            let maxW = 0;
            for (const ln of lines) {
              const w = ctx.measureText(ln || ' ').width;
              if (w > maxW) maxW = w;
            }
            const h = Math.max(1, lines.length) * fontPx * lineH;
            return { width: Math.ceil(maxW), height: Math.ceil(h) };
          };
          const m = measure(label);
          const padX = 8;
          const padY = Math.max(4, Math.ceil(width) + 6);
          return (
            <>
              <Rect
                x={cx}
                y={cy}
                width={m.width + padX}
                height={m.height + padY}
                offsetX={(m.width + padX) / 2}
                offsetY={(m.height + padY) / 2}
                fill={edgeBackgroundColor}
                listening={false}
                perfectDrawEnabled={false}
                cornerRadius={2}
              />
              <KonvaText
                ref={(n) => { setLabelTextRef((n as unknown as Konva.Text) || null); }}
                x={cx}
                y={cy}
                text={label}
                fontSize={fontPx}
                fontFamily={family}
                fill={connectorLabelFill}
                align="center"
                wrap="none"
                listening={!!canWrite}
                width={m.width}
                offsetX={m.width / 2}
                offsetY={m.height / 2}
                visible={!editingText}
                perfectDrawEnabled={false}
                onClick={onClick}
                onTap={onClick}
                onDblClick={() => {
                  if (!canWrite) return;
                  const textNode = labelTextRef.current;
                  if (!textNode) return;
                  onStartTextEdit?.(edge.id, textNode);
                }}
                onDblTap={() => {
                  if (!canWrite) return;
                  const textNode = labelTextRef.current;
                  if (!textNode) return;
                  onStartTextEdit?.(edge.id, textNode);
                }}
              />
            </>
          );
        })()}
        {/* Endpoint drag handles */}
        {selected && (
          <>
            <Circle
              x={pts[0]}
              y={pts[1]}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onDragStart={() => {
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: toNode?.id } })); } catch {}
              }}
	              onDragMove={(e) => {
	                const pos = e.target.position();
	                const snap = findSnapTarget(pos, toNode?.id, toDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  setDragPreview(prev => ({ ...(prev || {}), fromPoint: snap.snapped }));
	                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId: toNode?.id } })); } catch {}
	                } else {
                  e.target.position({ x: pos.x, y: pos.y });
                  setDragPreview(prev => ({ ...(prev || {}), fromPoint: { x: pos.x, y: pos.y } }));
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: pos.x, y: pos.y, sourceId: toNode?.id } })); } catch {}
                }
              }}
	              onDragEnd={(e) => {
	                const pos = e.target.position();
	                const snap = findSnapTarget(pos, toNode?.id, toDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  executeEdgeCommand({
                      from: snap.id,
                      fromAnchor: snap.side as any,
                      fromOffset: snap.offset as any,
                      attrs: { ...(edge.attrs || {}), fromPoint: undefined } as any,
                    } as any);
                } else if (orgTreePreviewOnlyDrag) {
                  // Tree ON: never detach an org edge to free space. Snap back to original endpoint.
                  e.target.position({ x: pts[0], y: pts[1] });
                } else {
                  const freeId = `__freeF:${edge.id}`;
                  e.target.position({ x: pos.x, y: pos.y });
                  executeEdgeCommand({
                    from: freeId,
                    fromAnchor: undefined as any,
                    fromOffset: undefined as any,
                    attrs: { ...(edge.attrs || {}), fromPoint: { x: pos.x, y: pos.y } } as any,
                  } as any);
                }
                setDragPreview(prev => ({ ...(prev || {}), fromPoint: undefined }));
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
            <Circle
              x={pts[pts.length - 2]}
              y={pts[pts.length - 1]}
              radius={6}
              fill="#ffffff"
              stroke="#3b82f6"
              strokeWidth={2}
              draggable
              onDragStart={() => {
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:start', { detail: { edgeId: edge.id, sourceId: fromNode?.id } })); } catch {}
              }}
              onDragMove={(e) => {
                const pos = e.target.position();
                const barSnap = findMarriageBarSnap(pos);
                if (barSnap) {
                  e.target.position(barSnap.snapped);
                  setDragPreview({ toPoint: barSnap.snapped });
                  setHoverMarriageBar(barSnap.bar);
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: barSnap.snapped.x, y: barSnap.snapped.y, sourceId: fromNode?.id } })); } catch {}
                  return;
	                }
	                setHoverMarriageBar(null);
	                const snap = findSnapTarget(pos, fromNode?.id, fromDockTableId);
	                if (snap) {
	                  e.target.position(snap.snapped);
	                  setDragPreview({ toPoint: snap.snapped });
	                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: snap.snapped.x, y: snap.snapped.y, sourceId: fromNode?.id } })); } catch {}
                } else {
                  e.target.position({ x: pos.x, y: pos.y });
                  setDragPreview({ toPoint: { x: pos.x, y: pos.y } });
                  try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:update', { detail: { x: pos.x, y: pos.y, sourceId: fromNode?.id } })); } catch {}
                }
              }}
              onDragEnd={(e) => {
                const pos = e.target.position();
                const barSnap = findMarriageBarSnap(pos);
                if (barSnap && fromNode) {
                  e.target.position(barSnap.snapped);
                  const p1 = nodes.find(n => n.id === barSnap.bar.parent1Id);
                  const p2 = nodes.find(n => n.id === barSnap.bar.parent2Id);
                  const m1 = p1 ? (getShapeState ? (getShapeState(p1.id, p1) as Node) : p1) : null;
                  const m2 = p2 ? (getShapeState ? (getShapeState(p2.id, p2) as Node) : p2) : null;
                  let chosenParentId = barSnap.bar.parent1Id;
                  if (m1 && m2) {
                    const childResolved = getShapeState ? (getShapeState(fromNode.id, fromNode) as Node) : fromNode;
                    const childCenterX = getNodeCenterX(childResolved);
                    const d1 = Math.abs(getNodeCenterX(m1) - childCenterX);
                    const d2 = Math.abs(getNodeCenterX(m2) - childCenterX);
                    chosenParentId = d1 <= d2 ? barSnap.bar.parent1Id : barSnap.bar.parent2Id;
                  }
                  executeEdgeCommand({
                    type: 'child' as any,
                    from: chosenParentId,
                    to: fromNode.id,
                    fromAnchor: undefined as any,
                    toAnchor: undefined as any,
                    fromOffset: undefined as any,
                    toOffset: undefined as any,
                    attrs: { ...(edge.attrs || {}), toPoint: undefined, fromPoint: undefined, routingType: undefined } as any,
                  } as any);
	                } else {
	                  const snap = findSnapTarget(pos, fromNode?.id, fromDockTableId);
	                  if (snap) {
	                    e.target.position(snap.snapped);
	                    executeEdgeCommand({
                        to: snap.id,
                        toAnchor: snap.side as any,
                        toOffset: snap.offset as any,
                        attrs: { ...(edge.attrs || {}), toPoint: undefined } as any,
                      } as any);
                  } else if (orgTreePreviewOnlyDrag) {
                    // Tree ON: never detach an org edge to free space. Snap back to original endpoint.
                    e.target.position({ x: pts[pts.length - 2], y: pts[pts.length - 1] });
                  } else {
                    const freeId = `__freeT:${edge.id}`;
                    e.target.position({ x: pos.x, y: pos.y });
                    executeEdgeCommand({
                      to: freeId,
                      toAnchor: undefined as any,
                      toOffset: undefined as any,
                      attrs: { ...(edge.attrs || {}), toPoint: { x: pos.x, y: pos.y } } as any,
                    } as any);
                  }
                }
                setDragPreview(prev => ({ ...(prev || {}), toPoint: undefined }));
                setHoverMarriageBar(null);
                try { window.dispatchEvent(new CustomEvent('edge:endpoint:drag:end', { detail: { edgeId: edge.id } })); } catch {}
              }}
            />
          </>
        )}
      </Group>
    );
  }

  return null;
};

EdgeConnectorBody.displayName = 'EdgeConnectorBody';

const EdgeConnectorInner: React.FC<EdgeConnectorProps> = (props) => {
  const autoLayoutEnabled = useOrgChartViewStore(s => s.autoLayoutEnabled);
  const orgVerticalSpacing = useOrgChartViewStore(s => s.verticalSpacing);
  const isStructural = props.edge.type === 'marriage' || props.edge.type === 'child';

  // Structural connectors require both endpoints. Keep this guard outside the
  // hook-heavy body so missing-endpoint edges do not subscribe to awareness or
  // initialize drag state.
  if (isStructural && (!props.fromNode || !props.toNode)) return null;

  return (
    <EdgeConnectorBody
      {...props}
      autoLayoutEnabled={autoLayoutEnabled}
      orgVerticalSpacing={orgVerticalSpacing}
    />
  );
};

EdgeConnectorInner.displayName = 'EdgeConnectorInner';

// Custom memo comparator: when auto-layout (snap-to-tree) is OFF, ignore
// changes to the `edges` and `nodes` arrays. Those props only feed
// sibling-dependent routing (staggered bus lines, childIsParent check) which
// is disabled when snap is off. Still honor explicit `version` bumps because
// they are the targeted route invalidation path after measured geometry
// changes, such as org footers appearing after report counts load.
const edgeConnectorAreEqual = (prev: EdgeConnectorProps, next: EdgeConnectorProps): boolean => {
  const autoLayoutEnabled = useOrgChartViewStore.getState().autoLayoutEnabled;
  const keys = Object.keys(next) as Array<keyof EdgeConnectorProps>;
  for (const key of keys) {
    if (!autoLayoutEnabled && (key === 'edges' || key === 'nodes')) continue;
    if (prev[key] !== next[key]) return false;
  }
  return true;
};

export const EdgeConnector = memo(EdgeConnectorInner, edgeConnectorAreEqual);
